<template>
    <div class="talent-system">
        <!-- 添加TopBar组件 -->
        <TopBar :user="user" :apps="apps" />
        <!-- 节点统计信息 -->
        <div class="node-stats">
            <span>节点加载情况: {{ displayedNodes.length }}/{{ talentNodes.length }}</span>
        </div>
        <!-- 快捷键提示按钮 -->
        <div class="shortcut-help-button" @click="toggleShortcutHelp">
            ?
        </div>

        <!-- 标签管理面板 - 仅对管理员显示 -->
        <div v-if="user.userType === 1" class="tag-management-panel">
            <div>标签数量: {{ graphTypes.length }}</div>
            <!-- <div>graphTypes数据: {{ JSON.stringify(graphTypes) }}</div> -->
            <div v-if="graphTypes.length === 0" class="no-tags-message">
                暂无标签数据
            </div>
            <div v-else v-for="tag in graphTypes" :key="tag.id" class="tag-item">
                {{ tag.name || tag.label || tag.id || '未知标签' }}
            </div>
        </div>

        <!-- 进程监控按钮 -->
        <div v-if="user.type === 1" class="process-monitor-button" @click="showProcessMonitor = !showProcessMonitor">
            ⚙️
        </div>

        <!-- 链接添加状态调试信息 -->
        <div v-if="isAddingLink" class="debug-info"
            style="position: fixed; top: 90px; right: 10px; z-index: 10000; background: rgba(255, 255, 200, 0.95); padding: 10px; border: 1px solid #ccc; border-radius: 4px; font-size: 12px; min-width: 250px;">
            <div><strong>操作提示:</strong> 请先选择源节点，然后点击目标节点</div>
            <div><strong>链接添加模式:</strong> {{ isAddingLink ? '开启' : '关闭' }}</div>
            <div v-if="sourceNodes && sourceNodes.length > 0">
                <strong>源节点 ({{ sourceNodes.length }} 个):</strong>
                <ul style="margin: 5px 0; padding-left: 20px;">
                    <li v-for="node in sourceNodes" :key="node.id">{{ node.name }} (ID: {{ node.id }})</li>
                </ul>
            </div>
            <div v-else>
                <strong>源节点:</strong> 无
            </div>
            <div v-if="targetNode">
                <strong>目标节点:</strong> {{ targetNode.name }} (ID: {{ targetNode.id }})
            </div>
            <div v-else>
                <strong>目标节点:</strong> 未选择
            </div>
        </div>
        <!-- 快捷键提示面板 -->
        <div v-if="showShortcutHelp" class="shortcut-help-panel">
            <div class="shortcut-help-content">
                <h3>快捷键说明</h3>
                <div class="shortcut-category">
                    <h4>画布操作</h4>
                    <ul>
                        <li><kbd>↑</kbd> <kbd>↓</kbd> <kbd>←</kbd> <kbd>→</kbd> - 移动画布</li>
                        <li><kbd>Ctrl</kbd> + <kbd>↑</kbd> <kbd>↓</kbd> <kbd>←</kbd> <kbd>→</kbd> - 大幅度移动画布</li>
                        <li><kbd>Alt</kbd> + <kbd>+</kbd> - 放大画布</li>
                        <li><kbd>Alt</kbd> + <kbd>-</kbd> - 缩小画布</li>
                        <li><kbd>Alt</kbd> + <kbd>0</kbd> - 重置缩放</li>
                    </ul>
                </div>
                <div v-if="user.userType === 1" class="shortcut-category">
                    <h4>编辑模式 (仅限管理员)</h4>
                    <ul>
                        <li><kbd>Ctrl</kbd> + <kbd>E</kbd> - 进入/退出编辑模式</li>
                        <li><kbd>Ctrl</kbd> + <kbd>S</kbd> - 保存当前布局</li>
                        <li><kbd>Ctrl</kbd> + <kbd>W</kbd> - 自动网格对齐</li>
                        <li><kbd>Ctrl</kbd> + <kbd>L</kbd> - 进入/退出添加链接状态</li>
                        <li><kbd>Ctrl</kbd> + <kbd>R</kbd> - 重新加载数据</li>
                        <li><kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>S</kbd> - 进入/退出设置界面</li>
                    </ul>
                </div>
                <div class="shortcut-category">
                    <h4>其他操作</h4>
                    <ul>
                        <li><kbd>Ctrl</kbd> + <kbd>P</kbd> - 快速移动选中节点</li>
                        <li><kbd>Ctrl</kbd> + <kbd>O</kbd> - 临时激活所有节点</li>
                    </ul>
                </div>
                <div class="shortcut-category">
                    <h4>关系线特殊高亮</h4>
                    <ul>
                        <li><kbd>1</kbd> - <kbd>9</kbd> - 特殊高亮第N条相关关系线</li>
                        <li><kbd>+</kbd> 或 <kbd>=</kbd> - 切换到下一条关系线</li>
                        <li><kbd>-</kbd> - 切换到上一条关系线</li>
                        <li><kbd>Tab</kbd> - 跳转到特殊高亮关系线的另一个节点</li>
                    </ul>
                </div>
                <button class="close-help-button" @click="toggleShortcutHelp">关闭</button>
            </div>
        </div>

        <!-- 进程监控悬浮窗 -->
        <div v-if="showProcessMonitor" class="process-monitor-float"
            :style="{ left: processMonitorPosition.x + 'px', top: processMonitorPosition.y + 'px' }"
            @mousedown="startProcessMonitorDrag" @touchstart="startProcessMonitorDrag">
            <div class="process-monitor-header">
                <span class="process-monitor-title">⚙️ 进程监控</span>
                <div class="process-monitor-controls">
                    <button class="minimize-btn" @click="minimizeProcessMonitor">{{ isProcessMonitorMinimized ? '+' :
                        '-' }}</button>
                    <button class="close-process-monitor" @click="showProcessMonitor = false">×</button>
                </div>
            </div>
            <div v-show="!isProcessMonitorMinimized" class="process-monitor-body">
                <div class="process-stats">
                    <h4>系统统计</h4>
                    <div class="stats-grid">
                        <div class="stat-item">
                            <span class="stat-label">总节点数:</span>
                            <span class="stat-value">{{ processStats.totalNodes }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">显示节点:</span>
                            <span class="stat-value">{{ processStats.displayedNodes }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">总连接数:</span>
                            <span class="stat-value">{{ processStats.totalLinks }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">渲染连接:</span>
                            <span class="stat-value">{{ processStats.renderedLinks }}</span>
                        </div>
                        <div class="stat-item" v-if="processStats.memoryUsage > 0">
                            <span class="stat-label">内存使用:</span>
                            <span class="stat-value">{{ processStats.memoryUsage }} MB</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">渲染时间:</span>
                            <span class="stat-value">{{ processStats.renderTime }} ms</span>
                        </div>
                    </div>
                </div>

                <!-- 测试节点控件 -->
                <div class="test-controls">
                    <div class="test-control-group">
                        <label>测试节点数量:</label>
                        <input v-model.number="testNodeCount" type="number" min="1" max="100" placeholder="输入节点数">
                        <button @click="addTestNodes" class="test-btn">添加测试节点</button>
                    </div>
                </div>

                <div class="running-processes">
                    <h4>运行中的进程</h4>
                    <div class="process-list">
                        <div v-for="process in runningProcesses" :key="process.id" class="process-item">
                            <div class="process-header">
                                <span class="process-name">{{ process.name }}</span>
                                <span class="process-status" :class="'status-' + process.status">{{ process.status
                                }}</span>
                            </div>
                            <div class="process-progress">
                                <div class="progress-bar">
                                    <div class="progress-fill" :style="{ width: process.progress + '%' }"></div>
                                </div>
                                <span class="progress-text">{{ process.progress }}%</span>
                            </div>
                            <div class="process-info">
                                <span class="process-update">更新时间: {{ process.lastUpdate }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- 缩放控制按钮 -->
    <div class="zoom-controls">
        <button class="zoom-button" @click="zoomIn">+</button>
        <button class="zoom-button" @click="zoomOut">-</button>
        <button class="zoom-button" @click="resetZoom">1:1</button>
    </div>
    <!-- <h1>未来引擎</h1> -->
    <div class="talent-container">
        <div v-if="isEditMode && user.userType === 1" class="toolbar">
            <button @click="toggleEditMode">退出编辑模式</button>
            <button @click="autoLayout">网格对齐</button>
            <button @click="togglePreviewMode" v-if="user.userType === 1">{{ isInPreviewMode ? '退出预览模式' : '预览用户模式'
                }}</button>
            <button @click="addNode">添加节点</button>
            <button @click="startAddLink" v-if="!isAddingLink">添加连接</button>
            <button @click="cancelAddLink" v-else>取消添加连接</button>
            <button @click="saveCurrentLayout">保存当前布局</button>
            <button @click="reloadData">重新加载数据</button>
            <button @click="openRatingModal">评分</button>
            <button @click="openHomeworkModal">布置作业</button>
            <button @click="openSettingsModal">设置</button>
            <!-- 用户切换功能 - 仅对管理员显示 -->
            <div v-if="user.userType === 1" class="user-switcher-container">
                <button @click="toggleUserSwitcher" class="user-switcher-btn">
                    {{ selectedUserForView ? `切换到: ${selectedUserForView.username || selectedUserForView.nickname}` :
                        '选择用户' }}
                </button>
                <div v-if="showUserSwitcher" class="user-switcher-dropdown">
                    <input v-model="userSwitchSearchText" placeholder="搜索用户..." class="search-input" />
                    <div class="users-list">
                        <div v-for="user in filteredUsersForSwitch" :key="user.id" @click="selectUserForView(user)"
                            class="user-item"
                            :class="{ 'selected': selectedUserForView && selectedUserForView.id === user.id }">
                            {{ user.username }} ({{ user.nickname }})
                        </div>
                    </div>
                </div>
            </div>

            <!-- 搜索框 -->
            <div class="search-container">
                <input v-model="searchQuery" @input="performSearch" @blur="hideSearchResults" @focus="showSearchResults"
                    placeholder="搜索节点..." class="search-input" />
                <div v-show="showSearchResultsList && searchResults.length > 0" class="search-results">
                    <div v-for="node in searchResults" :key="node.id" @click="selectSearchResult(node)"
                        class="search-result-item">
                        {{ node.name }}
                    </div>
                </div>
            </div>
            <!-- 进度条 -->
            <div v-if="loading" class="progress-container">
                <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
                </div>
                <span class="progress-text">{{ loadingStatus }}</span>
            </div>
        </div>
        <div v-else class="toolbar">
            <button @click="toggleEditMode" v-if="user.userType === 1">进入编辑模式</button>
            <button @click="togglePreviewMode" v-if="user.userType === 1">{{ isInPreviewMode ? '退出预览模式' : '预览用户模式'
                }}</button>
            <button @click="startAddLink" v-if="isEditMode && !isAddingLink">添加链接</button>
            <button @click="cancelAddLink" v-if="isAddingLink">取消添加链接</button>
            <!-- 搜索框 -->
            <div class="search-container">
                <input v-model="searchQuery" @input="performSearch" @blur="hideSearchResults" @focus="showSearchResults"
                    placeholder="搜索节点..." class="search-input" />
                <div v-show="showSearchResultsList && searchResults.length > 0" class="search-results">
                    <div v-for="node in searchResults" :key="node.id" @click="selectSearchResult(node)"
                        class="search-result-item">
                        {{ node.name }}
                    </div>
                </div>
            </div>
            <!-- 进度条 -->
            <div v-if="loading" class="progress-container">
                <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
                </div>
                <span class="progress-text">{{ loadingStatus }}</span>
            </div>
            <button @click="goToHomeworkView">查看我的作业</button>
        </div>

        <!-- 加载动画 -->
        <div v-if="loading" class="loading-overlay">
            <div class="loading-spinner"></div>
            <p>正在加载数据，请稍候...</p>
        </div>
        <!-- 调试信息，用于确认loading状态 -->
        <!-- <div v-if="loading" style="position: absolute; top: 10px; left: 10px; z-index: 1001; background: yellow; padding: 5px;">
            Loading: {{ loading }}, Current nodes: {{ talentNodes.length }}
        </div> -->

        <div class="talent-tree" ref="talentTree" @mousedown="handleCanvasMouseDown" @mousemove="handleCanvasMouseMove"
            @mouseup="handleCanvasMouseUp" @mouseleave="handleCanvasMouseUp" @touchstart="handleCanvasMouseDown"
            @touchmove="handleCanvasMouseMove" @touchend="handleCanvasTouchEnd" @dblclick="handleCanvasDoubleClick"
            @click="handleCanvasClick" @wheel="handleWheel" :style="{
                cursor: isPanning ? 'grabbing' : isEditMode ? 'default' : 'default',
                width: canvasSize.width + 'px',
                height: canvasSize.height + 'px'
            }">
            <!-- 缩放容器 -->
            <div class="zoom-container" :style="{ transform: `scale(${scale})`, transformOrigin: '0 0' }">
                <!-- 连接线 --> <svg class="links-layer" ref="linksLayer">
                    <path v-for="link in visibleLinks" :key="`${link.source}-${link.target}`" :d="getLinkPath(link)"
                        :stroke="isLinkActive(link) ? (link.style.stroke || (link.graphId === 'required' ? '#ff0000' : '#0000ff')) : '#cccccc'"
                        :stroke-width="link.value" :stroke-dasharray="getLineStyle(link.style.lineStyle)" :fill="'none'"
                        v-show="shouldShowLink(link)" stroke-linecap="round" class="link-line" :class="{
                            'related-link': relatedLinks.has(`${link.source}-${link.target}`),
                            'special-highlighted-link': specialHighlightedLink &&
                                ((link.source === specialHighlightedLink.source && link.target === specialHighlightedLink.target) ||
                                    (link.source === specialHighlightedLink.target && link.target === specialHighlightedLink.source))
                        }" @dblclick="selectLink($event, link)">
                        <title>双击查看详情</title>
                    </path>

                    <!-- 控制点 -->
                    <g v-for="link in links" :key="`control-group-${link.source}-${link.target}`"
                        v-show="shouldShowLink(link)">
                        <template v-if="isEditMode && link.style.lineType === 'sCurve'">
                            <circle v-for="(point, index) in getControlPoints(link)"
                                :key="`control-${link.fromId}-${link.toId}-${index}`" :cx="point.x" :cy="point.y" r="6"
                                fill="#ff9800" stroke="#fff" stroke-width="2" class="control-point"
                                @mousedown="handleControlPointMouseDown($event, point, index, link)"
                                @dblclick="selectLink($event, link)">
                                <title>拖拽调整路径 | 双击查看详情</title>
                            </circle>
                        </template>
                        <!-- 直线连线的编辑点 -->
                        <template
                            v-else-if="isEditMode && (!link.style.lineType || link.style.lineType === 'straight')">
                            <!-- 在连线中点添加一个可见的编辑点 -->
                            <circle :cx="(getNodePosition(link.source).x + getNodePosition(link.target).x) / 2"
                                :cy="(getNodePosition(link.source).y + getNodePosition(link.target).y) / 2" r="6"
                                fill="#ff9800" stroke="#fff" stroke-width="2"
                                class="control-point straight-control-point"
                                @mousedown="handleStraightControlPointMouseDown($event, link)"
                                @dblclick="selectLink($event, link)">
                                <title>拖拽调整路径 | 双击编辑连接</title>
                            </circle>
                        </template>
                    </g>
                </svg>

                <!-- 天赋节点 -->
                <div v-for="node in displayedNodes" :key="node.id" class="talent-node" :class="{
                    'active': isNodeActuallyActive(node),
                    'inactive': !isNodeActuallyActive(node),
                    'selected': selectedNode && selectedNode.id === node.id,
                    'highlighted': highlightedNodes.has(node.id),
                    'related-node': relatedNodes.has(node.id),
                    'search-highlighted': searchMatchedNodes.has(node.id), 'rectangle': node.desc.shape === 'rectangle',
                    'rounded-rectangle': node.desc.shape === 'rounded-rectangle',
                    'circle': node.desc.shape === 'circle',
                    'triangle': node.desc.shape === 'triangle',
                    'source-node': isAddingLink && (!sourceNodes?.value || sourceNodes.value.length === 0) && !targetNode,
                    'target-node': isAddingLink && sourceNodes?.value?.some(n => n.id === node.id) && !targetNode,
                    'draggable': isEditMode,
                    'dragging': isDragging && draggedNode && draggedNode.id === node.id,
                    'low-lod': getLODDetailLevel() === 'low',
                    'medium-lod': getLODDetailLevel() === 'medium',
                    'high-lod': getLODDetailLevel() === 'high'
                }" :style="{
                    left: (node.desc.position.x + currentPanOffset.x) + 'px',
                    top: (node.desc.position.y + currentPanOffset.y) + 'px',
                    borderColor: isNodeActuallyActive(node) ? node.desc.borderColor : '#cccccc',
                    backgroundColor: isNodeActuallyActive(node) ? (node.desc.backgroundColor || 'transparent') : '#f0f0f0',
                    width: node.desc.size + 'px',
                    height: node.desc.size + 'px',
                    cursor: isEditMode ? 'move' : 'pointer',
                    borderStyle: node.desc.style?.borderStyle || 'solid',
                    borderWidth: (node.desc.style?.borderWidth || 2) + 'px',
                    opacity: node.desc.style?.opacity || 1
                }" :data-node-id="node.id" @click="handleNodeClick($event, node)"
                    @dblclick="handleNodeClick($event, node)" @touchend="handleNodeClick($event, node)"
                    @mousedown="handleDragStart($event, node)" @touchstart="handleDragStart($event, node)"
                    @mouseup="handleDragEnd" @contextmenu="handleNodeRightClick($event, node)">
                    <title>双击查看详情</title>
                    <!-- LOD优化：仅在高细节级别显示图标 -->
                    <img v-if="node.desc.icon && getLODDetailLevel() !== 'low'" :src="node.desc.icon" :alt="node.name"
                        class="node-icon"
                        :style="{ width: (node.desc.size * 0.6 - 4) + 'px', height: (node.desc.size * 0.6 - 4) + 'px', objectFit: 'contain' }">
                    <div class="node-name" :style="{ fontSize: (node.desc.size * 0.15) + 'px' }">{{ node.name }}
                    </div>
                </div>

                <!-- 选框 -->
                <div v-if="isSelecting && !isShiftPressed" class="selection-box" :style="{
                    left: (Math.min(selectionStart.x, selectionEnd.x) * scale + currentPanOffset.x) + 'px',
                    top: (Math.min(selectionStart.y, selectionEnd.y) * scale + currentPanOffset.y) + 'px',
                    width: (Math.abs(selectionEnd.x - selectionStart.x) * scale) + 'px',
                    height: (Math.abs(selectionEnd.y - selectionStart.y) * scale) + 'px'
                }"></div>
                <!-- 反选框 -->
                <div v-if="isSelecting && isShiftPressed" class="deselection-box" :style="{
                    left: (Math.min(selectionStart.x, selectionEnd.x) * scale + currentPanOffset.x) + 'px',
                    top: (Math.min(selectionStart.y, selectionEnd.y) * scale + currentPanOffset.y) + 'px',
                    width: (Math.abs(selectionEnd.x - selectionStart.x) * scale) + 'px',
                    height: (Math.abs(selectionEnd.y - selectionStart.y) * scale) + 'px'
                }"></div>

                <!-- 多选节点包围多边形（带圆角，交互用） -->
                <svg v-if="highlightedNodes.size > 1" class="selection-polygon-layer" :style="{ zIndex: 2 }">
                    <polygon :points="getRoundedSelectionPolygonPoints()" class="selection-polygon" :style="{
                        fill: `${selectionPolygonFillColor}${opacityToHex(selectionPolygonFillOpacity)}`,
                        stroke: `${selectionPolygonStrokeColor}${opacityToHex(selectionPolygonStrokeOpacity)}`,
                        strokeWidth: selectionPolygonStrokeWidth,
                        strokeDasharray: selectionPolygonStrokeDasharray,
                        pointerEvents: 'none'
                    }" />
                </svg>

                <!-- 节点星级评价（移到节点外部） -->
                <div v-for="node in displayedNodes" :key="'star-' + node.id" class="star-rating-external" :style="{
                    left: (node.desc.position.x + currentPanOffset.x + node.desc.size / 2 - 40) + 'px',
                    top: (node.desc.position.y + currentPanOffset.y + node.desc.size + 5) + 'px',
                    position: 'absolute',
                    zIndex: 3
                }">
                    <div class="star-rating" v-if="nodeStars[node.id] !== undefined">
                        <svg v-for="i in 5" :key="'ext-star-' + node.id + '-' + i" class="star"
                            :class="{ 'filled': i <= (nodeStars[node.id] || 0) }" viewBox="0 0 24 24" width="16"
                            height="16">
                            <path
                                d="M12 2 L15.09 8.26 L22 9.27 L17 14.14 L18.18 21.02 L12 17.77 L5.82 21.02 L7 14.14 L2 9.27 L8.91 8.26 Z" />
                        </svg>
                    </div>
                </div>
            </div> <!-- 缩放容器结束 -->
        </div>

        <!-- 右键菜单 -->
        <div v-if="contextMenu.visible && user.userType === 1" class="context-menu"
            :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }" @click.stop>
            <div class="context-menu-item" @click="setAsSourceNode">
                <span class="menu-icon">🎯</span>
                设为源节点（待拓展）
            </div>
            <div class="context-menu-item" @click="setAsTargetNode">
                <span class="menu-icon">🎯</span>
                设为目标节点（待拓展）
            </div>
            <div class="context-menu-item" @click="addTagToNode">
                <span class="menu-icon">🏷️</span>
                添加标签（待拓展）
            </div>
            <div class="context-menu-item" @click="deleteNode">
                <span class="menu-icon">🗑️</span>
                删除节点（待拓展）
            </div>
            <div class="context-menu-item" @click="expandNode">
                <span class="menu-icon">🔍</span>
                拓展节点（待拓展）
            </div>
            <div class="context-menu-item" @click="editNode">
                <span class="menu-icon">✏️</span>
                编辑节点（待拓展）
            </div>

            <div class="context-menu-item" @click="toggleNodeLock">
                <span class="menu-icon">{{ selectedNodeForContextMenu?.locked ? '🔓' : '🔒' }}（待拓展）</span>
                {{ selectedNodeForContextMenu?.locked ? '解锁节点' : '锁定节点' }}
            </div>
            <div class="context-menu-item" @click="setAsTag">
                <span class="menu-icon">🏷️</span>
                设为标签
            </div>
            <div class="context-menu-item submenu-trigger" @mouseenter="showGroupSubmenuWithPosition"
                @mouseleave="showGroupSubMenu = false">
                <span class="menu-icon">🎯</span>
                群组管理
                <div v-if="showGroupSubMenu" ref="groupSubmenuRef" class="context-submenu"
                    @mouseenter="showGroupSubmenuWithPosition" @mouseleave="showGroupSubMenu = false">
                    <div v-for="group in groups" :key="group.id" class="context-menu-item"
                        :class="{ 'group-in': isNodeInGroup(selectedNodeForContextMenu?.value, group.id), 'group-out': !isNodeInGroup(selectedNodeForContextMenu?.value, group.id) }"
                        @click="() => toggleNodeGroup(group.id)">
                        <span class="menu-icon">{{ isNodeInGroup(selectedNodeForContextMenu?.value, group.id) ? '🔵' :
                            '⚪' }}</span>
                        {{ group.name }}
                    </div>
                </div>
            </div>
            <div class="context-menu-item submenu-trigger" @mouseenter="showTagSubmenuWithPosition"
                @mouseleave="showTagSubMenu = false">
                <span class="menu-icon">🏷️</span>
                标签管理
                <div v-if="showTagSubMenu" ref="tagSubmenuRef" class="context-submenu"
                    @mouseenter="showTagSubmenuWithPosition" @mouseleave="showTagSubMenu = false">
                    <div v-for="tag in graphTypes" :key="tag.id" class="context-menu-item"
                        @click="() => handleTagSelection(tag)">
                        <span class="menu-icon">{{ isNodeInTag(tag.id) ? '🔵' : '⚪' }}</span>
                        {{ tag.name || tag.label }}
                    </div>
                </div>
            </div>
        </div>
        <!-- 节点详情模态框 -->
        <div v-if="selectedNode" class="node-detail-modal" @click="closeDetailModal">
            <div class="modal-content" @click.stop>
                <h2>{{ selectedNode.name }} 详情</h2>
                <div class="detail-content">
                    <img v-if="selectedNode.desc.icon" :src="selectedNode.desc.icon" :alt="selectedNode.name"
                        class="detail-icon">
                    <p v-if="selectedNode.desc.description">{{ selectedNode.desc.description }}</p>
                    <div class="node-properties">
                        <h3>属性</h3>
                        <ul>
                            <li v-for="(value, key) in selectedNode.desc.properties" :key="key">
                                <strong>{{ key }}:</strong> {{ value }}
                            </li>
                        </ul>
                    </div>

                    <!-- 学习节点关系列表 -->
                    <div class="node-study-links">
                        <h3>学习节点关系</h3>
                        <div v-for="studyNode in studyNodes" :key="`study-${studyNode.id}`" class="link-item">
                            <span>{{ studyNode.name }}</span>
                            <span v-if="parseNodeDesc(studyNode.desc).stars">⭐{{ parseNodeDesc(studyNode.desc).stars
                            }}</span>
                            <button v-if="parseNodeDesc(studyNode.desc).link"
                                @click="openLink(parseNodeDesc(studyNode.desc).link)" class="link-btn">前往学习</button>
                            <button v-if="isEditMode" @click="removeStudyNodeRelation(studyNode.id)"
                                class="delete-link-btn">删除</button>
                        </div>
                        <div v-if="isEditMode" class="add-study-node">
                            <input v-model="newStudyNodeName" placeholder="输入学习节点名称" />
                            <input v-model="newStudyNodeStars" placeholder="星级(1-5)" type="number" min="1" max="5"
                                @input="validateStars(newStudyNodeStars)" />
                            <input v-model="newStudyNodeLink" placeholder="链接地址" />
                            <button @click="addStudyNodeRelation">添加学习节点</button>
                        </div>
                    </div>

                    <!-- 练习节点关系列表 -->
                    <div class="node-practice-links">
                        <h3>练习节点关系</h3>
                        <div v-for="practiceNode in practiceNodes" :key="`practice-${practiceNode.id}`"
                            class="link-item">
                            <span>{{ practiceNode.name }}</span>
                            <span v-if="practiceNode.stars">⭐{{ practiceNode.stars }}</span>
                            <button v-if="parseNodeDesc(practiceNode.desc).link"
                                @click="openLink(parseNodeDesc(practiceNode.desc).link)" class="link-btn">前往练习</button>
                            <button v-if="isEditMode" @click="removePracticeNodeRelation(practiceNode.id)"
                                class="delete-link-btn">删除</button>
                        </div>
                        <div v-if="isEditMode" class="add-practice-node">
                            <input v-model="newPracticeNodeName" placeholder="输入练习节点名称" />
                            <input v-model="newPracticeNodeStars" placeholder="星级(1-5)" type="number" min="1" max="5"
                                @input="validateStars(newPracticeNodeStars)" />
                            <input v-model="newPracticeNodeLink" placeholder="链接地址" />
                            <button @click="addPracticeNodeRelation">添加练习节点</button>
                        </div>
                    </div>

                    <!-- 显示连接信息 -->
                    <div class="node-links" v-if="isEditMode">
                        <h3>连接关系</h3>
                        <div v-for="link in getNodeLinks(selectedNode.id)" :key="`${link.fromId}-${link.toId}`"
                            class="link-item">
                            <span>{{ getLinkedNodeName(link, selectedNode.id) }}</span>
                            <span :class="link.graphType">{{ link.graphType }}</span>
                            <span>强度: {{ link.strength }}</span>
                            <button @click="deleteLink(link)" class="delete-link-btn">删除</button>
                        </div>
                    </div>
                </div>
                <div v-if="isEditMode" class="edit-actions">
                    <label>
                        <input type="checkbox" v-model="selectedNode.desc.active" @change="updateNodeActivation">
                        激活节点
                    </label>
                    <button @click="editNode">编辑节点</button>
                    <button @click="deleteNode" class="delete-btn">删除节点</button>
                </div>
                <button class="close-btn" @click="closeDetailModal">关闭</button>
            </div>
        </div>
        <!-- 节点编辑模态框 -->
        <div v-if="showEditModal" class="node-edit-modal" @click="closeEditModal">
            <div class="modal-content" @click.stop>
                <h2>{{ editingNode.id ? '编辑节点' : '添加节点' }}</h2>
                <form @submit.prevent="saveNode">
                    <div class="form-group">
                        <label>节点名称:</label>
                        <input v-model="editingNode.name" type="text" required>
                    </div>
                    <div class="form-group">
                        <label>图标URL:</label>
                        <input v-model="editingNode.icon" type="text">
                    </div>
                    <div class="form-group">
                        <label>描述:</label>
                        <textarea v-model="editingNode.description"></textarea>
                    </div>

                    <!-- 临时节点标签显示区域 - 显示在“描述”之后 -->
                    <div v-if="batchNodes.length > 0" class="batch-nodes-display">
                        <label>已记录节点:</label>
                        <div class="batch-nodes-tags">
                            <span v-for="(node, index) in batchNodes" :key="index" class="batch-node-tag">
                                {{ node.name }}
                                <button type="button" @click="removeBatchNode(index)" class="remove-node-btn">×</button>
                            </span>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>记录节点:</label>
                        <button type="button" @click="recordNode" class="record-node-btn">记录当前节点</button>
                        <p class="info-text">点击记录当前输入的节点信息，可继续添加更多节点</p>
                    </div>

                    <div class="form-group">
                        <label>形状:</label>
                        <select v-model="editingNode.shape">
                            <option value="circle">圆形</option>
                            <option value="rectangle">长方形</option>
                            <option value="rounded-rectangle">圆角长方形</option>
                            <option value="triangle">三角形</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>大小 (40-120px):</label>
                        <input v-model.number="editingNode.size" type="range" min="40" max="120" required>
                        <span>{{ editingNode.size }}px</span>
                    </div>
                    <div class="form-group">
                        <label>边框颜色:</label>
                        <div class="color-picker-container">
                            <input v-model="editingNode.borderColor" type="color" class="color-input">
                            <div class="color-preview" :style="{ backgroundColor: editingNode.borderColor }"></div>
                            <span class="color-value">{{ editingNode.borderColor }}</span>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>背景颜色:</label>
                        <div class="color-picker-container">
                            <input v-model="editingNode.backgroundColor" type="color" class="color-input">
                            <div class="color-preview" :style="{ backgroundColor: editingNode.backgroundColor }">
                            </div>
                            <span class="color-value">{{ editingNode.backgroundColor }}</span>
                        </div>
                        <label>
                            <input type="checkbox" v-model="editingNode.transparentBg"> 透明背景
                        </label>
                    </div>
                    <div class="form-group">
                        <label>位置 X:</label>
                        <input v-model.number="editingNode.position.x" type="number" step="1">
                    </div>
                    <div class="form-group">
                        <label>位置 Y:</label>
                        <input v-model.number="editingNode.position.y" type="number" step="1">
                    </div>
                    <div class="form-group">
                        <label>属性 (JSON格式):</label>
                        <textarea v-model="propertiesJson" placeholder='{"属性1": "值1", "属性2": "值2"}'></textarea>
                    </div>
                    <div class="form-group">
                        <h3>节点样式设置</h3>
                    </div>
                    <!-- 预设样式选择 -->
                    <div class="form-group preset-selection-group">
                        <label>预设样式:</label>
                        <select v-model="selectedNodePreset" @change="applyNodePresetStyle">
                            <option value="">-- 选择预设样式 --</option>
                            <option v-for="(preset, index) in presetStyles" :key="'node-' + index" :value="index">
                                {{ preset.name }}
                            </option>
                        </select>
                        <button type="button" @click="applyNodePresetStyle" :disabled="selectedNodePreset === ''"
                            class="apply-preset-btn">应用预设</button>
                    </div>
                    <div class="form-group">
                        <label>节点边框样式:</label>
                        <select v-model="editingNode.style.borderStyle">
                            <option value="solid">实线</option>
                            <option value="dashed">虚线</option>
                            <option value="dotted">点线</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>节点边框宽度 (1-10px):</label>
                        <input v-model.number="editingNode.style.borderWidth" type="range" min="1" max="10">
                        <span>{{ editingNode.style.borderWidth }}px</span>
                    </div>
                    <div class="form-group">
                        <label>节点透明度 (0-1):</label>
                        <input v-model.number="editingNode.style.opacity" type="range" min="0" max="1" step="0.1">
                        <span>{{ editingNode.style.opacity }}</span>
                    </div>
                    <div class="form-actions">
                        <button type="submit">保存</button>
                        <button type="button" @click="closeEditModal">取消</button>
                    </div>
                </form>
            </div>
        </div>

        <!-- 连接编辑模态框 -->
        <div v-if="showLinkModal" class="link-edit-modal" @click="closeLinkModal">
            <div class="modal-content" @click.stop>
                <h2>{{ getLinkModalTitle() }}</h2>
                <form @submit.prevent="saveLink">
                    <!-- 显示当前选择的源节点列表 -->
                    <div class="form-group" v-if="sourceNodes && sourceNodes.length > 0">
                        <label>源节点 ({{ sourceNodes.length || 0 }} 个):</label>
                        <div class="node-list">
                            <div v-for="node in sourceNodes" :key="node.id" class="selected-node">
                                {{ node.name }}
                            </div>
                        </div>
                    </div>
                    <!-- 显示当前选择的目标节点 -->
                    <div class="form-group" v-if="targetNode">
                        <label>目标节点:</label>
                        <div class="selected-node">
                            {{ targetNode.name }}
                        </div>
                    </div>
                    <!-- 在多源节点模式下，目标节点由用户选择，不显示下拉框 -->
                    <div class="form-group" v-if="!targetNode">
                        <label>目标节点:</label>
                        <p class="info-text">请先选择源节点，然后点击目标节点</p>
                    </div>
                    <div class="form-group">
                        <label>连接强度 (1-10):</label>
                        <input v-model.number="editingLink.value" type="range" min="1" max="10" required>
                        <span>{{ editingLink.value }}</span>
                    </div>
                    <!-- 预设样式选择 -->
                    <div class="form-group preset-selection-group">
                        <label>预设样式:</label>
                        <select v-model="selectedLinkPreset" @change="applyLinkPresetStyle">
                            <option value="">-- 选择预设样式 --</option>
                            <option v-for="(preset, index) in presetStyles" :key="'link-' + index" :value="index">
                                {{ preset.name }}
                            </option>
                        </select>
                        <button type="button" @click="applyLinkPresetStyle" :disabled="selectedLinkPreset === ''"
                            class="apply-preset-btn">应用预设</button>
                    </div>
                    <div class="form-group">
                        <label>连接颜色:</label>
                        <div class="color-picker-container">
                            <input v-model="editingLink.style.stroke" type="color" class="color-input">
                            <div class="color-preview" :style="{ backgroundColor: editingLink.style.stroke }"></div>
                            <span class="color-value">{{ editingLink.style.stroke }}</span>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>线条样式:</label>
                        <select v-model="editingLink.style.lineStyle">
                            <option value="solid">实线</option>
                            <option value="dashed">虚线</option>
                            <option value="dotted">点线</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>线型:</label>
                        <select v-model="editingLink.style.lineType">
                            <option value="straight">直线</option>
                            <option value="sCurve">S型线</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>圆角大小 (0-30):</label>
                        <input v-model.number="editingLink.style.cornerRadius" type="range" min="0" max="30" required>
                        <span>{{ editingLink.style.cornerRadius }}px</span>
                    </div>
                    <div class="form-actions">
                        <button type="submit">保存连接</button>
                        <button type="button" @click="closeLinkModal">取消</button>
                    </div>
                </form>
            </div>
        </div>

        <!-- 连接详情模态框 -->
        <div v-if="selectedLink" class="link-detail-modal" @click="closeLinkDetailModal">
            <div class="modal-content" @click.stop>
                <h2>连接详情</h2>
                <div class="link-info">
                    <p><strong>从:</strong> {{ getLinkedNodeName({ source: selectedLink.source },
                        selectedLink.target)
                    }}
                    </p>
                    <p><strong>到:</strong> {{ getLinkedNodeName({ target: selectedLink.target },
                        selectedLink.source) }}
                    </p>
                    <p><strong>强度:</strong> {{ selectedLink.value }}</p>
                    <p><strong>样式:</strong> {{ getLineStyleText(selectedLink.style.lineStyle) }}</p>
                    <p><strong>线型:</strong> {{ getLineTypeText(selectedLink.style.lineType) }}</p>
                    <p v-if="selectedLink.style.lineType === 'sCurve'"><strong>圆角:</strong> {{
                        selectedLink.style.cornerRadius }}px</p>
                </div>
                <div v-if="isEditMode" class="edit-actions">
                    <button @click="editLink">编辑连接</button>
                    <button @click="deleteLink(selectedLink)" class="delete-btn">删除连接</button>
                </div>
                <button class="close-btn" @click="closeLinkDetailModal">关闭</button>
            </div>
        </div>

        <!-- 评分弹窗 -->
        <div v-if="showRatingModal" class="rating-modal" @click="closeRatingModal">
            <div class="modal-content rating-content" @click.stop>
                <h2>评分</h2>
                <div class="rating-layout">
                    <!-- 左侧：nodes列表 -->
                    <div class="nodes-section">
                        <h3>节点列表</h3>
                        <input v-model="nodeSearchText" placeholder="搜索节点" class="search-input" />
                        <div class="nodes-list">
                            <div v-for="node in filteredNodes" :key="node.id" class="node-item"
                                :class="{ selected: selectedNodeForRating && selectedNodeForRating.id === node.id }"
                                @click="selectNodeForRating(node)">
                                {{ node.name }}
                            </div>
                        </div>
                    </div>

                    <!-- 中间：题目列表 -->
                    <div class="questions-section">
                        <h3>题目列表</h3>
                        <div class="questions-list">
                            <div v-for="question in practiceNodes" :key="question.id" class="question-item"
                                :class="{ selected: selectedQuestion && selectedQuestion.id === question.id, faded: selectedNodeForRating && selectedNodeForRating.id !== question.fromNodeId }"
                                @click="selectQuestion(question)">
                                {{ question.name }}
                            </div>
                        </div>
                    </div>

                    <!-- 右侧：用户列表 -->
                    <div class="users-section">
                        <h3>用户列表</h3>
                        <input v-model="userSearchText" placeholder="搜索用户" class="search-input" />
                        <div class="users-list">
                            <div v-for="user in filteredUsers" :key="user.id" class="user-item"
                                :class="{ selected: selectedUser && selectedUser.id === user.id }"
                                @click="selectUser(user)">
                                {{ user.username }} ({{ user.nickname }})
                            </div>
                        </div>
                    </div>

                    <!-- 最右侧：属性设置 -->
                    <div class="properties-section">
                        <h3>属性设置</h3>
                        <div class="property-item">
                            <label>星级:</label>
                            <div class="star-rating">
                                <span v-for="i in 5" :key="i" class="star" :class="{ filled: i <= stars }"
                                    @click="setStars(i)">
                                    ★
                                </span>
                            </div>
                        </div>
                        <div class="property-item">
                            <label>备注:</label>
                            <textarea v-model="remark" placeholder="请输入备注"></textarea>
                        </div>
                        <div class="property-item">
                            <label>
                                <input type="checkbox" v-model="result" />
                                结果 (正确/错误)
                            </label>
                        </div>
                    </div>
                </div>
                <div class="modal-actions">
                    <button @click="submitRating" class="submit-btn">提交</button>
                    <button @click="closeRatingModal" class="cancel-btn">取消</button>
                </div>
            </div>
        </div>

        <!-- 设置弹窗 -->
        <div v-if="showSettingsModal" class="settings-modal" @click="closeSettingsModal">
            <div class="modal-content settings-content" @click.stop>
                <h2>设置</h2>
                <div class="settings-layout">
                    <!-- 显示模式设置 -->
                    <div class="setting-section">
                        <h3>显示模式</h3>
                        <div class="form-group">
                            <label>显示选项:</label>
                            <select v-model="displayMode">
                                <option value="all">全部显示</option>
                                <option value="active">仅显示激活节点</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>多边形扩展像素 (10-200):</label>
                            <input v-model.number="selectionPolygonExpandOffset" type="range" min="10" max="200">
                            <span>{{ selectionPolygonExpandOffset }}px</span>
                        </div>
                        <div class="form-group">
                            <label>多边形圆角半径 (5-50):</label>
                            <input v-model.number="selectionPolygonCornerRadius" type="range" min="5" max="50">
                            <span>{{ selectionPolygonCornerRadius }}px</span>
                        </div>
                        <div class="form-group">
                            <label>多边形填充颜色:</label>
                            <div class="color-picker-container">
                                <input v-model="selectionPolygonFillColor" type="color" class="color-input">
                                <div class="color-preview" :style="{ backgroundColor: selectionPolygonFillColor }">
                                </div>
                                <span class="color-value">{{ selectionPolygonFillColor }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>多边形边框颜色:</label>
                            <div class="color-picker-container">
                                <input v-model="selectionPolygonStrokeColor" type="color" class="color-input">
                                <div class="color-preview" :style="{ backgroundColor: selectionPolygonStrokeColor }">
                                </div>
                                <span class="color-value">{{ selectionPolygonStrokeColor }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>多边形边框宽度 (1-10px):</label>
                            <input v-model.number="selectionPolygonStrokeWidth" type="range" min="1" max="10">
                            <span>{{ selectionPolygonStrokeWidth }}px</span>
                        </div>
                        <div class="form-group">
                            <label>多边形填充透明度 (0-1):</label>
                            <input v-model.number="selectionPolygonFillOpacity" type="range" min="0" max="1" step="0.1">
                            <span>{{ selectionPolygonFillOpacity }}</span>
                        </div>
                        <div class="form-group">
                            <label>多边形边框透明度 (0-1):</label>
                            <input v-model.number="selectionPolygonStrokeOpacity" type="range" min="0" max="1"
                                step="0.1">
                            <span>{{ selectionPolygonStrokeOpacity }}</span>
                        </div>
                        <div class="form-group">
                            <label>多边形边框线型:</label>
                            <select v-model="selectionPolygonStrokeDasharray">
                                <option value="">实线</option>
                                <option value="5,5">虚线</option>
                                <option value="2,5">点线</option>
                                <option value="10,5,2,5">点划线</option>
                            </select>
                        </div>
                    </div>

                    <!-- 网格间距设置 -->
                    <div class="setting-section">
                        <h3>网格间距</h3>
                        <div class="form-group">
                            <label>预览模式网格间距 (100-400px):</label>
                            <input v-model.number="gridSpacing" type="range" min="20" max="200">
                            <span>{{ gridSpacing }}px</span>
                        </div>
                    </div>

                    <!-- 预设样式管理 -->
                    <div class="setting-section">
                        <h3>预设样式管理</h3>
                        <div class="form-group">
                            <label>预设样式名称:</label>
                            <input v-model="presetName" type="text" placeholder="输入预设样式名称" class="preset-name-input" />
                            <button @click="savePresetStyle" class="save-preset-btn">保存为预设</button>
                        </div>
                        <div class="form-group">
                            <label>选择预设样式:</label>
                            <select v-model="selectedPreset" @change="applyPresetStyle">
                                <option value="">-- 选择预设样式 --</option>
                                <option v-for="(preset, index) in presetStyles" :key="index" :value="index">
                                    {{ preset.name }}
                                </option>
                            </select>
                            <button v-if="selectedPreset !== ''" @click="deletePresetStyle"
                                class="delete-preset-btn">删除预设</button>
                        </div>
                        <div class="preset-list">
                            <h4>预设样式列表:</h4>
                            <div v-if="presetStyles.length === 0" class="no-presets">
                                暂无预设样式
                            </div>
                            <div v-else class="preset-items">
                                <div v-for="(preset, index) in presetStyles" :key="index" class="preset-item"
                                    :class="{ 'selected': selectedPreset === index }" @click="selectPreset(index)">
                                    <span class="preset-name">{{ preset.name }}</span>
                                    <div class="preset-preview">
                                        <div class="node-preview" :style="{
                                            backgroundColor: preset.nodeStyle.backgroundColor,
                                            borderColor: preset.nodeStyle.borderColor,
                                            borderStyle: preset.nodeStyle.borderStyle,
                                            borderWidth: preset.nodeStyle.borderWidth + 'px'
                                        }"></div>
                                        <div class="line-preview" :style="{
                                            backgroundColor: preset.lineStyle.stroke
                                        }"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 默认节点样式设置 -->
                    <div class="setting-section">
                        <h3>默认节点样式</h3>
                        <div class="form-group">
                            <label>形状:</label>
                            <select v-model="defaultNodeStyle.shape">
                                <option value="circle">圆形</option>
                                <option value="rectangle">长方形</option>
                                <option value="rounded-rectangle">圆角长方形</option>
                                <option value="triangle">三角形</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>大小 (40-120px):</label>
                            <input v-model.number="defaultNodeStyle.size" type="range" min="40" max="120">
                            <span>{{ defaultNodeStyle.size }}px</span>
                        </div>
                        <div class="form-group">
                            <label>边框颜色:</label>
                            <div class="color-picker-container">
                                <input v-model="defaultNodeStyle.borderColor" type="color" class="color-input">
                                <div class="color-preview" :style="{ backgroundColor: defaultNodeStyle.borderColor }">
                                </div>
                                <span class="color-value">{{ defaultNodeStyle.borderColor }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>背景颜色:</label>
                            <div class="color-picker-container">
                                <input v-model="defaultNodeStyle.backgroundColor" type="color" class="color-input">
                                <div class="color-preview"
                                    :style="{ backgroundColor: defaultNodeStyle.backgroundColor }"></div>
                                <span class="color-value">{{ defaultNodeStyle.backgroundColor }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>边框样式:</label>
                            <select v-model="defaultNodeStyle.borderStyle">
                                <option value="solid">实线</option>
                                <option value="dashed">虚线</option>
                                <option value="dotted">点线</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>边框宽度 (1-10px):</label>
                            <input v-model.number="defaultNodeStyle.borderWidth" type="range" min="1" max="10">
                            <span>{{ defaultNodeStyle.borderWidth }}px</span>
                        </div>
                        <div class="form-group">
                            <label>透明度 (0-1):</label>
                            <input v-model.number="defaultNodeStyle.opacity" type="range" min="0" max="1" step="0.1">
                            <span>{{ defaultNodeStyle.opacity }}</span>
                        </div>
                    </div>

                    <!-- 默认线条样式设置 -->
                    <div class="setting-section">
                        <h3>默认线条样式</h3>
                        <div class="form-group">
                            <label>线条颜色:</label>
                            <div class="color-picker-container">
                                <input v-model="defaultLineStyle.stroke" type="color" class="color-input">
                                <div class="color-preview" :style="{ backgroundColor: defaultLineStyle.stroke }">
                                </div>
                                <span class="color-value">{{ defaultLineStyle.stroke }}</span>
                            </div>
                        </div>
                        <div class="form-group">
                            <label>线条样式:</label>
                            <select v-model="defaultLineStyle.lineStyle">
                                <option value="solid">实线</option>
                                <option value="dashed">虚线</option>
                                <option value="dotted">点线</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>线型:</label>
                            <select v-model="defaultLineStyle.lineType">
                                <option value="straight">直线</option>
                                <option value="sCurve">S型线</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>圆角大小 (0-30):</label>
                            <input v-model.number="defaultLineStyle.cornerRadius" type="range" min="0" max="30">
                            <span>{{ defaultLineStyle.cornerRadius }}px</span>
                        </div>
                    </div>
                </div>
                <div class="modal-actions">
                    <button @click="applySettings" class="submit-btn">确认</button>
                    <button @click="closeSettingsModal" class="cancel-btn">取消</button>
                </div>
            </div>
        </div>
    </div> <!-- talent-container 结束 -->

    <!-- 布置作业弹窗 -->
    <div v-if="showHomeworkModal" class="homework-modal" @click="closeHomeworkModal">
        <div class="modal-content homework-content" @click.stop>
            <h2>布置作业</h2>
            <div class="homework-layout">
                <!-- 左侧：nodes列表 -->
                <div class="nodes-section">
                    <h3>节点列表</h3>
                    <input v-model="homeworkNodeSearchText" placeholder="搜索节点" class="search-input" />
                    <div class="nodes-list">
                        <div v-for="node in filteredHomeworkNodes" :key="node.id" class="node-item"
                            :class="{ selected: selectedNodeForHomework && selectedNodeForHomework.id === node.id }"
                            @click="selectNodeForHomework(node)">
                            {{ node.name }}
                        </div>
                    </div>
                </div>

                <!-- 中间：题目列表 -->
                <div class="questions-section">
                    <h3>题目列表</h3>
                    <div class="questions-list">
                        <div v-for="question in homeworkQuestions" :key="question.id" class="question-item"
                            :class="{ selected: homeworkTasks.some(task => task.id === question.id), faded: selectedNodeForHomework && selectedNodeForHomework.id !== question.fromNodeId }"
                            @click="addQuestionToHomework(question)">
                            {{ question.name }}
                        </div>
                    </div>
                </div>

                <!-- 右侧：用户列表 -->
                <div class="users-section">
                    <h3>用户列表</h3>
                    <input v-model="homeworkUserSearchText" placeholder="搜索用户" class="search-input" />
                    <div class="users-list">
                        <div v-for="user in filteredHomeworkUsers" :key="user.id" class="user-item"
                            :class="{ selected: selectedUserForHomework && selectedUserForHomework.id === user.id }"
                            @click="selectUserForHomework(user)">
                            {{ user.username }} ({{ user.nickname }})
                        </div>
                    </div>
                </div>

                <!-- 最右侧：任务列表 -->
                <div class="properties-section">
                    <h3>任务列表</h3>
                    <div class="tasks-list">
                        <div v-for="task in homeworkTasks" :key="task.id" class="task-item">
                            <span>{{ task.name }}</span>
                            <button @click="removeQuestionFromHomework(task.id)" class="remove-task-btn">移除</button>
                        </div>
                        <div v-if="homeworkTasks.length === 0" class="empty-tasks">
                            请从题目列表中选择题目添加到任务列表
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-actions">
                <button @click="submitHomework" class="submit-btn">提交</button>
                <button @click="closeHomeworkModal" class="cancel-btn">取消</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* 用户切换器样式 */
.user-switcher-container {
    position: relative;
    display: inline-block;
    margin: 0 10px;
}

.user-switcher-btn {
    background-color: #409eff;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
}

.user-switcher-btn:hover {
    background-color: #66b1ff;
}

.user-switcher-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    z-index: 1000;
    width: 300px;
    max-height: 400px;
    overflow-y: auto;
}

.search-input {
    width: calc(100% - 16px);
    padding: 8px;
    margin: 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 14px;
}

.users-list {
    max-height: 300px;
    overflow-y: auto;
}

.user-item {
    padding: 10px 15px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;
}

.user-item:hover {
    background-color: #f5f7fa;
}

.user-item.selected {
    background-color: #f0f9ff;
    color: #409eff;
    font-weight: bold;
}

.user-item:last-child {
    border-bottom: none;
}

/* 工具栏样式 */
.toolbar {
    display: flex;
    align-items: center;
    padding: 10px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;
    flex-wrap: wrap;
}

.toolbar-item {
    margin: 5px 10px;
}

.toolbar-button {
    padding: 6px 12px;
    margin: 0 5px;
    border: 1px solid #ccc;
    background-color: white;
    cursor: pointer;
    border-radius: 4px;
    font-size: 14px;
}

.toolbar-button:hover {
    background-color: #e6e6e6;
}

/* 节点样式 */
.node {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: white;
    cursor: pointer;
    margin: 5px;
    transition: all 0.3s;
}

.node:hover {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.node.selected {
    border-color: #409eff;
    background-color: #f0f9ff;
}

.node.active {
    border-color: #67c23a;
    background-color: #f0f9eb;
}

/* 链接样式 */
.links-container {
    display: flex;
    flex-direction: column;
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
    margin: 10px 0;
}

.link-item {
    padding: 8px;
    border-bottom: 1px solid #eee;
    cursor: pointer;
}

.link-item:hover {
    background-color: #f5f7fa;
}

.link-item:last-child {
    border-bottom: none;
}

/* 评分弹窗样式 */
.rating-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.rating-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    min-width: 500px;
    max-width: 800px;
    max-height: 80vh;
    overflow-y: auto;
}

.rating-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.rating-title {
    font-size: 18px;
    font-weight: bold;
}

.rating-close {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
}

/* 设置弹窗样式 */
.settings-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.settings-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    min-width: 600px;
    max-width: 900px;
    max-height: 80vh;
    overflow-y: auto;
}

.settings-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.settings-title {
    font-size: 18px;
    font-weight: bold;
}

.settings-close {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
}

/* 作业弹窗样式 */
.homework-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.homework-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    min-width: 800px;
    max-width: 1200px;
    max-height: 80vh;
    overflow-y: auto;
}

.homework-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.homework-title {
    font-size: 18px;
    font-weight: bold;
}

.homework-close {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
}

/* 表格样式 */
.homework-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
}

.homework-table th,
.homework-table td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
}

.homework-table th {
    background-color: #f5f5f5;
}

/* 搜索输入样式 */
.search-input {
    width: 100%;
    padding: 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 14px;
}

/* 过滤器样式 */
.filters-container {
    display: flex;
    gap: 10px;
    margin: 10px 0;
    flex-wrap: wrap;
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.filter-label {
    font-size: 14px;
    font-weight: bold;
}

/* 布局样式 */
.main-container {
    display: flex;
    flex-direction: column;
    height: 100vh;
}

.content-container {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.left-panel {
    width: 300px;
    border-right: 1px solid #ddd;
    overflow-y: auto;
    padding: 10px;
}

.right-panel {
    flex: 1;
    padding: 10px;
    overflow-y: auto;
}

/* 加载动画样式 */
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 3000;
}

.loading-spinner {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* 按钮样式 */
.btn {
    padding: 6px 12px;
    border: 1px solid #ccc;
    background-color: white;
    cursor: pointer;
    border-radius: 4px;
    font-size: 14px;
    margin: 0 2px;
}

.btn:hover {
    background-color: #e6e6e6;
}

.btn-primary {
    background-color: #409eff;
    color: white;
    border-color: #409eff;
}

.btn-primary:hover {
    background-color: #66b1ff;
    border-color: #66b1ff;
}

.btn-success {
    background-color: #67c23a;
    color: white;
    border-color: #67c23a;
}

.btn-success:hover {
    background-color: #85ce61;
    border-color: #85ce61;
}

.btn-danger {
    background-color: #f56c6c;
    color: white;
    border-color: #f56c6c;
}

.btn-danger:hover {
    background-color: #f78989;
    border-color: #f78989;
}

/* 星级评分样式 */
.star-rating {
    display: flex;
    gap: 2px;
}

.star {
    font-size: 20px;
    cursor: pointer;
    color: #ddd;
    transition: color 0.2s;
}

.star.filled {
    color: #ffd700;
}

.star:hover {
    color: #ffa500;
}

/* 输入框样式 */
.input-field {
    width: 100%;
    padding: 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 14px;
}

.input-field:focus {
    outline: none;
    border-color: #409eff;
}

/* 选择框样式 */
.select-field {
    width: 100%;
    padding: 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
}

/* 标签样式 */
.tag {
    display: inline-block;
    padding: 2px 6px;
    background-color: #ecf5ff;
    color: #409eff;
    border-radius: 4px;
    font-size: 12px;
    margin: 2px;
}

.tag:hover {
    cursor: pointer;
    background-color: #409eff;
    color: white;
}

/* 拖拽样式 */
.draggable {
    cursor: move;
}

.draggable:hover {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

/* 滚动条样式 */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* 进度条样式 */
.progress-container {
    display: flex;
    align-items: center;
    margin-left: 15px;
    min-width: 200px;
}

.progress-bar {
    width: 120px;
    height: 10px;
    background-color: #f0f0f0;
    border-radius: 5px;
    overflow: hidden;
    margin-right: 10px;
    border: 1px solid #ddd;
}

.progress-fill {
    height: 100%;
    background-color: #409eff;
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 12px;
    color: #666;
    white-space: nowrap;
}
</style>

<script>
import { ref, reactive, onMounted, onUnmounted, watch, nextTick, computed } from 'vue';
import graphService from '@/services/graphService';
import AuthService from '@/services/authService';
import TopBar from '@/components/TopBar.vue';
export default {
    name: 'TalentSystem',
    components: {
        TopBar
    },
    setup() {
        // 用户信息
        const user = ref({
            id: 1,
            userType: 10, // 默认用户类型为10
            name: '用户名',
            avatar: require('@/assets/logo.png')
        });

        // 加载状态
        const loading = ref(false);

        // 加载进度相关变量
        const loadingProgress = ref(0); // 加载进度百分比
        const loadingStatus = ref(''); // 加载状态文本

        // 应用信息
        const apps = ref([]);

        // 加载用户信息
        const loadUserInfo = () => {
            const currentUser = AuthService.getCurrentUser();
            if (currentUser) {
                console.log('当前用户信息:', currentUser);
                user.value.name = currentUser.nickname || currentUser.username || '用户名';
                user.value.id = currentUser.userid || currentUser.id || 1;
                // 确保userType正确设置，如果没有则默认为10
                // 先尝试获取userType字段，如果不存在则尝试user_type字段
                const userType = currentUser.userType !== undefined ? currentUser.userType : (currentUser.user_type !== undefined ? currentUser.user_type : null);
                user.value.userType = (userType !== null) ? userType : 10;
                console.log("userType:", user.value.userType);
            }
        };

        // 加载应用信息
        const loadAppInfo = async () => {
            try {
                // 导入UserMainService
                const UserMainService = (await import('@/services/userMainService.js')).default;
                const response = await UserMainService.getAllApps(user.value.userType);
                if (response.status === 200 && response.apps) {
                    apps.value = response.apps.map(app => ({
                        id: app.id,
                        name: app.name,
                        desc: app.desc,
                        link: app.name === '未来引擎' ? '/futureengine' : app.link,
                        icon: app.icon || 'code',
                        used: app.used || false
                    }));
                }
            } catch (error) {
                console.error('获取应用数据失败:', error);
                apps.value = [
                    { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
                    { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
                    { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
                    { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/graph', icon: 'share', used: false },
                    { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/abhs', icon: 'quiz', used: false },
                    { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
                    { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
                ];
            }
        };

        // 天赋节点数据
        const talentNodes = ref([]);

        // 节点星级数据
        const nodeStars = ref({});
        const futureEngineGraphId = ref(null);
        const futureStudyGraphId = ref(null);
        const futurePracticeGraphId = ref(null);

        // 多边形选择相关设置
        const selectionPolygonExpandOffset = ref(80); // 选择多边形扩展像素
        const selectionPolygonCornerRadius = ref(20); // 选择多边形圆角半径
        const selectionPolygonFillColor = ref('#4A90E2'); // 选择多边形填充颜色
        const selectionPolygonFillOpacity = ref(0.3); // 选择多边形填充透明度
        const selectionPolygonStrokeColor = ref('#4A90E2'); // 选择多边形边框颜色
        const selectionPolygonStrokeOpacity = ref(1); // 选择多边形边框透明度
        const selectionPolygonStrokeDasharray = ref(''); // 选择多边形边框线型
        const selectionPolygonStrokeWidth = ref(2); // 选择多边形边框宽度

        // 缩放相关数据
        const scale = ref(1);
        const minScale = ref(0.1);
        const maxScale = ref(3);

        // 学习节点和练习节点数据
        const studyNodes = ref([]);
        const practiceNodes = ref([]);

        // 跟踪预览模式状态
        const isInPreviewMode = ref(false);

        // 网格间距设置
        const gridSpacing = ref(200);
        // 存储当前选中节点的学习节点和练习节点关系
        // const selectedNodeStudyRelations = ref([]);
        // const selectedNodePracticeRelations = ref([]);
        // 新增：用于添加新学习节点和练习节点的输入框数据
        const newStudyNodeName = ref('');
        const newPracticeNodeName = ref('');
        const newStudyNodeStars = ref(0);
        const newStudyNodeLink = ref('');
        const newPracticeNodeStars = ref(0);
        const newPracticeNodeLink = ref('');
        // 存储节点的原始位置信息
        const originalNodePositions = ref({});
        // 存储连接线的原始offset信息
        const originalLinkOffsets = ref({});

        // 存储预览模式下的节点位置
        const previewModeNodePositions = ref({});
        // 临时生成匹配各nodeid的nodeStars数据的函数
        const generateTempNodeStars = () => {            // 清空现有的nodeStars数据
            nodeStars.value = {};

            // 为每个节点生成随机星级数据（1-5星）
            talentNodes.value.forEach(node => {
                // 生成1到5之间的随机整数作为星级
                const stars = Math.floor(Math.random() * 5) + 1;
                nodeStars.value[node.id] = stars;
            });

            console.log('Generated temporary node stars data:', nodeStars.value);
        };

        // 群组和标签数据
        const groups = ref([]);
        const graphTypes = ref([]);

        // 加载群组和标签数据
        const loadGroupsAndTags = async () => {
            // 只有userType为1的用户才能使用群组和标签功能
            if (user.value.userType !== 1) {
                console.log('普通用户无法使用群组和标签功能');
                return;
            }

            try {
                console.log('开始加载标签数据...');
                // 获取所有标签（graphTypes）
                const tagsResponse = await graphService.findAllTags();
                console.log('标签API响应:', tagsResponse);
                if (tagsResponse && tagsResponse.status === 200 && tagsResponse.data) {
                    console.log('标签数据:', tagsResponse.data);
                    graphTypes.value = tagsResponse.data.map(tag => ({
                        id: tag.id || tag.name,
                        name: tag.name || tag.tagName,
                        label: tag.name || tag.tagName
                    }));
                    console.log('转换后的graphTypes:', graphTypes.value);
                } else {
                    console.log('标签API响应不包含有效数据:', tagsResponse);
                }

                // 获取群组数据 - 从API获取群组信息
                // 由于可能没有专门的群组API，我们可以使用标签作为群组，或从节点数据中获取群组信息
                try {
                    // 尝试获取群组数据（如果后端有提供群组API）
                    // 这里先使用标签作为群组的替代方案，可以根据需要扩展
                    const groupsResponse = await graphService.findAllTags(); // 使用标签API作为群组数据源
                    if (groupsResponse && groupsResponse.status === 200 && groupsResponse.data) {
                        groups.value = groupsResponse.data.map(tag => ({
                            id: tag.id || tag.name,
                            name: tag.name || tag.tagName,
                            label: tag.name || tag.tagName
                        }));
                    } else {
                        // 如果没有群组数据，使用标签数据作为群组
                        groups.value = [...graphTypes.value];
                    }
                } catch (error) {
                    console.error('获取群组数据失败，使用标签作为群组:', error);
                    // 如果获取群组失败，使用标签作为群组
                    groups.value = [...graphTypes.value];
                }
            } catch (error) {
                console.error('加载群组和标签数据失败:', error);
            }
        };

        // 连接数据
        const links = ref([
            // GESP认证路径连接
            {
                source: 1,
                target: 2,
                graphId: '前置要求',
                value: 3,
                style: {
                    stroke: '#2196f3',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 15
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 2,
                target: 3,
                graphId: '前置要求',
                value: 3,
                style: {
                    stroke: '#4caf50',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 15
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 3,
                target: 4,
                graphId: '前置要求',
                value: 3,
                style: {
                    stroke: '#ff9800',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 15
                },
                offset: { x: 0, y: 0 }
            },
            // CSP路径连接
            {
                source: 4,
                target: 5,
                graphId: '推荐路径',
                value: 2,
                style: {
                    stroke: '#f44336',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 15
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 5,
                target: 6,
                graphId: '进阶路径',
                value: 2,
                style: {
                    stroke: '#e91e63',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 15
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 6,
                target: 7,
                graphId: '竞赛路径',
                value: 2,
                style: {
                    stroke: '#673ab7',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 15
                },
                offset: { x: 0, y: 0 }
            },
            // 细节知识点连接
            {
                source: 1,
                target: 8,
                graphId: '包含知识点',
                value: 1,
                style: {
                    stroke: '#03a9f4',
                    lineStyle: 'dashed',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 1,
                target: 9,
                graphId: '包含知识点',
                value: 1,
                style: {
                    stroke: '#03a9f4',
                    lineStyle: 'dashed',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 2,
                target: 10,
                graphId: '包含知识点',
                value: 1,
                style: {
                    stroke: '#8bc34a',
                    lineStyle: 'dashed',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 2,
                target: 11,
                graphId: '包含知识点',
                value: 1,
                style: {
                    stroke: '#8bc34a',
                    lineStyle: 'dashed',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 3,
                target: 12,
                graphId: '包含知识点',
                value: 1,
                style: {
                    stroke: '#ffc107',
                    lineStyle: 'dashed',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 3,
                target: 13,
                graphId: '包含知识点',
                value: 1,
                style: {
                    stroke: '#ffc107',
                    lineStyle: 'dashed',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 5,
                target: 14,
                graphId: '核心知识点',
                value: 2,
                style: {
                    stroke: '#ff5722',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 5,
                target: 15,
                graphId: '核心知识点',
                value: 2,
                style: {
                    stroke: '#ff5722',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 6,
                target: 16,
                graphId: '高级知识点',
                value: 2,
                style: {
                    stroke: '#e91e63',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            },
            {
                source: 6,
                target: 17,
                graphId: '高级知识点',
                value: 2,
                style: {
                    stroke: '#e91e63',
                    lineStyle: 'solid',
                    lineType: 'sCurve',
                    cornerRadius: 10
                },
                offset: { x: 0, y: 0 }
            }
        ]);

        // 状态管理
        const isEditMode = ref(false);
        const isAddingLink = ref(false);
        const isDragging = ref(false);
        const isDraggingControlPoint = ref(false);
        const draggedNode = ref(null);
        const draggedNodeElement = ref(null);
        const draggedControlPoint = ref(null);
        const dragOffset = ref({ x: 0, y: 0 });
        const initialNodePosition = ref({ x: 0, y: 0 });
        const initialMousePosition = ref({ x: 0, y: 0 });
        const sourceNodes = ref([]);
        const targetNode = ref(null);
        const selectedNode = ref(null);
        const selectedLink = ref(null);
        const showEditModal = ref(false);
        const showLinkModal = ref(false);
        const talentTree = ref(null);
        const linksLayer = ref(null);
        let animationFrameId = null;

        // 新增：用于跟踪高亮节点的响应式变量（支持多选）
        const highlightedNodes = ref(new Set());

        // 新增：用于跟踪与选中节点相关的节点和连线
        const relatedNodes = ref(new Set());
        const relatedLinks = ref(new Set());

        // 新增：用于跟踪特殊高亮的关系线
        const specialHighlightedLink = ref(null); // 存储特殊高亮的链接
        const specialHighlightedLinkId = ref(null); // 存储特殊高亮的链接ID
        // 键盘状态监控
        const isCtrlPressed = ref(false);
        const isShiftPressed = ref(false);
        const isAltPressed = ref(false);

        // 批量添加节点功能相关数据
        const batchNodes = ref([]); // 临时存储待添加的节点

        const editingNode = reactive({
            id: null,
            name: '',
            icon: '',
            description: '',
            position: { x: 0, y: 0 },
            active: true,
            shape: 'circle',
            size: 80,
            borderColor: '#000000',
            backgroundColor: '#ffffff',
            transparentBg: false,
            properties: {},
            style: {
                borderStyle: 'solid',
                borderWidth: 2,
                opacity: 1
            }
        });

        const editingLink = reactive({
            source: null,
            target: null,
            graphId: futureEngineGraphId.value,
            value: 3,
            style: {
                stroke: '#000000',
                lineStyle: 'solid',
                lineType: 'straight',
                cornerRadius: 10
            },
            offset: { x: 0, y: 0 }
        });

        const propertiesJson = ref('{}');

        // 触摸双击检测相关的响应式数据
        const lastTouchTime = ref(0);
        const lastTouchTarget = ref(null);
        const lastNodeTouchTime = ref(0);
        const lastNodeTouchTarget = ref(null);
        const lastLinkTouchTime = ref(0);
        const lastLinkTouchTarget = ref(null);

        // 画布拖拽相关状态
        const isPanning = ref(false);
        const isDraggingCanvas = ref(false);
        const hadSelectedNodes = ref(false);
        const panStart = ref({ x: 0, y: 0 });
        const panOffset = ref({ x: 0, y: 0 });
        const currentPanOffset = ref({ x: 0, y: 0 });
        const canvasSize = ref({ width: 2000, height: 2000 });

        // 框选相关状态
        const isSelecting = ref(false);
        const selectionStart = ref({ x: 0, y: 0 });
        const selectionEnd = ref({ x: 0, y: 0 });

        // 初始化画布偏移量
        onMounted(() => {
            // 确保currentPanOffset有初始值
            if (!currentPanOffset.value) {
                currentPanOffset.value = { x: 0, y: 0 };
            }
        });

        // 获取节点位置
        const getNodePosition = (nodeId) => {
            const node = talentNodes.value.find(n => n.id === nodeId);
            if (node && node.desc?.position) {
                // 返回节点中心位置
                const size = node.desc.size || 80;
                // 确保currentPanOffset已定义
                const panOffset = currentPanOffset.value || { x: 0, y: 0 };
                return {
                    x: node.desc.position.x + size / 2 + panOffset.x,
                    y: node.desc.position.y + size / 2 + panOffset.y
                };
            }
            return { x: 0, y: 0 };
        };
        // 添加统一的坐标获取函数，用于处理鼠标和触摸事件
        const getEventCoordinates = (event) => {
            if (event.touches && event.touches.length > 0) {
                // 触摸事件
                return {
                    clientX: event.touches[0].clientX,
                    clientY: event.touches[0].clientY
                };
            } else {
                // 鼠标事件
                return {
                    clientX: event.clientX,
                    clientY: event.clientY
                };
            }
        };

        // 画布鼠标按下事件
        const handleCanvasMouseDown = (event) => {
            // 检查是否点击在节点上
            const isClickOnNode = event.target.closest('.talent-node');

            // 检查是否按住了Ctrl键或Shift键，如果是则开始框选
            if (isEditMode.value && (isCtrlPressed.value || isShiftPressed.value) && !isClickOnNode) {
                isSelecting.value = true;
                isPanning.value = false;

                // 使用统一的坐标获取函数处理鼠标和触摸事件
                const coords = getEventCoordinates(event);

                // 获取点击位置相对于画布的坐标（考虑缩放因子和平移偏移）
                const rect = talentTree.value.getBoundingClientRect();
                const x = (coords.clientX - rect.left - currentPanOffset.value.x) / scale.value;
                const y = (coords.clientY - rect.top - currentPanOffset.value.y) / scale.value;

                selectionStart.value = { x, y };
                selectionEnd.value = { x, y };

                event.preventDefault();
                return;
            }

            // 允许拖动画布，除非在框选模式下
            if (!(isEditMode.value && (isCtrlPressed.value || isShiftPressed.value))) {
                // 设置一个标志，表示可能开始拖拽
                isPanning.value = true;
                // 记录是否有选中的节点
                hadSelectedNodes.value = highlightedNodes.value.size > 0;
                // 使用统一的坐标获取函数处理鼠标和触摸事件
                const coords = getEventCoordinates(event);
                panStart.value = { x: coords.clientX, y: coords.clientY };
                panOffset.value = { ...currentPanOffset.value };

                // 添加拖动优化类
                const talentTreeElement = talentTree.value;
                if (talentTreeElement) {
                    talentTreeElement.classList.add('is-panning');
                }

                event.preventDefault();
            }
        };

        // 画布鼠标移动事件
        const handleCanvasMouseMove = (event) => {
            if (isSelecting.value) {
                // 使用统一的坐标获取函数处理鼠标和触摸事件
                const coords = getEventCoordinates(event);

                // 获取当前位置相对于画布的坐标（考虑缩放因子和平移偏移）
                const rect = talentTree.value.getBoundingClientRect();
                const x = (coords.clientX - rect.left - currentPanOffset.value.x) / scale.value;
                const y = (coords.clientY - rect.top - currentPanOffset.value.y) / scale.value;

                selectionEnd.value = { x, y };

                // 在框选过程中输出当前框选范围（可选，用于调试）
                // console.log('框选范围更新:', { startX: selectionStart.value.x, startY: selectionStart.value.y, endX: x, endY: y });

                event.preventDefault();
                return;
            }

            if (isPanning.value) {
                // 使用统一的坐标获取函数处理鼠标和触摸事件
                const coords = getEventCoordinates(event);
                const deltaX = (coords.clientX - panStart.value.x) / scale.value;
                const deltaY = (coords.clientY - panStart.value.y) / scale.value;
                currentPanOffset.value = {
                    x: panOffset.value.x + deltaX,
                    y: panOffset.value.y + deltaY
                };

                // 如果移动距离超过阈值，标记为正在拖拽
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
                if (distance > 5 && !isDraggingCanvas.value) {
                    isDraggingCanvas.value = true;
                }

                // 触发Vue的响应式更新，重新计算显示的节点
                debouncedForceUpdateDisplayedNodes();

                event.preventDefault();
            }
        };

        // 动态调整画布大小（只调整画布大小而不移动节点）
        const adjustCanvasSize = () => {
            // 计算所有节点的边界
            if (talentNodes.value.length === 0) return;

            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

            talentNodes.value.forEach(node => {
                if (node.desc?.position) {
                    const size = node.desc.size || 80;
                    minX = Math.min(minX, node.desc.position.x);
                    minY = Math.min(minY, node.desc.position.y);
                    maxX = Math.max(maxX, node.desc.position.x + size);
                    maxY = Math.max(maxY, node.desc.position.y + size);
                }
            });

            // 添加边距
            const padding = 200;
            minX -= padding;
            minY -= padding;
            maxX += padding;
            maxY += padding;

            // 更新画布大小（基于节点的实际边界，但不移动节点）
            canvasSize.value = {
                width: Math.max(2000, maxX, Math.abs(minX) + maxX),
                height: Math.max(2000, maxY, Math.abs(minY) + maxY)
            };
        };

        // 画布鼠标释放事件
        const handleCanvasMouseUp = () => {
            if (isSelecting.value) {
                // 框选结束，选择框内的节点
                selectNodesInSelection();
                isSelecting.value = false;
                return;
            }

            if (isPanning.value) {
                // 如果没有真正开始拖拽（是单纯点击），则清除节点选择
                if (!isDraggingCanvas.value) {
                    // 只有在没有按住Ctrl或Shift键时才清除选择
                    if (!isCtrlPressed.value && !isShiftPressed.value) {
                        highlightedNodes.value.clear();
                        selectedNode.value = null; // 清除选中节点
                        // 同时清空相关的节点和连线高亮状态
                        relatedNodes.value.clear();
                        relatedLinks.value.clear();
                        // 同时清除特殊高亮状态
                        specialHighlightedLink.value = null;
                        specialHighlightedLinkId.value = null;

                        // 清除源节点选择（如果在添加连接模式下）
                        if (isAddingLink.value) {
                            sourceNodes.value = [];
                            targetNode.value = null;
                            console.log('清除连接模式下的源节点和目标节点选择');
                        }
                        // 输出节点选择被清除的信息
                        console.log('清除节点选择');
                    }
                }

                isPanning.value = false;
                isDraggingCanvas.value = false;
                hadSelectedNodes.value = false;
                panOffset.value = { ...currentPanOffset.value };

                // 移除拖动优化类
                const talentTreeElement = talentTree.value;
                if (talentTreeElement) {
                    talentTreeElement.classList.remove('is-panning');
                }
            }
        };

        // 选择框内的节点
        const selectNodesInSelection = () => {
            // 获取选框的边界
            const startX = Math.min(selectionStart.value.x, selectionEnd.value.x);
            const startY = Math.min(selectionStart.value.y, selectionEnd.value.y);
            const endX = Math.max(selectionStart.value.x, selectionEnd.value.x);
            const endY = Math.max(selectionStart.value.y, selectionEnd.value.y);

            // 遍历所有节点，检查是否在选框内
            const selectedNodes = [];
            talentNodes.value.forEach(node => {
                const nodeX = node.desc.position.x;
                const nodeY = node.desc.position.y;
                const nodeSize = node.desc.size || 80;

                // 检查节点中心点是否在选框内
                const nodeCenterX = nodeX + nodeSize / 2;
                const nodeCenterY = nodeY + nodeSize / 2;

                if (nodeCenterX >= startX && nodeCenterX <= endX &&
                    nodeCenterY >= startY && nodeCenterY <= endY) {

                    if (isAddingLink.value) {
                        // 如果在添加连接模式下，将框选的节点中源节点中不存在的节点添加到源节点中
                        const sourceNodeIds = sourceNodes.value.map(n => n.id);
                        if (!sourceNodeIds.includes(node.id)) {
                            // 检查节点是否已经在源节点数组中
                            const nodeInTalentNodes = talentNodes.value.find(n => n.id === node.id);
                            if (nodeInTalentNodes) {
                                sourceNodes.value.push(nodeInTalentNodes);
                                console.log('框选添加源节点:', node.id, node.name);
                            }
                        }
                    }
                    // 根据按下的键决定是添加还是移除节点
                    if (isShiftPressed.value) {
                        // Shift键按下，从选中列表中移除节点
                        highlightedNodes.value.delete(node.id);
                    } else {
                        // Ctrl键按下或没有按下特殊键，添加节点到选中列表
                        highlightedNodes.value.add(node.id);
                        selectedNodes.push({ id: node.id, name: node.name });
                    }

                }
            });

            // 输出框选的节点信息
            if (selectedNodes.length > 0) {
                console.log('框选节点:', selectedNodes.map(node => node.id));
                console.log('框选节点详情:', selectedNodes);
            }

            // 输出最终选中的节点
            const finalSelection = Array.from(highlightedNodes.value);
            if (finalSelection.length > 0) {
                console.log('当前选中节点ID:', finalSelection);
                const selectedNodeDetails = talentNodes.value.filter(node => highlightedNodes.value.has(node.id));
                console.log('selectNodesInSelection 当前选中节点详情:', selectedNodeDetails.map(node => ({ id: node.id, name: node.name })));
            }
        };

        // 画布双击事件 - 用于添加新节点，同时支持触摸设备
        const handleCanvasDoubleClick = (event) => {
            // 检测是否为触摸设备上的双击模拟
            if (event.type === 'touchend') {
                const now = Date.now();
                const DOUBLE_CLICK_DELAY = 300; // 双击时间间隔阈值

                // 检查是否为同一目标元素且在时间阈值内
                if (lastTouchTarget.value === event.target && (now - lastTouchTime.value) < DOUBLE_CLICK_DELAY) {
                    // 触摸双击事件
                    lastTouchTime.value = 0; // 重置时间
                    lastTouchTarget.value = null; // 重置目标

                    // 阻止默认行为
                    event.preventDefault();
                } else {
                    // 记录第一次触摸的时间和目标
                    lastTouchTime.value = now;
                    lastTouchTarget.value = event.target;
                    return; // 不执行双击操作
                }
            }

            // 只在编辑模式下响应双击事件，并且确保点击的是画布本身而不是节点或控制点
            const isClickOnNode = event.target.closest('.talent-node');
            const isClickOnControlPoint = event.target.closest('.control-point');

            // 只有在点击画布空白处时才添加新节点
            if (isEditMode.value && !isClickOnNode && !isClickOnControlPoint) {
                // 获取点击位置相对于画布的坐标（考虑缩放因子）
                const rect = talentTree.value.getBoundingClientRect();
                // 使用统一的坐标获取函数处理鼠标和触摸事件
                const coords = getEventCoordinates(event);
                const x = (coords.clientX - rect.left - currentPanOffset.value.x) / scale.value;
                const y = (coords.clientY - rect.top - currentPanOffset.value.y) / scale.value;

                // 打开添加节点模态框，并设置默认位置
                showEditModal.value = true;
                editingNode.id = null;
                editingNode.name = '';
                editingNode.icon = '';
                editingNode.description = '';
                editingNode.position = { x: Math.round(x), y: Math.round(y) };
                editingNode.active = false;
                editingNode.shape = defaultNodeStyle.shape;
                editingNode.size = defaultNodeStyle.size;
                editingNode.borderColor = defaultNodeStyle.borderColor;
                editingNode.backgroundColor = defaultNodeStyle.backgroundColor;
                editingNode.transparentBg = false;
                editingNode.properties = {};
                editingNode.style = {
                    borderStyle: defaultNodeStyle.borderStyle,
                    borderWidth: defaultNodeStyle.borderWidth,
                    opacity: defaultNodeStyle.opacity
                };
                propertiesJson.value = '{}';
            }
        };

        // 画布触摸结束事件 - 用于处理触摸设备上的双击
        const handleCanvasTouchEnd = (event) => {
            // 在触摸设备上，我们将touchend事件视为潜在的双击事件
            handleCanvasDoubleClick(event);
        };

        // 获取线条样式
        const getLineStyle = (style) => {
            switch (style) {
                case 'dashed':
                    return '5,5';
                case 'dotted':
                    return '2,5';
                default:
                    return 'none';
            }
        };

        // 获取线条样式文本
        const getLineStyleText = (style) => {
            switch (style) {
                case 'dashed':
                    return '虚线';
                case 'dotted':
                    return '点线';
                default:
                    return '实线';
            }
        };

        // 获取线型文本
        const getLineTypeText = (type) => {
            switch (type) {
                case 'sCurve':
                    return 'S型线';
                default:
                    return '直线';
            }
        };

        // 添加路径计算缓存
        const pathCache = new Map();
        const MAX_CACHE_SIZE = 500;

        // 计算S型路径
        const calculateSCurvePath = (fromPos, toPos, cornerRadius, offset = { x: 0, y: 0 }) => {
            // 检查输入参数是否有效
            if (!fromPos || !toPos || isNaN(fromPos.x) || isNaN(fromPos.y) || isNaN(toPos.x) || isNaN(toPos.y)) {
                return 'M 0 0 L 0 0';
            }

            // 生成缓存键
            const cacheKey = `${fromPos.x},${fromPos.y}-${toPos.x},${toPos.y}-${cornerRadius}-${offset.x},${offset.y}`;

            // 检查缓存
            if (pathCache.has(cacheKey)) {
                return pathCache.get(cacheKey);
            }

            const dx = toPos.x - fromPos.x;
            const dy = toPos.y - fromPos.y;

            // 确保圆角半径不超过路径长度的一半
            const maxRadius = Math.min(Math.abs(dx), Math.abs(dy)) / 2;
            const radius = Math.min(cornerRadius, maxRadius);

            let path;
            if (Math.abs(dx) > Math.abs(dy)) {
                // 水平主导的S型路径
                const midX = (fromPos.x + toPos.x) / 2 + offset.x;

                if (radius <= 0) {
                    // 无圆角
                    path = `M ${fromPos.x} ${fromPos.y} L ${midX} ${fromPos.y} L ${midX} ${toPos.y} L ${toPos.x} ${toPos.y}`;
                } else {
                    // 有圆角
                    const signX = dx > 0 ? 1 : -1;
                    const signY = dy > 0 ? 1 : -1;

                    // 根据方向自适应调整圆角
                    if (signY > 0) {
                        // 从上到下
                        path = `M ${fromPos.x} ${fromPos.y} 
                               L ${midX - signX * radius} ${fromPos.y} 
                               Q ${midX} ${fromPos.y} ${midX} ${fromPos.y + radius} 
                               L ${midX} ${toPos.y - radius} 
                               Q ${midX} ${toPos.y} ${midX + signX * radius} ${toPos.y} 
                               L ${toPos.x} ${toPos.y}`;
                    } else {
                        // 从下到上
                        path = `M ${fromPos.x} ${fromPos.y} 
                               L ${midX - signX * radius} ${fromPos.y} 
                               Q ${midX} ${fromPos.y} ${midX} ${fromPos.y - radius} 
                               L ${midX} ${toPos.y + radius} 
                               Q ${midX} ${toPos.y} ${midX + signX * radius} ${toPos.y} 
                               L ${toPos.x} ${toPos.y}`;
                    }
                }
            } else {
                // 垂直主导的S型路径
                const midY = (fromPos.y + toPos.y) / 2 + offset.y;

                if (radius <= 0) {
                    // 无圆角
                    path = `M ${fromPos.x} ${fromPos.y} L ${fromPos.x} ${midY} L ${toPos.x} ${midY} L ${toPos.x} ${toPos.y}`;
                } else {
                    // 有圆角
                    const signX = dx > 0 ? 1 : -1;
                    const signY = dy > 0 ? 1 : -1;

                    // 根据方向自适应调整圆角
                    if (signX > 0) {
                        // 从左到右
                        path = `M ${fromPos.x} ${fromPos.y} 
                               L ${fromPos.x} ${midY - signY * radius} 
                               Q ${fromPos.x} ${midY} ${fromPos.x + radius} ${midY} 
                               L ${toPos.x - radius} ${midY} 
                               Q ${toPos.x} ${midY} ${toPos.x} ${midY + signY * radius} 
                               L ${toPos.x} ${toPos.y}`;
                    } else {
                        // 从右到左
                        path = `M ${fromPos.x} ${fromPos.y} 
                               L ${fromPos.x} ${midY - signY * radius} 
                               Q ${fromPos.x} ${midY} ${fromPos.x - radius} ${midY} 
                               L ${toPos.x + radius} ${midY} 
                               Q ${toPos.x} ${midY} ${toPos.x} ${midY + signY * radius} 
                               L ${toPos.x} ${toPos.y}`;
                    }
                }
            }

            // 维护缓存大小
            if (pathCache.size >= MAX_CACHE_SIZE) {
                const firstKey = pathCache.keys().next().value;
                pathCache.delete(firstKey);
            }

            // 缓存结果
            pathCache.set(cacheKey, path);

            return path;
        };

        // 获取控制点位置
        const getControlPoints = (link) => {
            if (!link || link.style.lineType !== 'sCurve') return [];

            const fromPos = getNodePosition(link.source);
            const toPos = getNodePosition(link.target);

            // 检查节点位置是否有效
            if (!fromPos || !toPos || isNaN(fromPos.x) || isNaN(fromPos.y) || isNaN(toPos.x) || isNaN(toPos.y)) {
                return [];
            }

            const cornerRadius = link.style.cornerRadius || 10;
            const offset = link.offset || { x: 0, y: 0 };

            const dx = toPos.x - fromPos.x;
            const dy = toPos.y - fromPos.y;

            // 确保圆角半径不超过路径长度的一半
            const maxRadius = Math.min(Math.abs(dx), Math.abs(dy)) / 2;
            const radius = Math.min(cornerRadius, maxRadius);

            if (Math.abs(dx) > Math.abs(dy)) {
                // 水平主导
                const midX = (fromPos.x + toPos.x) / 2 + offset.x;
                const signX = dx > 0 ? 1 : -1;
                const signY = dy > 0 ? 1 : -1;

                // 添加更多控制点以支持中间部分拖拽
                return [
                    { x: midX - signX * radius, y: fromPos.y, type: 'horizontal1' },
                    { x: midX, y: fromPos.y + radius * signY, type: 'vertical1' },
                    { x: midX, y: toPos.y - radius * signY, type: 'vertical2' },
                    { x: midX + signX * radius, y: toPos.y, type: 'horizontal2' },
                    // 添加中间控制点
                    { x: midX, y: (fromPos.y + toPos.y) / 2 + offset.y, type: 'middle' }
                ];
            } else {
                // 垂直主导
                const midY = (fromPos.y + toPos.y) / 2 + offset.y;
                const signX = dx > 0 ? 1 : -1;
                const signY = dy > 0 ? 1 : -1;

                // 添加更多控制点以支持中间部分拖拽
                return [
                    { x: fromPos.x, y: midY - signY * radius, type: 'vertical1' },
                    { x: fromPos.x + radius * signX, y: midY, type: 'horizontal1' },
                    { x: toPos.x - radius * signX, y: midY, type: 'horizontal2' },
                    { x: toPos.x, y: midY + signY * radius, type: 'vertical2' },
                    // 添加中间控制点
                    { x: (fromPos.x + toPos.x) / 2 + offset.x, y: midY, type: 'middle' }
                ];
            }
        };

        // 计算链接路径
        const getLinkPath = (link) => {
            const fromPos = getNodePosition(link.source);
            const toPos = getNodePosition(link.target);

            // 检查节点位置是否有效
            if (!fromPos || !toPos || isNaN(fromPos.x) || isNaN(fromPos.y) || isNaN(toPos.x) || isNaN(toPos.y)) {
                // 返回一个默认的有效路径
                return 'M 0 0 L 0 0';
            }

            if (link.style.lineType === 'sCurve') {
                // 计算S型路径
                const offset = link.offset || { x: 0, y: 0 };
                return calculateSCurvePath(fromPos, toPos, link.style.cornerRadius || 10, offset);
            } else {
                // 默认直线路径
                return `M ${fromPos.x} ${fromPos.y} L ${toPos.x} ${toPos.y}`;
            }
        };

        // // 更新控制点位置
        // const updateControlPointPosition = (link, pointIndex, newX, newY) => {
        //     // 获取所有控制点
        //     const controlPoints = getControlPoints(link);

        //     // 获取当前控制点
        //     const point = controlPoints[pointIndex];
        //     if (!point) return;

        //     // 根据节点位置计算基础路径参数
        //     const fromPos = getNodePosition(link.source);
        //     const toPos = getNodePosition(link.target);
        //     const dx = toPos.x - fromPos.x;
        //     const dy = toPos.y - fromPos.y;

        //     // 初始化链接的偏移量
        //     if (!link.offset) {
        //         link.offset = { x: 0, y: 0 };
        //     }

        //     // 创建偏移量的副本以避免直接修改响应式对象
        //     const newOffset = { ...link.offset };

        //     if (point.type === 'middle') {
        //         // 中间控制点
        //         if (Math.abs(dx) > Math.abs(dy)) {
        //             // 水平主导，调整垂直偏移
        //             const midY = (fromPos.y + toPos.y) / 2;
        //             newOffset.y = newY - midY;
        //         } else {
        //             // 垂直主导，调整水平偏移
        //             const midX = (fromPos.x + toPos.x) / 2;
        //             newOffset.x = newX - midX;
        //         }
        //     } else if (point.type === 'vertical1' || point.type === 'vertical2') {
        //         // 垂直控制点
        //         if (Math.abs(dx) > Math.abs(dy)) {
        //             // 水平主导，调整水平偏移
        //             // 当拖动垂直控制点时，如果是水平移动，则更新水平偏移
        //             newOffset.x += newX - point.x;
        //         } else {
        //             // 垂直主导，调整垂直偏移
        //             // 当拖动垂直控制点时，如果是垂直移动，则更新垂直偏移
        //             newOffset.y += newY - point.y;
        //         }
        //     } else if (point.type === 'horizontal1' || point.type === 'horizontal2') {
        //         // 水平控制点
        //         if (Math.abs(dx) > Math.abs(dy)) {
        //             // 水平主导，调整垂直偏移
        //             // 当拖动水平控制点时，如果是垂直移动，则更新垂直偏移
        //             newOffset.y += newY - point.y;
        //         } else {
        //             // 垂直主导，调整水平偏移
        //             // 当拖动水平控制点时，如果是水平移动，则更新水平偏移
        //             newOffset.x += newX - point.x;
        //         }
        //     }

        //     // 更新链接的偏移量
        //     link.offset = newOffset;
        // };

        // 更新控制点及其相关控制点
        // const updateControlPointWithRelated = (link, pointIndex, newX, newY) => {
        //     // 首先更新主控制点
        //     updateControlPointPosition(link, pointIndex, newX, newY);

        //     // 然后协调更新相关控制点
        //     updateRelatedControlPoints(link, pointIndex, newX, newY);
        // };

        // 获取控制点的关联控制点索引
        // const getRelatedControlPointIndices = (pointIndex, controlPoints) => {
        //     // 根据控制点类型返回相关的控制点索引
        //     const point = controlPoints[pointIndex];
        //     if (!point) return [];

        //     // 对于S型线，控制点通常是成对出现的
        //     // 这里可以根据实际的控制点布局来确定关联关系
        //     switch (point.type) {
        //         case 'vertical1':
        //             return ['horizontal1'];
        //         case 'vertical2':
        //             return ['horizontal2'];
        //         case 'horizontal1':
        //             return ['vertical1'];
        //         case 'horizontal2':
        //             return ['vertical2'];
        //         default:
        //             return [];
        //     }
        // };

        // // 协调更新所有相关控制点
        // const coordinateControlPoints = (link, pointIndex, newX, newY) => {
        //     // 获取所有控制点
        //     const controlPoints = getControlPoints(link);

        //     // 获取当前控制点
        //     const point = controlPoints[pointIndex];
        //     if (!point) return;

        //     // 获取节点位置
        //     const fromPos = getNodePosition(link.source);
        //     const toPos = getNodePosition(link.target);
        //     const dx = toPos.x - fromPos.x;
        //     const dy = toPos.y - fromPos.y;

        //     // 更新主控制点
        //     updateControlPointPosition(link, pointIndex, newX, newY);

        //     // 根据控制点类型协调更新相关控制点
        //     if (point.type === 'vertical1' || point.type === 'vertical2') {
        //         // 当拖动垂直控制点时，如果是水平主导的路径，需要更新相关的水平控制点
        //         if (Math.abs(dx) > Math.abs(dy)) {
        //             // 水平主导路径，垂直控制点的水平移动会影响水平控制点
        //             // 更新相关的水平控制点，保持路径的一致性
        //         }
        //     } else if (point.type === 'horizontal1' || point.type === 'horizontal2') {
        //         // 当拖动水平控制点时，如果是垂直主导的路径，需要更新相关的垂直控制点
        //         if (Math.abs(dy) > Math.abs(dx)) {
        //             // 垂直主导路径，水平控制点的垂直移动会影响垂直控制点
        //             // 更新相关的垂直控制点，保持路径的一致性
        //         }
        //     } else if (point.type === 'middle') {
        //         // 当拖动中间控制点时，更新所有相关控制点
        //         // 中间控制点的移动会影响整个路径的偏移
        //     }
        // };

        // 更新控制点并保持路径一致性
        const updateControlPointConsistently = (link, pointIndex, newX, newY) => {
            // 获取所有控制点
            const controlPoints = getControlPoints(link);

            // 获取当前控制点
            const point = controlPoints[pointIndex];
            if (!point) return;

            // 获取节点位置
            const fromPos = getNodePosition(link.source);
            const toPos = getNodePosition(link.target);
            const dx = toPos.x - fromPos.x;
            const dy = toPos.y - fromPos.y;

            // 确保每个链接都有独立的offset对象，避免引用共享
            // 创建新的offset对象，避免修改原始引用
            const newOffset = link.offset ? { ...link.offset } : { x: 0, y: 0 };

            // 根据控制点类型和路径方向决定如何更新
            if (point.type === 'vertical1' || point.type === 'vertical2') {
                // 垂直控制点
                if (Math.abs(dx) > Math.abs(dy)) {
                    // 水平主导路径
                    // 垂直控制点的水平移动应该影响整个路径的水平偏移
                    newOffset.x += newX - point.x;
                } else {
                    // 垂直主导路径
                    // 垂直控制点的垂直移动应该影响整个路径的垂直偏移
                    newOffset.y += newY - point.y;
                }
            } else if (point.type === 'horizontal1' || point.type === 'horizontal2') {
                // 水平控制点
                if (Math.abs(dx) > Math.abs(dy)) {
                    // 水平主导路径
                    // 水平控制点的垂直移动应该影响整个路径的垂直偏移
                    newOffset.y += newY - point.y;
                } else {
                    // 垂直主导路径
                    // 水平控制点的水平移动应该影响整个路径的水平偏移
                    newOffset.x += newX - point.x;
                }
            } else if (point.type === 'middle') {
                // 中间控制点
                if (Math.abs(dx) > Math.abs(dy)) {
                    // 水平主导路径，调整垂直偏移
                    const midY = (fromPos.y + toPos.y) / 2;
                    newOffset.y = newY - midY;
                } else {
                    // 垂直主导路径，调整水平偏移
                    const midX = (fromPos.x + toPos.x) / 2;
                    newOffset.x = newX - midX;
                }
            }

            // 更新链接的offset，确保Vue能正确检测到变化
            link.offset = newOffset;
        };

        // 强制更新SVG路径
        const forceUpdatePaths = () => {
            // 触发Vue的响应式更新
            nextTick(() => {
                // 这里可以添加任何需要在下一帧执行的代码
                // 例如重新计算某些路径或触发重绘
            });
        };

        // 性能监控对象
        const performanceMonitor = {
            lastRenderTime: 0,
            renderCount: 0,

            startRender() {
                this.lastRenderTime = performance.now();
                this.renderCount++;
            },

            endRender() {
                const endTime = performance.now();
                const duration = endTime - this.lastRenderTime;

                if (duration > 16) { // 超过一帧的时间(16ms)
                    console.warn(`渲染耗时过长: ${duration.toFixed(2)}ms`);
                }

                if (this.renderCount % 100 === 0) {
                    console.log(`平均每100次渲染耗时: ${(duration / 100).toFixed(2)}ms`);
                }
            }
        };

        // 防抖函数
        const debounce = (func, delay) => {
            let timeoutId;
            return function (...args) {
                const context = this;
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => func.apply(context, args), delay);
            };
        };

        // 防抖版本的强制更新显示的节点
        // 优化后的防抖函数，降低延迟以提高响应性
        const debouncedForceUpdateDisplayedNodes = debounce(() => {
            // 启动性能监控
            performanceMonitor.startRender();

            // 触发Vue的响应式更新
            nextTick(() => {
                // 重新计算显示的节点
                // 通过触发currentPanOffset的更新来刷新计算属性
                currentPanOffset.value = { ...currentPanOffset.value };

                // 结束性能监控
                performanceMonitor.endRender();
            });
        }, 150); // 150ms防抖延迟

        // 监听节点位置变化并强制更新路径
        watch(() => talentNodes.value.map(n => ({ id: n.id, x: n.desc?.position?.x || 0, y: n.desc?.position?.y || 0 })), () => {
            forceUpdatePaths();
            adjustCanvasSize();
        }, { deep: true });

        // 获取节点的连接关系
        const getNodeLinks = (nodeId) => {
            return links.value.filter(link =>
                link.source === nodeId || link.target === nodeId
            );
        };

        // 获取连接的另一个节点名称
        const getLinkedNodeName = (link, currentNodeId) => {
            const linkedNodeId = link.source === currentNodeId ? link.target : link.source;
            const linkedNode = talentNodes.value.find(n => n.id === linkedNodeId);
            return linkedNode ? linkedNode.name : '未知节点';
        };

        // 检查两个节点是否已经连接
        const isAlreadyLinked = (fromId, toId) => {
            return links.value.some(link =>
                (link.source === fromId && link.target === toId) ||
                (link.source === toId && link.target === fromId)
            );
        };

        // 切换编辑模式
        const toggleEditMode = () => {
            isEditMode.value = !isEditMode.value;
            // 退出编辑模式时重置连接添加状态
            if (!isEditMode.value) {
                isAddingLink.value = false;
                sourceNodes.value = [];
                targetNode.value = null;

                // 清除所有节点选择
                highlightedNodes.value.clear();
                selectedNode.value = null; // 清除选中节点
                // 同时清空相关的节点和连线高亮状态
                relatedNodes.value.clear();
                relatedLinks.value.clear();
                // 同时清除特殊高亮状态
                specialHighlightedLink.value = null;
                specialHighlightedLinkId.value = null;
            }
        };

        // 开始添加连接
        const startAddLink = () => {
            isAddingLink.value = true;

            // 将当前已选中的节点作为源节点
            if (highlightedNodes.value.size > 0) {
                sourceNodes.value = talentNodes.value.filter(node =>
                    highlightedNodes.value.has(node.id)
                );
                console.log('将已选节点作为源节点:', Array.from(highlightedNodes.value));
            } else {
                sourceNodes.value = [];
            }

            targetNode.value = null;
        };

        // 取消添加连接
        const cancelAddLink = () => {
            isAddingLink.value = false;
            clearSourceNodes();
            // 重置编辑连接的值
            editingLink.source = null;
            editingLink.target = null;

            // 清除所有节点选择
            highlightedNodes.value.clear();
            selectedNode.value = null; // 清除选中节点
            // 同时清空相关的节点和连线高亮状态
            relatedNodes.value.clear();
            relatedLinks.value.clear();
            // 同时清除特殊高亮状态
            specialHighlightedLink.value = null;
            specialHighlightedLinkId.value = null;
        };


        // 处理节点点击（用于添加连接），同时支持触摸双击
        const handleNodeClick = (event, node) => {
            console.log("handleNodeClick id:", node.id, " name:", node.name);
            // 检测是否为触摸设备上的双击模拟
            if (event && event.type === 'touchend') {
                const now = Date.now();
                const DOUBLE_CLICK_DELAY = 300; // 双击时间间隔阈值

                // 检查是否为同一目标元素且在时间阈值内
                if (lastNodeTouchTarget.value === event.target && (now - lastNodeTouchTime.value) < DOUBLE_CLICK_DELAY) {
                    // 触摸双击事件
                    lastNodeTouchTime.value = 0; // 重置时间
                    lastNodeTouchTarget.value = null; // 重置目标

                    // 阻止默认行为
                    event.preventDefault();

                    // 调用双击处理函数
                    handleNodeDoubleClick(event, node);
                    return;
                } else {
                    // 记录第一次触摸的时间和目标
                    lastNodeTouchTime.value = now;
                    lastNodeTouchTarget.value = event.target;


                    // 如果当前是添加链接模式
                    if (isAddingLink.value) {
                        console.log("in add link :", node.id, node.name);
                        // 如果点击的节点已经是源节点，将其设为目标节点
                        if (sourceNodes.value.some(n => n.id === node.id)) {
                            targetNode.value = node;
                            // 从源节点数组中移除该节点
                            sourceNodes.value = sourceNodes.value.filter(n => n.id !== node.id);

                            // 检查是否有源节点，如果没有则提示用户
                            if (sourceNodes.value.length === 0) {
                                alert('请先选择至少一个源节点，再选择目标节点！');
                                cancelAddLink();
                                return;
                            }

                            console.log('触摸设置目标节点:', node.id, node.name);
                            console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                            console.log('目标节点ID:', targetNode.value.id);

                            // 打开连接编辑模态框
                            showLinkModal.value = true;
                            // 使用第一个源节点作为参考
                            if (sourceNodes.value.length > 0) {
                                editingLink.source = sourceNodes.value[0].id;
                            }
                            editingLink.target = targetNode.value.id;
                            editingLink.graphId = futureEngineGraphId.value;
                            editingLink.value = 3;
                            editingLink.style = {
                                stroke: defaultLineStyle.stroke,
                                lineStyle: defaultLineStyle.lineStyle,
                                lineType: defaultLineStyle.lineType,
                                cornerRadius: defaultLineStyle.cornerRadius
                            };
                        } else {
                            // 如果点击的节点不是源节点，将其添加到源节点数组
                            if (!sourceNodes.value.some(n => n.id === node.id)) {
                                sourceNodes.value.push(node);
                                console.log('触摸添加源节点:', node.id, node.name);
                                console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                            }
                        }
                    }
                    handleNodeHighlight(node);

                    // 输出当前所有选中节点（用于调试）
                    if (highlightedNodes.value.size > 0) {
                        console.log('触摸双击后当前选中节点ID:', Array.from(highlightedNodes.value));
                        const selectedNodeDetails = talentNodes.value.filter(n => highlightedNodes.value.has(n.id));
                        console.log('触摸双击后当前选中节点详情:', selectedNodeDetails.map(n => ({ id: n.id, name: n.name })));
                    }

                    return; // 不执行双击操作
                }
            }

            // 区分单击和双击事件
            if (event.type === 'click') {
                // 如果当前是添加链接模式
                if (isAddingLink.value) {
                    const nodeIndex = sourceNodes.value.findIndex(n => n.id === node.id);
                    // 按住Ctrl键时，切换当前节点在源节点数组中的状态
                    if (isCtrlPressed.value) {
                        if (nodeIndex !== -1) {
                            // 如果节点已在源节点数组中，移除它
                            sourceNodes.value.splice(nodeIndex, 1);
                            console.log('Ctrl移除源节点:', node.id, node.name);
                        } else {
                            // 如果节点不在源节点数组中，添加它
                            sourceNodes.value.push(node);
                            console.log('Ctrl添加源节点:', node.id, node.name);
                        }
                        console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                    } else if (isShiftPressed.value) {
                        // 按住Shift键时，如果节点在源节点数组中则移除
                        // const nodeIndex = sourceNodes.value.findIndex(n => n.id === node.id);
                        if (nodeIndex !== -1) {
                            sourceNodes.value.splice(nodeIndex, 1);
                            console.log('Shift移除源节点:', node.id, node.name);
                        }
                        console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                    } else {
                        // 普通点击时，如果已经有源节点，则将当前节点设为目标节点
                        if (sourceNodes.value.length > 0 && nodeIndex == -1) {
                            // 将当前节点设为目标节点
                            targetNode.value = node;

                            console.log('设置目标节点:', node.id, node.name);
                            console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                            console.log('目标节点ID:', targetNode.value.id);

                            // 打开连接编辑模态框
                            showLinkModal.value = true;
                            // 使用第一个源节点作为参考
                            if (sourceNodes.value.length > 0) {
                                editingLink.source = sourceNodes.value[0].id;
                            }
                            editingLink.target = targetNode.value.id;
                            editingLink.graphId = futureEngineGraphId.value;
                            editingLink.value = 3;
                            editingLink.style = {
                                stroke: defaultLineStyle.stroke,
                                lineStyle: defaultLineStyle.lineStyle,
                                lineType: defaultLineStyle.lineType,
                                cornerRadius: defaultLineStyle.cornerRadius
                            };
                        } else {
                            // 如果还没有源节点，将当前节点添加为源节点
                            if (!sourceNodes.value.some(n => n.id === node.id)) {
                                sourceNodes.value.push(node);
                                console.log('添加源节点:', node.id, node.name);
                            }
                            console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                        }
                    }
                }
                handleNodeHighlight(node);

                // 输出当前所有选中节点（用于调试）
                if (highlightedNodes.value.size > 0) {
                    console.log('当前选中节点ID:', Array.from(highlightedNodes.value));
                    const selectedNodeDetails = talentNodes.value.filter(node => highlightedNodes.value.has(node.id));
                    console.log('handleNodeClick 当前选中节点详情:', selectedNodeDetails.map(n => ({ id: n.id, name: n.name })));
                }
            } else if (event.type === 'dblclick') {
                // 双击事件 - 处理连接和详情弹窗
                handleNodeDoubleClick(event, node);
            }
        };

        // 清空源节点选择
        const clearSourceNodes = () => {
            sourceNodes.value = [];
            targetNode.value = null;
        };

        // 调试函数：输出当前所有节点选择状态
        const debugNodeSelection = () => {
            console.log('=== 节点选择状态调试 ===');
            console.log('当前选中节点ID:', Array.from(highlightedNodes.value));
            console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
            console.log('目标节点ID:', targetNode.value ? targetNode.value.id : null);
            console.log('是否在添加链接模式:', isAddingLink.value);

            if (highlightedNodes.value.size > 0) {
                const selectedNodeDetails = talentNodes.value.filter(node => highlightedNodes.value.has(node.id));
                console.log('debugNodeSelection 当前选中节点详情:', selectedNodeDetails.map(n => ({ id: n.id, name: n.name })));
            }

            if (sourceNodes.value.length > 0) {
                console.log('当前源节点详情:', sourceNodes.value.map(n => ({ id: n.id, name: n.name })));
            }

            if (targetNode.value) {
                console.log('目标节点详情:', { id: targetNode.value.id, name: targetNode.value.name });
            }
            console.log('=======================');
        };

        // 处理画布点击，用于在添加链接模式下取消选择
        const handleCanvasClick = (event) => {
            // 如果点击的是空白区域且当前是添加链接模式
            if (isAddingLink.value && event.target.classList.contains('talent-tree')) {
                // 检查是否点击在节点上
                if (!event.target.classList.contains('talent-node') &&
                    !event.target.closest('.talent-node')) {
                    // 如果点击的是空白区域，可以清除选择
                    // 目前我们不自动清除，让用户明确点击取消按钮
                    console.log('Clicked on canvas blank area in add link mode');
                }
            }
        };



        // 处理节点高亮
        const handleNodeHighlight = (node) => {
            console.log("handleNodeHighlight:", node.id, " name:", node.name)
            // 如果正在拖拽，则不处理点击事件
            if (isDragging.value) return;

            // // 如果当前是添加链接模式，处理源节点选择
            // if (isAddingLink.value) {
            //     if (isCtrlPressed.value) {
            //         // 按住Ctrl键时，切换当前节点在源节点数组中的状态
            //         const nodeIndex = sourceNodes.value.findIndex(n => n.id === node.id);
            //         if (nodeIndex !== -1) {
            //             // 如果节点已在源节点数组中，移除它
            //             sourceNodes.value.splice(nodeIndex, 1);
            //             console.log('移除源节点:', node.id, node.name);
            //         } else {
            //             // 如果节点不在源节点数组中，添加它
            //             sourceNodes.value.push(node);
            //             console.log('添加源节点:', node.id, node.name);
            //         }
            //         console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
            //     } else if (isShiftPressed.value) {
            //         // 按住Shift键时，如果节点在源节点数组中则移除
            //         const nodeIndex = sourceNodes.value.findIndex(n => n.id === node.id);
            //         if (nodeIndex !== -1) {
            //             sourceNodes.value.splice(nodeIndex, 1);
            //             console.log('Shift移除源节点:', node.id, node.name);
            //         }
            //         console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
            //     } else {
            //         // 普通点击时，如果已经有源节点，则将当前节点设为目标节点
            //         if (sourceNodes.value.length > 0) {
            //             // 将当前节点设为目标节点
            //             targetNode.value = node;

            //             console.log('设置目标节点:', node.id, node.name);
            //             console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
            //             console.log('目标节点ID:', targetNode.value.id);

            //             // 打开连接编辑模态框
            //             showLinkModal.value = true;
            //             // 使用第一个源节点作为参考
            //             if (sourceNodes.value.length > 0) {
            //                 editingLink.source = sourceNodes.value[0].id;
            //             }
            //             editingLink.target = targetNode.value.id;
            //             editingLink.graphId = futureEngineGraphId.value;
            //             editingLink.value = 3;
            //             editingLink.style = {
            //                 stroke: defaultLineStyle.stroke,
            //                 lineStyle: defaultLineStyle.lineStyle,
            //                 lineType: defaultLineStyle.lineType,
            //                 cornerRadius: defaultLineStyle.cornerRadius
            //             };
            //         } else {
            //             // 如果还没有源节点，将当前节点添加为源节点
            //             if (!sourceNodes.value.some(n => n.id === node.id)) {
            //                 sourceNodes.value.push(node);
            //                 console.log('添加源节点:', node.id, node.name);
            //             }
            //             console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
            //         }
            //     }
            //     return; // 在添加链接模式下，不执行常规高亮逻辑
            // }

            // 清空之前的相关节点和连线
            relatedNodes.value.clear();
            relatedLinks.value.clear();

            // 同时清除特殊高亮状态
            specialHighlightedLink.value = null;
            specialHighlightedLinkId.value = null;

            // 处理节点高亮逻辑（支持多选）
            if (isCtrlPressed.value) {
                // 按住Ctrl键时，切换当前节点的选中状态
                if (highlightedNodes.value.has(node.id)) {
                    highlightedNodes.value.delete(node.id);
                    console.log('取消选中节点:', node.id, node.name);
                } else {
                    highlightedNodes.value.add(node.id);
                    console.log('选中节点:', node.id, node.name);
                }
                // 查找与当前节点相关的节点和连线
                findRelatedNodesAndLinks(node.id);
                console.log('当前选中节点ID:', Array.from(highlightedNodes.value));
            } else if (isShiftPressed.value) {
                // 按住Shift键时，如果节点已选中则取消选中
                if (highlightedNodes.value.has(node.id)) {
                    highlightedNodes.value.delete(node.id);
                    console.log('Shift取消选中节点:', node.id, node.name);
                } else {
                    highlightedNodes.value.add(node.id);
                    console.log('Shift选中节点:', node.id, node.name);
                }
                // 查找与当前节点相关的节点和连线
                findRelatedNodesAndLinks(node.id);
                console.log('当前选中节点ID:', Array.from(highlightedNodes.value));
            } else {
                // 普通点击时，清空之前选中并选中当前节点
                console.log('清空之前选中，选中节点:', node.id, node.name);
                highlightedNodes.value.clear();
                highlightedNodes.value.add(node.id);

                // 查找与当前节点相关的节点和连线
                findRelatedNodesAndLinks(node.id);
                console.log('当前选中节点ID:', Array.from(highlightedNodes.value));
            }
        };

        // 查找与指定节点相关的节点和连线
        const findRelatedNodesAndLinks = (nodeId) => {
            // 清空之前的结果
            relatedNodes.value.clear();
            relatedLinks.value.clear();

            // 同时重置特殊高亮状态
            specialHighlightedLink.value = null;
            specialHighlightedLinkId.value = null;

            // 查找与该节点相连的所有连线
            const connectedLinks = links.value.filter(link =>
                link.source === nodeId || link.target === nodeId
            );

            // 将相关连线添加到relatedLinks集合中
            connectedLinks.forEach(link => {
                relatedLinks.value.add(`${link.source}-${link.target}`);

                // 将相连的节点添加到relatedNodes集合中（除了自身）
                if (link.source === nodeId && link.target !== nodeId) {
                    relatedNodes.value.add(link.target);
                } else if (link.target === nodeId && link.source !== nodeId) {
                    relatedNodes.value.add(link.source);
                }
            });
        };
        // 处理节点双击
        const handleNodeDoubleClick = (event, node) => {
            // 如果正在拖拽，则不处理点击事件
            if (isDragging.value) return;

            if (isAddingLink.value) {
                // 在双击事件中，如果当前是添加链接模式，也执行相同逻辑
                // 如果点击的节点已经是源节点，将其设为目标节点
                if (sourceNodes?.value?.some(n => n.id === node.id)) {
                    targetNode.value = node;
                    // 从源节点数组中移除该节点
                    sourceNodes.value = sourceNodes?.value?.filter(n => n.id !== node.id) || [];

                    // 检查是否有源节点，如果没有则提示用户
                    if (sourceNodes?.value?.length === 0) {
                        alert('请先选择至少一个源节点，再选择目标节点！');
                        cancelAddLink();
                        return;
                    }

                    console.log('双击设置目标节点:', node.id, node.name);
                    console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                    console.log('目标节点ID:', targetNode.value.id);

                    // 打开连接编辑模态框
                    showLinkModal.value = true;
                    // 设置编辑连接的源和目标信息
                    // 使用第一个源节点作为参考
                    if (sourceNodes.value.length > 0) {
                        editingLink.source = sourceNodes.value[0].id;
                    }
                    editingLink.target = targetNode.value.id;
                    editingLink.graphId = futureEngineGraphId.value;
                    editingLink.value = 3;
                    editingLink.style = {
                        stroke: defaultLineStyle.stroke,
                        lineStyle: defaultLineStyle.lineStyle,
                        lineType: defaultLineStyle.lineType,
                        cornerRadius: defaultLineStyle.cornerRadius
                    };
                } else {
                    // 如果点击的节点不是源节点，将其添加到源节点数组
                    if (!sourceNodes?.value?.some(n => n.id === node.id)) {
                        sourceNodes.value.push(node);
                        console.log('双击添加源节点:', node.id, node.name);
                        console.log('当前源节点ID:', sourceNodes.value.map(n => n.id));
                    }
                }
            } else {
                // 正常选择节点（仅在双击时触发）
                selectNode(node);
            }
        };

        // 自动排列节点
        const autoLayout = () => {
            // 根据现有节点坐标，以50为单位进行取整计算，实现整齐排列
            const nodes = talentNodes.value;
            const gridSize = 50; // 网格大小

            nodes.forEach((node) => {
                if (node.desc?.position) {
                    // 获取当前坐标
                    const currentX = node.desc.position.x;
                    const currentY = node.desc.position.y;

                    // 以50为单位进行取整计算
                    const roundedX = Math.round(currentX / gridSize) * gridSize;
                    const roundedY = Math.round(currentY / gridSize) * gridSize;

                    // 更新节点位置
                    node.desc.position.x = roundedX;
                    node.desc.position.y = roundedY;
                }
            });
        };

        // 重置节点布局，将节点向窗口中心聚集
        const resetNodes = () => {
            if (!talentNodes.value || talentNodes.value.length === 0) return;

            // 使用第一个节点作为中心节点（不需要计算哪个节点在中间）
            const centerNode = talentNodes.value[0];
            if (!centerNode.desc?.position) return;

            // 获取当前画布中心位置
            const centerPos = getCanvasCenterPosition();

            // 计算偏移量，使第一个节点移动到画布中心
            const offsetX = centerPos.x - centerNode.desc.position.x;
            const offsetY = centerPos.y - centerNode.desc.position.y;

            // 先将所有节点按偏移量移动，使第一个节点在画布中心
            talentNodes.value.forEach(node => {
                if (node.desc?.position) {
                    node.desc.position.x += offsetX;
                    node.desc.position.y += offsetY;
                }
            });

            // 定义网格大小（使用可配置的间距）
            const GRID_SIZE = gridSpacing.value;

            // 重新计算第一个节点（现在的中心节点）的网格坐标
            const centerX = talentNodes.value[0].desc.position.x;
            const centerY = talentNodes.value[0].desc.position.y;

            // 计算中心节点所在的网格坐标（对齐到网格中心）
            const centerGridX = Math.round(centerX / GRID_SIZE);
            const centerGridY = Math.round(centerY / GRID_SIZE);

            // 将中心节点精确放在网格中心
            talentNodes.value[0].desc.position.x = centerGridX * GRID_SIZE;
            talentNodes.value[0].desc.position.y = centerGridY * GRID_SIZE;

            // 创建一个集合来追踪已被占用的网格位置
            const occupiedGrids = new Map();
            const gridKey = (x, y) => `${x},${y}`;

            // 标记中心节点占用的网格
            occupiedGrids.set(gridKey(centerGridX, centerGridY), 0);

            // 用于生成网格坐标的函数（螺旋式向外扩展）
            function* generateGridPositions(startX, startY) {
                yield [startX, startY]; // 中心点

                let radius = 1;
                while (true) {
                    // 生成当前半径的环
                    // 上边（y = startY - radius）
                    for (let x = startX - radius; x <= startX + radius; x++) {
                        yield [x, startY - radius];
                    }
                    // 右边（x = startX + radius）
                    for (let y = startY - radius + 1; y <= startY + radius; y++) {
                        yield [startX + radius, y];
                    }
                    // 下边（y = startY + radius）
                    for (let x = startX + radius - 1; x >= startX - radius; x--) {
                        yield [x, startY + radius];
                    }
                    // 左边（x = startX - radius）
                    for (let y = startY + radius - 1; y >= startY - radius; y--) {
                        yield [startX - radius, y];
                    }

                    radius++;
                }
            }

            const gridPositionGenerator = generateGridPositions(centerGridX, centerGridY);

            // 消耗第一个位置（中心点）
            gridPositionGenerator.next();

            // 为其余节点分配网格位置
            for (let i = 1; i < talentNodes.value.length; i++) {
                if (!talentNodes.value[i].desc?.position) continue;

                // 寻找下一个未被占用的网格位置
                let nextGridPos;
                let found = false;

                while (!found) {
                    nextGridPos = gridPositionGenerator.next().value;
                    if (!occupiedGrids.has(gridKey(nextGridPos[0], nextGridPos[1]))) {
                        // 找到未被占用的网格
                        found = true;
                    }
                }

                // 将节点放在网格中心
                talentNodes.value[i].desc.position.x = nextGridPos[0] * GRID_SIZE;
                talentNodes.value[i].desc.position.y = nextGridPos[1] * GRID_SIZE;

                // 标记该网格已被占用
                occupiedGrids.set(gridKey(nextGridPos[0], nextGridPos[1]), i);
            }
        };

        // 切换预览模式
        const togglePreviewMode = () => {
            if (isInPreviewMode.value) {
                // 退出预览模式，恢复到进入预览模式前的位置
                talentNodes.value.forEach(node => {
                    if (previewModeNodePositions.value[node.id]) {
                        node.desc.position = { ...previewModeNodePositions.value[node.id] };
                    }
                });
                isInPreviewMode.value = false;

                // 节点位置改变后，需要重建四叉树
                quadTreeNeedsRebuild = true;
            } else {
                // 进入预览模式前，保存当前节点位置到预览模式位置存储
                talentNodes.value.forEach(node => {
                    previewModeNodePositions.value[node.id] = { ...node.desc.position };
                });

                // 进入预览模式，重置节点布局
                resetNodes();
                isInPreviewMode.value = true;

                // 节点位置改变后，需要重建四叉树
                quadTreeNeedsRebuild = true;

                // 在节点位置更新后，触发视图更新以确保关系线也更新
                nextTick(() => {
                    // 关系线会在模板中自动重新绘制，因为它们依赖于节点位置
                    console.log('节点位置已更新，关系线将自动更新');
                });
            }
        };

        // 获取画布中心位置
        const getCanvasCenterPosition = () => {
            if (!talentTree.value) return { x: 200, y: 200 };

            const rect = talentTree.value.getBoundingClientRect();
            const centerX = window.innerWidth / 2;
            const centerY = window.innerHeight / 2;

            // 计算相对于画布的坐标（考虑缩放和偏移）
            const relativeX = (centerX - rect.left - currentPanOffset.value.x) / scale.value;
            const relativeY = (centerY - rect.top - currentPanOffset.value.y) / scale.value;

            return { x: Math.round(relativeX), y: Math.round(relativeY) };
        };

        // 添加节点
        const addNode = () => {
            showEditModal.value = true;
            editingNode.id = null;
            editingNode.name = '';
            editingNode.icon = '';
            editingNode.description = '';

            // 获取画布中心位置作为默认位置
            const centerPos = getCanvasCenterPosition();
            editingNode.position = { x: centerPos.x, y: centerPos.y };

            editingNode.active = false;
            editingNode.shape = defaultNodeStyle.shape;
            editingNode.size = defaultNodeStyle.size;
            editingNode.borderColor = defaultNodeStyle.borderColor;
            editingNode.backgroundColor = defaultNodeStyle.backgroundColor;
            editingNode.transparentBg = false;
            editingNode.properties = {};
            editingNode.style = {
                borderStyle: defaultNodeStyle.borderStyle,
                borderWidth: defaultNodeStyle.borderWidth,
                opacity: defaultNodeStyle.opacity
            };
            propertiesJson.value = '{}';
        };

        // 记录当前节点信息到临时节点列表
        const recordNode = () => {
            // 验证节点信息
            if (!editingNode.name.trim()) {
                alert('请先输入节点名称');
                return;
            }

            // 检查节点名称是否已存在
            const existingNodeIndex = batchNodes.value.findIndex(node => node.name.trim() === editingNode.name.trim());
            if (existingNodeIndex !== -1) {
                alert('节点名称已存在，请使用不同的名称');
                return;
            }

            // 创建节点副本并添加到临时列表
            const nodeCopy = {
                ...editingNode,
                id: null, // 新节点ID由后端生成
                position: { ...editingNode.position }, // 确保位置是独立的副本
                style: { ...editingNode.style },
                properties: { ...editingNode.properties }
            };

            // 解析属性JSON
            try {
                if (propertiesJson.value) {
                    nodeCopy.properties = JSON.parse(propertiesJson.value);
                }
            } catch (e) {
                console.error('属性JSON格式错误:', e);
                alert('属性JSON格式错误，请检查格式');
                return;
            }

            // 根据已添加的节点数量，调整位置以避免重叠
            const spacing = 100; // 节点间距
            const nodeCount = batchNodes.value.length;

            // 计算新节点的位置，围绕中心位置进行均匀分布
            if (nodeCount > 0) {
                // 按照屏幕中心位置进行均匀分布，如节点2在(a+100,b)，节点3在(a,b+100)等
                // 使用简单的网格布局算法
                const centerX = editingNode.position.x; // 中心X坐标
                const centerY = editingNode.position.y; // 中心Y坐标

                // 计算节点在网格中的位置
                if (nodeCount === 1) {
                    // 第一个临时节点保持在中心位置
                    nodeCopy.position.x = centerX;
                    nodeCopy.position.y = centerY;
                } else {
                    // 后续节点按照要求的模式分布
                    // 节点2: (a+100, b), 节点3: (a, b+100), 节点4: (a-100, b), 节点5: (a, b-100)
                    // 之后继续扩展，如节点6: (a+100, b+100), 节点7: (a+100, b-100)等

                    // 为简化实现，我们按以下模式分布：
                    // 1: (a, b), 2: (a+100, b), 3: (a, b+100), 4: (a+100, b+100), 5: (a-100, b), 6: (a, b-100)...
                    const offsetIndex = nodeCount - 1; // 从0开始计算偏移

                    // 按照特定模式分配位置
                    switch (offsetIndex % 8) {
                        case 0: // 第2个节点
                            nodeCopy.position.x = centerX + spacing;
                            nodeCopy.position.y = centerY;
                            break;
                        case 1: // 第3个节点
                            nodeCopy.position.x = centerX;
                            nodeCopy.position.y = centerY + spacing;
                            break;
                        case 2: // 第4个节点
                            nodeCopy.position.x = centerX - spacing;
                            nodeCopy.position.y = centerY;
                            break;
                        case 3: // 第5个节点
                            nodeCopy.position.x = centerX;
                            nodeCopy.position.y = centerY - spacing;
                            break;
                        case 4: // 第6个节点
                            nodeCopy.position.x = centerX + spacing;
                            nodeCopy.position.y = centerY + spacing;
                            break;
                        case 5: // 第7个节点
                            nodeCopy.position.x = centerX - spacing;
                            nodeCopy.position.y = centerY + spacing;
                            break;
                        case 6: // 第8个节点
                            nodeCopy.position.x = centerX - spacing;
                            nodeCopy.position.y = centerY - spacing;
                            break;
                        case 7: // 第9个节点
                            nodeCopy.position.x = centerX + spacing;
                            nodeCopy.position.y = centerY - spacing;
                            break;
                        default:
                            // 如果超过8个节点，继续使用螺旋模式
                            {
                                const angle = (offsetIndex % 8) * (Math.PI / 4);
                                const radius = Math.floor(offsetIndex / 8 + 1) * spacing;
                                nodeCopy.position.x = centerX + Math.round(radius * Math.cos(angle));
                                nodeCopy.position.y = centerY + Math.round(radius * Math.sin(angle));
                            }
                    }
                }
            }

            batchNodes.value.push(nodeCopy);

            // 清空当前编辑的节点信息，准备下一个节点
            editingNode.name = '';
            editingNode.icon = '';
            editingNode.description = '';
            editingNode.properties = {};
            propertiesJson.value = '{}';

            alert('节点已记录到临时列表');
        };

        // 从临时节点列表中删除节点
        const removeBatchNode = (index) => {
            if (confirm('确定要删除这个临时节点吗？')) {
                batchNodes.value.splice(index, 1);
            }
        };

        // 编辑节点
        const editNode = () => {
            if (!selectedNode.value) return;

            showEditModal.value = true;
            editingNode.id = selectedNode.value.id;
            editingNode.name = selectedNode.value.name;
            editingNode.icon = selectedNode.value.desc?.icon || '';
            editingNode.description = selectedNode.value.desc?.description || '';
            editingNode.position = { ...(selectedNode.value.desc?.position || { x: 0, y: 0 }) };
            editingNode.active = selectedNode.value.desc?.active ?? false;
            editingNode.shape = selectedNode.value.desc?.shape || 'circle';
            editingNode.size = selectedNode.value.desc?.size || 80;
            editingNode.borderColor = selectedNode.value.desc?.borderColor || '#000000';
            editingNode.backgroundColor = selectedNode.value.desc?.backgroundColor || '#ffffff';
            editingNode.transparentBg = !selectedNode.value.desc?.backgroundColor;
            editingNode.properties = { ...(selectedNode.value.desc?.properties || {}) };
            // 处理style属性
            editingNode.style = { ...(selectedNode.value.desc?.style || { borderStyle: 'solid', borderWidth: 2, opacity: 1 }) };
            propertiesJson.value = JSON.stringify(selectedNode.value.desc?.properties || {}, null, 2);
        };

        // 编辑连接
        const editLink = () => {
            if (!selectedLink.value) return;

            showLinkModal.value = true;
            editingLink.source = selectedLink.value.source;
            editingLink.target = selectedLink.value.target;
            editingLink.graphId = selectedLink.value.graphId || futureEngineGraphId.value;
            editingLink.value = selectedLink.value.value || 3;
            editingLink.style = selectedLink.value.style || {
                stroke: '#000000',
                lineStyle: 'solid',
                lineType: 'straight',
                cornerRadius: 10
            };
            editingLink.offset = selectedLink.value.offset || { x: 0, y: 0 };

            closeLinkDetailModal();
        };

        // 保存节点
        const saveNode = async () => {
            try {
                // 如果有批量节点，优先保存批量节点
                if (batchNodes.value.length > 0) {
                    let successCount = 0;
                    let errorCount = 0;

                    // 批量保存所有临时节点
                    for (const node of batchNodes.value) {
                        try {
                            // 处理透明背景
                            const backgroundColor = node.transparentBg ? 'transparent' : node.backgroundColor;

                            // 构造完整的节点描述对象
                            const nodeDesc = {
                                icon: node.icon,
                                description: node.description,
                                position: { ...node.position },
                                shape: node.shape,
                                size: node.size,
                                borderColor: node.borderColor,
                                backgroundColor: backgroundColor,
                                properties: node.properties,
                                style: { ...node.style },
                                active: node.active
                            };

                            // 将节点描述对象转换为JSON字符串
                            const nodeDescString = JSON.stringify(nodeDesc);

                            const response = await graphService.addNode(
                                node.name,
                                nodeDescString
                            );

                            if (response.status === 200) {
                                const newNode = {
                                    id: response.data.id,
                                    name: node.name,
                                    desc: nodeDesc
                                };

                                // 检查是否已存在相同ID的节点，如果存在则替换，否则添加新节点
                                const existingNodeIndex = talentNodes.value.findIndex(n => n.id === newNode.id);
                                if (existingNodeIndex !== -1) {
                                    // 替换已存在的节点
                                    talentNodes.value[existingNodeIndex] = newNode;
                                } else {
                                    // 添加新节点
                                    talentNodes.value.push(newNode);
                                    // 仅为新添加的节点自动添加"futureEngine"标签
                                    try {
                                        const tagResponse = await graphService.addTagToNode(newNode.id, "futureEngine");
                                        if (tagResponse.status === 200) {
                                            console.log('成功为新节点添加"futureEngine"标签');
                                        } else {
                                            console.error('为节点添加标签失败:', tagResponse.message);
                                        }
                                    } catch (tagError) {
                                        console.error('为节点添加标签时发生错误:', tagError);
                                    }
                                }
                                successCount++;
                            } else {
                                console.error(`节点 ${node.name} 添加失败:`, response.message);
                                errorCount++;
                            }
                        } catch (error) {
                            console.error(`添加节点 ${node.name} 时发生错误:`, error);
                            errorCount++;
                        }
                    }

                    // 清空临时节点列表
                    batchNodes.value = [];

                    alert(`批量节点保存完成: 成功 ${successCount} 个, 失败 ${errorCount} 个`);
                } else {
                    // 保存单个节点（原有逻辑）
                    // 解析属性JSON
                    const properties = propertiesJson.value ? JSON.parse(propertiesJson.value) : {};

                    // 处理透明背景
                    const backgroundColor = editingNode.transparentBg ? 'transparent' : editingNode.backgroundColor;

                    // 构造完整的节点描述对象
                    const nodeDesc = {
                        icon: editingNode.icon,
                        description: editingNode.description,
                        position: { ...editingNode.position },
                        shape: editingNode.shape,
                        size: editingNode.size,
                        borderColor: editingNode.borderColor,
                        backgroundColor: backgroundColor,
                        properties: properties,
                        style: { ...editingNode.style },
                        active: editingNode.id ? talentNodes.value.find(node => node.id === editingNode.id)?.desc?.active : editingNode.active
                    };

                    // 将节点描述对象转换为JSON字符串
                    const nodeDescString = JSON.stringify(nodeDesc);

                    if (editingNode.id) {
                        // 更新现有节点
                        const index = talentNodes.value.findIndex(node => node.id === editingNode.id);
                        if (index !== -1) {
                            // 先更新前端数据
                            talentNodes.value[index] = {
                                ...talentNodes.value[index],
                                name: editingNode.name,
                                desc: nodeDesc
                            };

                            // 然后更新后端数据
                            try {
                                const response = await graphService.modifyNode(
                                    editingNode.id,
                                    editingNode.name,
                                    nodeDescString
                                );

                                if (response.status === 200) {
                                    alert('节点更新成功');
                                } else {
                                    alert(`节点更新失败: ${response.message}`);
                                }
                            } catch (error) {
                                console.error('更新节点时发生错误:', error);
                                alert('节点更新失败，请查看控制台错误信息');
                            }
                        }
                    } else {
                        // 添加新节点
                        try {
                            const response = await graphService.addNode(
                                editingNode.name,
                                nodeDescString
                            );

                            if (response.status === 200) {
                                const newNode = {
                                    id: response.data.id,
                                    name: editingNode.name,
                                    desc: nodeDesc
                                };
                                // 检查是否已存在相同ID的节点，如果存在则替换，否则添加新节点
                                const existingNodeIndex = talentNodes.value.findIndex(node => node.id === newNode.id);
                                if (existingNodeIndex !== -1) {
                                    // 替换已存在的节点
                                    talentNodes.value[existingNodeIndex] = newNode;
                                } else {
                                    // 添加新节点
                                    talentNodes.value.push(newNode);
                                    // 仅为新添加的节点自动添加"futureEngine"标签
                                    try {
                                        const tagResponse = await graphService.addTagToNode(newNode.id, "futureEngine");
                                        if (tagResponse.status === 200) {
                                            console.log('成功为新节点添加"futureEngine"标签');
                                        } else {
                                            console.error('为节点添加标签失败:', tagResponse.message);
                                        }
                                    } catch (tagError) {
                                        console.error('为节点添加标签时发生错误:', tagError);
                                    }
                                }
                                alert('节点添加成功');
                            } else {
                                alert(`节点添加失败: ${response.message}`);
                            }
                        } catch (error) {
                            console.error('添加节点时发生错误:', error);
                            alert('节点添加失败，请查看控制台错误信息');
                        }
                    }
                }

                // 保存节点后可能改变了位置，需要重建四叉树
                quadTreeNeedsRebuild = true;

                // 强制更新显示的节点，确保新添加的节点立即显示
                forceUpdateDisplayedNodes();

                closeEditModal();
            } catch (e) {
                console.error('保存节点时发生错误:', e);
                alert('属性格式错误，请确保输入的是有效的JSON格式');
            }
        };
        // 保存连接
        const saveLink = async () => {
            // 检查是否是多源节点连接
            if (sourceNodes?.value?.length > 0 && targetNode?.value) {
                // 为每个源节点创建到目标节点的连接
                let successCount = 0;
                let errorCount = 0;

                for (const sourceNode of sourceNodes.value) {
                    // 检查是否已经存在相同的连接
                    const existingLinkIndex = links.value.findIndex(link =>
                        (link.source === sourceNode.id && link.target === targetNode.value.id) ||
                        (link.source === targetNode.value.id && link.target === sourceNode.id)
                    );

                    // 构造链接描述对象
                    const linkDesc = {
                        style: { ...editingLink.style },
                        offset: editingLink.offset || { x: 0, y: 0 }
                    };

                    // 将链接描述对象转换为JSON字符串
                    const linkDescString = JSON.stringify(linkDesc);
                    console.log('Link Description String:', linkDescString);
                    console.log('Link futureEngineGraphId:', futureEngineGraphId.value);

                    try {
                        if (existingLinkIndex === -1) {
                            // 添加新连接到后端
                            const response = await graphService.addGraph(
                                sourceNode.id,
                                targetNode.value.id,
                                editingLink.graphId || futureEngineGraphId.value,
                                linkDescString
                            );

                            if (response.status === 200) {
                                // 添加新连接到前端
                                const newLink = {
                                    source: sourceNode.id,
                                    target: targetNode.value.id,
                                    graphId: editingLink.graphId || futureEngineGraphId.value,
                                    value: editingLink.value,
                                    style: { ...editingLink.style },
                                    offset: editingLink.offset || { x: 0, y: 0 }
                                };
                                links.value.push(newLink);
                                // 更新原始offset记录
                                originalLinkOffsets.value[`${newLink.source}-${newLink.target}`] = { ...(newLink.offset || { x: 0, y: 0 }) };
                                successCount++;
                            } else {
                                alert(`连接添加失败: ${response.message}`);
                                errorCount++;
                            }
                        } else {
                            // 如果连接已存在，跳过
                            console.log(`连接 ${sourceNode.id} -> ${targetNode.value.id} 已存在，跳过`);
                        }
                    } catch (error) {
                        console.error('保存连接时发生错误:', error);
                        errorCount++;
                    }
                }

                // 显示操作结果
                if (successCount > 0) {
                    alert(`成功创建 ${successCount} 个连接${errorCount > 0 ? '，' + errorCount + ' 个连接失败' : ''}`);
                } else if (errorCount > 0) {
                    alert(`所有连接创建都失败了，失败数量: ${errorCount}`);
                }

                // 重置状态
                isAddingLink.value = false;
                sourceNodes.value = [];
                targetNode.value = null;

                closeLinkModal();
            } else if (editingLink.source && editingLink.target) {
                // 原有的单源节点连接逻辑
                // 检查是否已经存在相同的连接
                const existingLinkIndex = links.value.findIndex(link =>
                    (link.source === editingLink.source && link.target === editingLink.target) ||
                    (link.source === editingLink.target && link.target === editingLink.source)
                );

                // 构造链接描述对象
                const linkDesc = {
                    style: { ...editingLink.style },
                    offset: editingLink.offset || { x: 0, y: 0 }
                };

                // 将链接描述对象转换为JSON字符串
                const linkDescString = JSON.stringify(linkDesc);
                console.log('Link Description String:', linkDescString);
                console.log('Link futureEngineGraphId:', futureEngineGraphId.value);

                try {
                    if (existingLinkIndex === -1) {
                        // 添加新连接到后端
                        const response = await graphService.addGraph(
                            editingLink.source,
                            editingLink.target,
                            editingLink.graphId || futureEngineGraphId.value,
                            linkDescString
                        );

                        if (response.status === 200) {
                            // 添加新连接到前端
                            const newLink = { ...editingLink };
                            links.value.push(newLink);
                            // 更新原始offset记录
                            originalLinkOffsets.value[`${newLink.source}-${newLink.target}`] = { ...(newLink.offset || { x: 0, y: 0 }) };
                            alert('连接添加成功');
                        } else {
                            alert(`连接添加失败: ${response.message}`);
                        }
                    } else {
                        // 更新现有连接到后端
                        const response = await graphService.modifyGraph(
                            editingLink.source,
                            editingLink.target,
                            editingLink.graphId,
                            linkDescString,
                            editingLink.strength || 1
                        );

                        if (response.status === 200) {
                            // 更新现有连接
                            const updatedLink = { ...editingLink };
                            links.value[existingLinkIndex] = updatedLink;
                            // 更新原始offset记录
                            originalLinkOffsets.value[`${updatedLink.source}-${updatedLink.target}`] = { ...(updatedLink.offset || { x: 0, y: 0 }) };
                            alert('连接更新成功');
                        } else {
                            alert(`连接更新失败: ${response.message}`);
                        }
                    }
                } catch (error) {
                    console.error('保存连接时发生错误:', error);
                    alert('连接保存失败，请查看控制台错误信息');
                }

                closeLinkModal();
            }
        };

        // 删除节点
        const deleteNode = async () => {
            if (!selectedNode.value) return;

            if (confirm(`确定要删除节点 "${selectedNode.value.name}" 吗？`)) {
                const nodeId = selectedNode.value.id;

                // 调用后端接口移除节点与futureEngine标签的绑定关系
                try {
                    const response = await graphService.removeTagToNode(nodeId, futureEngineGraphId.value);
                    if (response.status === 200) {
                        // 从前端数据中移除节点
                        const index = talentNodes.value.findIndex(node => node.id === nodeId);
                        if (index !== -1) {
                            talentNodes.value.splice(index, 1);
                        }

                        // 删除与该节点相关的所有连接
                        links.value = links.value.filter(link =>
                            link.source !== nodeId && link.target !== nodeId
                        );

                        selectedNode.value = null;

                        // 删除节点后需要重建四叉树
                        quadTreeNeedsRebuild = true;

                        alert('节点删除成功');
                    } else {
                        alert(`节点删除失败: ${response.message}`);
                    }
                } catch (error) {
                    console.error('删除节点时发生错误:', error);
                    alert('节点删除失败，请查看控制台错误信息');
                }
            }
        };

        // 删除连接
        const deleteLink = async (link) => {
            try {
                // 调用后端删除接口
                const response = await graphService.deleteGraph(
                    link.source,
                    link.target,
                    link.graphId
                );

                if (response.status === 200) {
                    // 从本地数据中删除
                    const index = links.value.findIndex(l =>
                        l.source === link.source && l.target === link.target
                    );
                    if (index !== -1) {
                        links.value.splice(index, 1);
                    }
                    alert('连接删除成功');
                } else {
                    alert(`连接删除失败: ${response.message}`);
                }
            } catch (error) {
                console.error('删除连接时发生错误:', error);
                alert('连接删除失败，请查看控制台错误信息');
            }
            closeLinkDetailModal();
        };

        // 选择节点
        const selectNode = async (node) => {
            console.log('Selecting node:', node);
            // 如果正在拖拽，则不处理选择
            if (isDragging.value) return;

            selectedNode.value = node;
            // 同步更新highlightedNodes以保持状态一致
            highlightedNodes.value.clear();
            highlightedNodes.value.add(node.id);
            selectedLink.value = null; // 取消选择连接

            // 加载与当前选中节点相关的学习节点和练习节点数据
            await loadFutureStudyData(node.id);
            await loadFuturePracticeData(node.id);

            // 自动计算并更新节点的stars值
            await autoCalculateAndUpdateStars(node);
        };

        // 选择连接，同时支持触摸双击
        const selectLink = (event, link) => {
            // 检测是否为触摸设备上的双击模拟
            if (event && event.type === 'touchend') {
                const now = Date.now();
                const DOUBLE_CLICK_DELAY = 300; // 双击时间间隔阈值

                // 检查是否为同一目标元素且在时间阈值内
                if (lastLinkTouchTarget.value === event.target && (now - lastLinkTouchTime.value) < DOUBLE_CLICK_DELAY) {
                    // 触摸双击事件
                    lastLinkTouchTime.value = 0; // 重置时间
                    lastLinkTouchTarget.value = null; // 重置目标

                    // 阻止默认行为
                    event.preventDefault();
                } else {
                    // 记录第一次触摸的时间和目标
                    lastLinkTouchTime.value = now;
                    lastLinkTouchTarget.value = event.target;
                    return; // 不执行双击操作
                }
            }

            // 如果正在拖拽控制点，则不处理选择
            if (isDraggingControlPoint.value) return;

            selectedLink.value = link;
            selectedNode.value = null; // 取消选择节点
        };
        // 关闭详情模态框
        const closeDetailModal = () => {
            selectedNode.value = null;
            highlightedNodes.value.clear(); // 同时清除高亮节点
        };

        // 关闭编辑模态框
        const closeEditModal = () => {
            showEditModal.value = false;
        };

        // 关闭连接模态框
        const closeLinkModal = () => {
            showLinkModal.value = false;
        };

        // 关闭连接详情模态框
        const closeLinkDetailModal = () => {
            selectedLink.value = null;
        };

        // 保存当前布局
        const saveCurrentLayout = async () => {
            try {
                let nodeUpdateCount = 0;
                let linkUpdateCount = 0;

                // 遍历所有节点，检查位置是否有变化
                for (const node of talentNodes.value) {
                    // 获取节点当前的描述信息
                    const currentDesc = node.desc;

                    // 检查位置是否存在
                    if (currentDesc.position) {
                        // 检查位置是否有变化
                        const originalPosition = originalNodePositions.value[node.id];
                        const currentPosition = currentDesc.position;

                        // 如果原始位置不存在，或者位置发生了变化，则更新
                        if (!originalPosition ||
                            originalPosition.x !== currentPosition.x ||
                            originalPosition.y !== currentPosition.y) {

                            // 构造更新后的节点描述对象
                            const nodeDesc = {
                                ...currentDesc,
                                position: { ...currentDesc.position }
                            };

                            // 将节点描述对象转换为JSON字符串
                            const nodeDescString = JSON.stringify(nodeDesc);

                            // 调用后端接口更新节点位置
                            try {
                                const response = await graphService.modifyNode(
                                    node.id,
                                    node.name,
                                    nodeDescString
                                );

                                if (response.status === 200) {
                                    nodeUpdateCount++;
                                    // 更新原始位置记录
                                    originalNodePositions.value[node.id] = { ...currentPosition };
                                } else {
                                    console.error(`节点 ${node.name} 更新失败: ${response.message}`);
                                }
                            } catch (error) {
                                console.error(`更新节点 ${node.name} 时发生错误:`, error);
                            }
                        }
                    }
                }

                // 遍历所有连接线，检查offset是否有变化
                for (const link of links.value) {
                    // 检查offset是否存在
                    if (link.offset) {
                        // 检查offset是否有变化
                        const originalOffset = originalLinkOffsets.value[`${link.source}-${link.target}`] || { x: 0, y: 0 };
                        const currentOffset = link.offset;

                        // 如果原始offset不存在，或者offset发生了变化，则更新
                        if (!originalOffset ||
                            originalOffset.x !== currentOffset.x ||
                            originalOffset.y !== currentOffset.y) {

                            // 构造链接描述对象
                            const linkDesc = {
                                style: { ...link.style },
                                offset: { ...link.offset }
                            };

                            // 将链接描述对象转换为JSON字符串
                            const linkDescString = JSON.stringify(linkDesc);

                            // 调用后端接口更新连接线offset
                            try {
                                const response = await graphService.modifyGraph(
                                    link.source,
                                    link.target,
                                    link.graphId,
                                    linkDescString
                                );

                                if (response.status === 200) {
                                    linkUpdateCount++;
                                    // 更新原始offset记录
                                    originalLinkOffsets.value[`${link.source}-${link.target}`] = { ...currentOffset };
                                } else {
                                    console.error(`连接线 ${link.source}-${link.target} 更新失败: ${response.message}`);
                                }
                            } catch (error) {
                                console.error(`更新连接线 ${link.source}-${link.target} 时发生错误:`, error);
                            }
                        }
                    }
                }

                alert(`布局保存成功，共更新了 ${nodeUpdateCount} 个节点的位置信息和 ${linkUpdateCount} 条连接线的偏移信息`);
            } catch (e) {
                console.error('保存布局时发生错误:', e);
                alert('保存布局失败，请查看控制台错误信息');
            }
        };        // 重新加载数据
        const reloadData = async () => {
            // 重新加载FutureEngine数据
            await loadFutureEngineData();

            // 重新生成临时的节点星级数据
            // generateTempNodeStars();

            // 强制更新链接层以确保offset被正确应用
            forceUpdateLinks();

            // 再次强制触发Vue的响应式更新
            nextTick(() => {
                // 通过创建新的链接数组来触发重新渲染
                const newLinks = links.value.map(link => ({
                    ...link,
                    // 确保offset对象也是新的
                    offset: link.offset ? { ...link.offset } : { x: 0, y: 0 }
                }));
                // 使用splice方法更新链接数组，确保Vue能正确检测到变化
                links.value.splice(0, links.value.length, ...newLinks);
            });

            // 重新加载数据后，更新originalNodePositions以匹配重载后的节点位置
            originalNodePositions.value = {};
            talentNodes.value.forEach(node => {
                originalNodePositions.value[node.id] = { ...node.desc.position };
            });

            alert('数据重新加载完成');
        };

        // 强制更新链接层
        const forceUpdateLinks = () => {
            // 触发链接层的重新渲染
            nextTick(() => {
                // 通过创建新的链接数组来触发重新渲染
                const newLinks = links.value.map(link => ({
                    ...link,
                    // 确保offset对象也是新的
                    offset: link.offset ? { ...link.offset } : { x: 0, y: 0 }
                }));
                // 使用splice方法更新链接数组，确保Vue能正确检测到变化
                links.value.splice(0, links.value.length, ...newLinks);
            });
        };
        // 更新节点激活状态
        const updateNodeActivation = () => {
            // 这里可以添加保存逻辑
            console.log('节点激活状态已更新');
        };

        // 拖拽开始
        const handleDragStart = (event, node) => {
            // 只有在编辑模式下才处理拖拽
            if (!isEditMode.value) return;

            // 阻止默认行为，防止页面滚动
            event.preventDefault();

            // 清除可能存在的双击定时器
            if (window.nodeDoubleClickTimer) {
                clearTimeout(window.nodeDoubleClickTimer);
                window.nodeDoubleClickTimer = null;
                // 如果定时器存在，说明是双击操作，不进行拖拽
                return;
            }

            // 获取事件坐标
            const coords = getEventCoordinates(event);

            // 保存鼠标的初始位置
            initialMousePosition.value = {
                x: coords.clientX,
                y: coords.clientY
            };

            // 先处理节点高亮逻辑，确保多选功能正常工作
            // 检查当前节点是否已被选中
            const isNodeSelected = highlightedNodes.value.has(node.id);

            // 如果节点未被选中，且没有按住Ctrl或Shift键，则清空其他选中节点
            if (!isNodeSelected && !isCtrlPressed.value && !isShiftPressed.value) {
                highlightedNodes.value.clear();
                highlightedNodes.value.add(node.id);
            }

            // 如果节点已被选中，且按住了Ctrl键，则取消选中
            if (isNodeSelected && isCtrlPressed.value) {
                highlightedNodes.value.delete(node.id);
                // 如果取消选中后没有节点被选中，则选中当前节点
                if (highlightedNodes.value.size === 0) {
                    highlightedNodes.value.add(node.id);
                }
            }

            // 不立即设置拖拽状态，而是等待鼠标移动一定距离后再开始拖拽
            // 这样可以确保click事件能够正常触发
            draggedNode.value = node;
            draggedNodeElement.value = event.currentTarget;

            // 保存所有被选中节点的初始位置
            const selectedNodes = talentNodes.value.filter(n => highlightedNodes.value.has(n.id));
            selectedNodes.forEach(selectedNode => {
                selectedNode.initialPosition = {
                    x: selectedNode.desc.position.x,
                    y: selectedNode.desc.position.y
                };
            });

            // 计算手指/鼠标相对于节点左上角的偏移量（考虑缩放因子）
            const targetElement = event.currentTarget;
            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                // 计算鼠标相对于节点的偏移量（在缩放坐标系中）
                dragOffset.value.x = (coords.clientX - rect.left) / scale.value;
                dragOffset.value.y = (coords.clientY - rect.top) / scale.value;

                // 保存节点的初始位置
                initialNodePosition.value = {
                    x: node.desc.position.x,
                    y: node.desc.position.y
                };
            }

            // 阻止默认行为和事件冒泡
            event.preventDefault();
            event.stopPropagation();

            // 防止文本选择
            document.body.style.userSelect = 'none';
            document.body.style.webkitUserSelect = 'none'; // Safari兼容

            // 设置一个非常短的延迟定时器，用于可能的双击检测
            window.nodeDoubleClickTimer = setTimeout(() => {
                window.nodeDoubleClickTimer = null;
            }, 200); // 200ms延迟，用于双击检测
        };

        // 拖拽中
        const handleDrag = (event) => {
            // 只有在编辑模式下才处理拖拽
            if (!isEditMode.value) return;

            // 如果还没有开始拖拽且有拖拽节点，则检查是否需要开始拖拽
            if (!isDragging.value && draggedNode.value) {
                // 计算鼠标移动的距离
                const coords = getEventCoordinates(event);
                const deltaX = Math.abs(coords.clientX - initialMousePosition.value.x);
                const deltaY = Math.abs(coords.clientY - initialMousePosition.value.y);

                // 只有当鼠标移动超过一定距离时才开始拖拽，这样可以确保click事件能够正常触发
                const DRAG_THRESHOLD = 5; // 5像素的拖拽阈值
                if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
                    isDragging.value = true;
                } else {
                    // 如果还没达到拖拽阈值，不执行任何操作
                    return;
                }
            }

            // 如果没有在拖拽状态，直接返回
            if (!isDragging.value) return;

            console.log('handleDrag');
            if (!draggedNode.value || !talentTree.value || !draggedNodeElement.value) return;

            // 阻止默认行为，防止浏览器滚动等
            event.preventDefault();

            // 获取事件坐标
            const coords = getEventCoordinates(event);

            // 计算鼠标移动的距离（在缩放坐标系中）
            const deltaX = (coords.clientX - initialMousePosition.value.x) / scale.value;
            const deltaY = (coords.clientY - initialMousePosition.value.y) / scale.value;

            // 获取所有被选中的节点
            const selectedNodes = talentNodes.value.filter(n => highlightedNodes.value.has(n.id));

            // 更新所有被选中节点的位置
            selectedNodes.forEach(selectedNode => {
                // 计算节点的新位置
                const newX = selectedNode.initialPosition.x + deltaX;
                const newY = selectedNode.initialPosition.y + deltaY;

                // 确保坐标为整数
                const roundedX = Math.round(newX);
                const roundedY = Math.round(newY);

                // 更新数据模型中的位置（不包含画布偏移量）
                selectedNode.desc.position.x = roundedX;
                selectedNode.desc.position.y = roundedY;

                // 更新节点的样式位置
                const nodeElement = document.querySelector(`[data-node-id="${selectedNode.id}"]`);
                if (nodeElement) {
                    nodeElement.style.left = `${roundedX + currentPanOffset.value.x}px`;
                    nodeElement.style.top = `${roundedY + currentPanOffset.value.y}px`;
                }
            });
        };

        // 控制点鼠标按下
        const handleControlPointMouseDown = (event, point, index, link) => {
            if (!isEditMode.value) return;

            event.stopPropagation();

            // 清除可能存在的双击定时器
            if (window.controlPointDoubleClickTimer) {
                clearTimeout(window.controlPointDoubleClickTimer);
                window.controlPointDoubleClickTimer = null;
                // 如果定时器存在，说明是双击操作，不进行拖拽
                return;
            }

            // 立即设置拖拽状态，避免延迟造成的卡顿感
            isDraggingControlPoint.value = true;
            draggedControlPoint.value = { point, index, link };

            // 添加拖拽视觉反馈
            const targetElement = event.currentTarget;
            if (targetElement) {
                targetElement.classList.add('dragging');
                // 立即计算鼠标相对于控制点的偏移量（考虑缩放因子）
                const rect = targetElement.getBoundingClientRect();
                dragOffset.value.x = (event.clientX - rect.left - 6) * scale.value; // 6 is half of the circle radius
                dragOffset.value.y = (event.clientY - rect.top - 6) * scale.value;
            }

            // 阻止默认行为
            event.preventDefault();

            // 设置一个非常短的延迟定时器，用于可能的双击检测
            window.controlPointDoubleClickTimer = setTimeout(() => {
                window.controlPointDoubleClickTimer = null;
            }, 200); // 200ms延迟，用于双击检测
        };

        // 直线控制点鼠标按下
        const handleStraightControlPointMouseDown = (event, link) => {
            if (!isEditMode.value) return;

            event.stopPropagation();

            // 清除可能存在的双击定时器
            if (window.controlPointDoubleClickTimer) {
                clearTimeout(window.controlPointDoubleClickTimer);
                window.controlPointDoubleClickTimer = null;
                // 如果定时器存在，说明是双击操作，不进行拖拽
                return;
            }

            // 立即设置拖拽状态，避免延迟造成的卡顿感
            isDraggingControlPoint.value = true;
            draggedControlPoint.value = { link };

            // 添加拖拽视觉反馈
            const targetElement = event.currentTarget;
            if (targetElement) {
                targetElement.classList.add('dragging');
                // 立即计算鼠标相对于控制点的偏移量（考虑缩放因子）
                const rect = targetElement.getBoundingClientRect();
                dragOffset.value.x = (event.clientX - rect.left - 6) * scale.value; // 6 is half of the circle radius
                dragOffset.value.y = (event.clientY - rect.top - 6) * scale.value;
            }

            // 阻止默认行为
            event.preventDefault();

            // 设置一个非常短的延迟定时器，用于可能的双击检测
            window.controlPointDoubleClickTimer = setTimeout(() => {
                window.controlPointDoubleClickTimer = null;
            }, 200); // 200ms延迟，用于双击检测
        };

        // 控制点拖拽中
        const handleControlPointDrag = (event) => {
            if (!isDraggingControlPoint.value || !draggedControlPoint.value) return;

            // 阻止默认行为
            event.preventDefault();

            // 计算控制点的新位置（相对于容器，考虑缩放因子）
            const scaledX = (event.clientX - talentTree.value.getBoundingClientRect().left) / scale.value;
            const scaledY = (event.clientY - talentTree.value.getBoundingClientRect().top) / scale.value;
            const newX = scaledX - dragOffset.value.x / scale.value;
            const newY = scaledY - dragOffset.value.y / scale.value;

            const link = draggedControlPoint.value.link;

            // 如果是直线控制点
            if (!draggedControlPoint.value.index && draggedControlPoint.value.index !== 0) {
                // 对于直线控制点，我们创建一个虚拟的中间控制点来模拟S型线的效果
                // 确保每个链接都有独立的offset对象，避免引用共享
                // 创建新的offset对象，避免修改原始引用
                const newOffset = link.offset ? { ...link.offset } : { x: 0, y: 0 };

                // 计算原始中点
                const fromPos = getNodePosition(link.source);
                const toPos = getNodePosition(link.target);
                const originalMidX = (fromPos.x + toPos.x) / 2;
                const originalMidY = (fromPos.y + toPos.y) / 2;

                // 更新偏移量
                newOffset.x = newX - originalMidX;
                newOffset.y = newY - originalMidY;

                // 更新链接的offset，确保Vue能正确检测到变化
                link.offset = newOffset;
            } else {
                // 更新S型线控制点位置
                const index = draggedControlPoint.value.index;
                // 使用保持路径一致性的更新函数来更新控制点位置
                updateControlPointConsistently(link, index, newX, newY);
            }
        };

        // 拖拽结束
        const handleDragEnd = () => {
            // 清除可能存在的节点双击定时器
            if (window.nodeDoubleClickTimer) {
                clearTimeout(window.nodeDoubleClickTimer);
                window.nodeDoubleClickTimer = null;
            }

            // 清除可能存在的控制点双击定时器
            if (window.controlPointDoubleClickTimer) {
                clearTimeout(window.controlPointDoubleClickTimer);
                window.controlPointDoubleClickTimer = null;
            }

            // 重置拖拽相关状态
            isDragging.value = false;
            isDraggingControlPoint.value = false;
            draggedNode.value = null;
            draggedNodeElement.value = null;
            draggedControlPoint.value = null;
            dragOffset.value = { x: 0, y: 0 };

            // 清除所有节点的初始位置信息
            talentNodes.value.forEach(node => {
                delete node.initialPosition;
            });

            // 恢复文本选择
            document.body.style.userSelect = '';

            // 移除所有控制点的拖拽视觉反馈
            const controlPoints = document.querySelectorAll('.control-point.dragging');
            controlPoints.forEach(point => {
                point.classList.remove('dragging');
            });

            // 节点拖拽结束后，可能改变了位置，需要重建四叉树
            if (isDragging.value) {
                quadTreeNeedsRebuild = true;
            }
        };

        // 在窗口上监听鼠标移动和释放事件
        const handleMouseMove = (event) => {
            // 即使在未正式开始拖拽时也要调用handleDrag，以便检测是否需要开始拖拽
            if (draggedNode.value && talentTree.value) {
                handleDrag(event);
            }

            if (isDraggingControlPoint.value && talentTree.value && draggedControlPoint.value && draggedControlPoint.value.link) {
                handleControlPointDrag(event);
            }
        };

        const handleMouseUp = () => {
            // 总是调用拖拽结束函数以确保状态正确重置
            handleDragEnd();
        };

        // 在组件挂载时添加全局事件监听器
        onMounted(() => {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            // 添加触摸事件监听器
            window.addEventListener('touchmove', handleMouseMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
            // 初始化画布大小
            adjustCanvasSize();

        });

        // 在组件卸载时移除事件监听器
        onUnmounted(() => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            // 取消可能存在的动画帧
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            // 清除可能存在的控制点双击定时器
            if (window.controlPointDoubleClickTimer) {
                clearTimeout(window.controlPointDoubleClickTimer);
                window.controlPointDoubleClickTimer = null;
            }
            // 清除可能存在的节点双击定时器
            if (window.nodeDoubleClickTimer) {
                clearTimeout(window.nodeDoubleClickTimer);
                window.nodeDoubleClickTimer = null;
            }
            // 清除触摸双击定时器
            lastTouchTime.value = 0;
            lastTouchTarget.value = null;
            lastNodeTouchTime.value = 0;
            lastNodeTouchTarget.value = null;
            lastLinkTouchTime.value = 0;
            lastLinkTouchTarget.value = null;
            // 恢复文本选择
            document.body.style.userSelect = '';
        });

        // // 创建计算属性来过滤节点，避免在模板中使用 v-if 和 v-for 混合
        // const filteredNodesForLink = computed(() => {
        //     if (!editingLink.source) return talentNodes.value;
        //     return talentNodes.value.filter(node => node.id !== editingLink.source);
        // });

        // 检查连接线两端的节点是否都处于激活状态
        const isLinkActive = (link) => {
            // 添加保护措施，防止talentNodes.value为null或undefined
            if (!talentNodes.value || !Array.isArray(talentNodes.value) || !link) {
                return false;
            }

            const sourceNode = talentNodes.value.find(node => node && node.id === link.source);
            const targetNode = talentNodes.value.find(node => node && node.id === link.target);

            // 使用isNodeActuallyActive函数来检查节点是否激活
            return sourceNode && targetNode &&
                sourceNode.desc && targetNode.desc &&
                isNodeActuallyActive(sourceNode) && isNodeActuallyActive(targetNode);
        };

        // 修改shouldShowLink函数以使用isNodeActuallyActive
        const shouldShowLink = (link) => {
            // 如果显示模式是'all'，显示所有连接线
            if (displayMode.value === 'all') {
                return true;
            }

            // 如果显示模式是'active'，只显示两端节点都激活的连接线
            if (displayMode.value === 'active') {
                const sourceNode = talentNodes.value.find(node => node && node.id === link.source);
                const targetNode = talentNodes.value.find(node => node && node.id === link.target);

                // 使用isNodeActuallyActive函数来检查节点是否激活
                return sourceNode && targetNode && isNodeActuallyActive(sourceNode) && isNodeActuallyActive(targetNode);
            }

            // 默认显示所有连接线
            return true;
        };



        // 加载FutureEngine数据
        const loadFutureEngineData = async (page = 1, pageSize = 100) => {
            // 设置最大重试次数
            const maxRetries = 5;

            // 用于跟踪总节点数，以便计算进度
            let totalLoadedNodes = 0;

            // 辅助函数：带重试的API调用
            const fetchWithRetry = async (currentPage, currentPageSize, retryCount = 0) => {
                try {
                    const response = await graphService.findFutureEngineData(currentPage, currentPageSize);
                    return response;
                } catch (error) {
                    // 检查是否是超时错误
                    const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');

                    if (isTimeoutError && retryCount < maxRetries) {
                        console.warn(`获取第 ${currentPage} 页数据超时，正在进行第 ${retryCount + 1} 次重试...`);
                        // 等待一段时间后重试，使用指数退避策略
                        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
                        return fetchWithRetry(currentPage, currentPageSize, retryCount + 1);
                    } else if (retryCount >= maxRetries) {
                        console.error(`获取第 ${currentPage} 页数据失败，已达到最大重试次数 ${maxRetries}:`, error);
                        throw error; // 抛出错误以停止整个加载过程
                    } else {
                        console.error(`获取第 ${currentPage} 页数据发生错误:`, error);
                        throw error; // 非超时错误直接抛出
                    }
                }
            };

            try {
                // 显示加载动画
                loading.value = true;

                // 更新进度条状态
                loadingProgress.value = 0;
                loadingStatus.value = '准备加载数据...';

                // 如果是第一页，清空现有数据
                if (page === 1) {
                    talentNodes.value = [];
                    links.value = [];
                    nodeStars.value = {};

                    // 重置进度
                    totalLoadedNodes = 0;
                }

                const response = await fetchWithRetry(page, pageSize);
                if (response.status === 200) {
                    // 更新talentNodes
                    if (response.data.talentNodes && Array.isArray(response.data.talentNodes)) {
                        // 转换数据格式以适配现有结构
                        const convertedNodes = response.data.talentNodes.map(node => {
                            // 尝试解析节点描述
                            let parsedDesc = {};
                            console.log('Processing node:', node.id, 'desc type:', typeof node.desc, 'desc value:', node.desc);
                            if (typeof node.desc === 'string') {
                                try {
                                    parsedDesc = JSON.parse(node.desc);
                                    console.log('Parsed desc for node', node.id, ':', parsedDesc);
                                } catch (e) {
                                    console.warn('Failed to parse node description for node:', node.id, e);
                                    parsedDesc = {};
                                }
                            } else if (typeof node.desc === 'object' && node.desc !== null) {
                                parsedDesc = node.desc;
                                console.log('Direct object desc for node', node.id, ':', parsedDesc);
                            }

                            // 提取或设置默认值
                            let position = parsedDesc.position || { x: Math.random() * 500, y: Math.random() * 500 };
                            // 确保坐标为整数
                            position = {
                                x: Math.round(position.x),
                                y: Math.round(position.y)
                            };
                            const shape = parsedDesc.shape || 'circle';
                            const size = parsedDesc.size || 80;
                            const borderColor = parsedDesc.borderColor || '#000000';
                            const backgroundColor = parsedDesc.backgroundColor || '#ffffff';
                            const icon = parsedDesc.icon || '';
                            const description = parsedDesc.description || '';
                            const properties = parsedDesc.properties || {};
                            const active = Object.prototype.hasOwnProperty.call(parsedDesc, 'active') ? parsedDesc.active : false;
                            // 保存原始位置信息
                            originalNodePositions.value[node.id] = { ...position };

                            return {
                                id: node.id,
                                name: node.name,
                                desc: {
                                    ...parsedDesc,
                                    position,
                                    shape,
                                    size,
                                    borderColor,
                                    backgroundColor,
                                    icon,
                                    description,
                                    properties,
                                    active
                                }
                            };
                        });

                        // 追加新节点到现有节点列表
                        talentNodes.value = [...talentNodes.value, ...convertedNodes];

                        // 更新已加载节点数
                        totalLoadedNodes += convertedNodes.length;

                        // 更新进度条
                        // 根据当前已加载节点数和当前页数来估算进度
                        // 使用一个简单的进度计算方式：假设总页数为当前页数的2倍（如果当前是第1页）或当前页数（如果当前是后续页）
                        const estimatedTotalPages = Math.max(page * 2, 10); // 估算总页数
                        const estimatedTotalNodes = estimatedTotalPages * pageSize; // 估算总节点数

                        // 计算进度，但限制在80%以内，直到确认完成
                        const estimatedProgress = Math.min(80, Math.round((totalLoadedNodes / estimatedTotalNodes) * 100));
                        loadingProgress.value = estimatedProgress;
                        loadingStatus.value = `加载中... (${totalLoadedNodes}个节点)`;
                    }
                    // 更新links
                    if (response.data.links && Array.isArray(response.data.links)) {
                        // 转换链接数据格式以适配现有结构
                        const convertedLinks = response.data.links.map(link => {
                            // 确保链接有默认的样式
                            const style = link.style || {
                                stroke: "#000000",
                                lineStyle: "solid",
                                lineType: "straight"
                            };

                            // 确保每个链接都有独立的offset对象，避免引用共享
                            const linkOffset = link.offset ? { ...link.offset } : { x: 0, y: 0 };

                            return {
                                source: link.source,
                                target: link.target,
                                graphId: link.graphId || "futureEngine",
                                value: link.value || 1,
                                style: style,
                                offset: linkOffset
                            };
                        });

                        // 追加新链接到现有链接列表
                        links.value = [...links.value, ...convertedLinks];

                        // 强制触发Vue的响应式更新
                        nextTick(() => {
                            // 通过创建新的链接数组来触发重新渲染
                            const newLinks = links.value.map(link => ({
                                ...link,
                                // 确保offset对象也是新的
                                offset: link.offset ? { ...link.offset } : { x: 0, y: 0 }
                            }));
                            // 再次使用splice方法更新链接数组，确保Vue能正确检测到变化
                            links.value.splice(0, links.value.length, ...newLinks);
                        });

                        // 保存原始连接线offset信息
                        links.value.forEach(link => {
                            originalLinkOffsets.value[`${link.source}-${link.target}`] = {
                                ...(link.offset || { x: 0, y: 0 })
                            };
                        });
                    }
                    //更新nodestars
                    // 后端现在返回的是字典而不是列表
                    if (response.data.nodeStars && typeof response.data.nodeStars === 'object' && !Array.isArray(response.data.nodeStars)) {
                        // 合并星级数据
                        nodeStars.value = { ...nodeStars.value, ...response.data.nodeStars };
                    }

                    // 检查是否有更多数据需要加载
                    if (response.data.pagination && response.data.pagination.has_more) {
                        // 递归加载下一页数据
                        console.log('Loading more data...');
                        await loadFutureEngineData(page + 1, pageSize);
                    } else {
                        // 所有数据加载完成，更新进度条
                        loadingProgress.value = 100;
                        loadingStatus.value = '加载完成！';

                        // 短暂延迟后更新active状态，让用户能看到完成状态
                        await new Promise(resolve => setTimeout(resolve, 300));

                        // 所有数据加载完成，更新active状态
                        updateNodeActiveStatus();

                        // 在active状态更新完成后，将第一个active节点居中显示
                        nextTick(() => {
                            // 查找第一个active节点
                            const firstActiveNode = talentNodes.value.find(node => node.desc?.active);
                            if (firstActiveNode) {
                                // 延迟一小段时间确保DOM完全渲染后再执行居中操作
                                setTimeout(() => {
                                    centerNodeOnScreen(firstActiveNode);
                                }, 100);
                            }
                        });

                        // 数据更新后，需要重建四叉树
                        quadTreeNeedsRebuild = true;
                    }
                } else {
                    console.error('加载FutureEngine数据失败:', response.message);
                    // 更新进度状态为失败
                    loadingStatus.value = '加载失败';
                }
            } catch (error) {
                // 检查是否是超时错误
                const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');

                // 更新进度状态为失败
                loadingStatus.value = '加载失败';

                if (isTimeoutError) {
                    console.error('加载FutureEngine数据超时，已达到最大重试次数:', error);
                    // 即使超时，也要显示部分已加载的数据
                    if (page > 1) {
                        // 如果不是第一页超时，至少显示已加载的数据
                        updateNodeActiveStatus();
                        nextTick(() => {
                            const firstActiveNode = talentNodes.value.find(node => node.desc?.active);
                            if (firstActiveNode) {
                                setTimeout(() => {
                                    centerNodeOnScreen(firstActiveNode);
                                }, 100);
                            }
                        });
                        quadTreeNeedsRebuild = true;
                    }
                } else {
                    console.error('加载FutureEngine数据时发生错误:', error);
                }
            } finally {
                // 隐藏加载动画
                // 只有在第一页调用（即最外层调用）完成后才隐藏加载动画
                if (page === 1) {  // 只有在第一次调用（第一页）完成后才隐藏加载动画
                    // 确保加载动画至少显示一段时间，让用户能看到
                    setTimeout(() => {
                        loading.value = false;
                        // 最后重置进度条状态
                        setTimeout(() => {
                            loadingProgress.value = 0;
                            loadingStatus.value = '';
                        }, 1000);
                    }, 500); // 最少显示500毫秒
                }
            }
        };
        const updateNodeActiveStatus = () => {
            console.log('updateNodeActiveStatus:');
            console.log('nodeStars:', nodeStars.value);

            // 第一步：根据stars值设置节点的active状态
            talentNodes.value.forEach(node => {
                if (nodeStars.value[node.id] && nodeStars.value[node.id] > 0) {
                    node.desc.active = true;
                } else {
                    node.desc.active = false;
                }
            });

            // 第二步：遍历links，如果一端节点stars>0，则将另一端节点active设为true
            links.value.forEach(link => {
                const sourceNode = talentNodes.value.find(n => n.id === link.source);
                const targetNode = talentNodes.value.find(n => n.id === link.target);

                if (sourceNode && targetNode) {
                    // 如果源节点stars>0，则目标节点active设为true
                    if (nodeStars.value[sourceNode.id] && nodeStars.value[sourceNode.id] > 0) {
                        targetNode.desc.active = true;
                    }
                    // 如果目标节点stars>0，则源节点active设为true
                    if (nodeStars.value[targetNode.id] && nodeStars.value[targetNode.id] > 0) {
                        sourceNode.desc.active = true;
                    }
                }
            });

            // 第三步：更新links的active状态（如果两端节点都为active，则link也为active）
            links.value.forEach(link => {
                const sourceNode = talentNodes.value.find(n => n.id === link.source);
                const targetNode = talentNodes.value.find(n => n.id === link.target);

                if (sourceNode && targetNode) {
                    link.active = sourceNode.desc.active && targetNode.desc.active;
                } else {
                    link.active = false;
                }
            });

            console.log('节点active状态更新完成');
        }
        // 获取futureEngine graph的ID
        const getFutureEngineGraphId = async () => {
            try {
                // 通过graphService获取futureEngine graph的ID
                const graphId = await graphService.findFutureEngineGraphId();
                console.log('findFutureEngineGraphId graph ID:', graphId);
                return graphId || -1; // 如果获取不到，返回默认ID
            } catch (error) {
                console.error('获取futureEngine graph ID时发生错误:', error);
                return -1; // 默认ID
            }
        };

        // 获取futureStudy graph的ID
        const getFutureStudyGraphId = async () => {
            try {
                // 通过graphService获取futureStudy graph的ID
                const graphId = await graphService.findFutureStudyGraphId();
                console.log('findFutureStudyGraphId graph ID:', graphId);
                return graphId || -1; // 如果获取不到，返回默认ID
            } catch (error) {
                console.error('获取futureStudy graph ID时发生错误:', error);
                return -1; // 默认ID
            }
        };

        // 获取futurePractice graph的ID
        const getFuturePracticeGraphId = async () => {
            try {
                // 通过graphService获取futurePractice graph的ID
                const graphId = await graphService.findFuturePracticeGraphId();
                console.log('findFuturePracticeGraphId graph ID:', graphId);
                return graphId || -1; // 如果获取不到，返回默认ID
            } catch (error) {
                console.error('获取futurePractice graph ID时发生错误:', error);
                return -1; // 默认ID
            }
        };

        // 加载学习节点数据
        const loadFutureStudyData = async (nodeId = null) => {
            try {
                const response = await graphService.findFutureStudyData(nodeId);
                if (response.status === 200) {
                    // 更新studyNodes
                    if (response.data.studyNodes && Array.isArray(response.data.studyNodes)) {
                        studyNodes.value = response.data.studyNodes;
                    }
                    // 如果提供了nodeId，更新selectedNodeStudyRelations
                    // if (nodeId && response.data.links && Array.isArray(response.data.links)) {
                    //     selectedNodeStudyRelations.value = response.data.links;
                    // }
                } else {
                    console.error('加载FutureStudy数据失败:', response.message);
                }
            } catch (error) {
                console.error('加载FutureStudy数据时发生错误:', error);
            }
        };        // 加载练习节点数据
        const loadFuturePracticeData = async (nodeId = null) => {
            try {
                const response = await graphService.findFuturePracticeData(nodeId);
                if (response.status === 200) {
                    console.log('FuturePractice数据:', response.data);
                    // 更新practiceNodes
                    if (response.data.practiceNodes && Array.isArray(response.data.practiceNodes)) {
                        practiceNodes.value = response.data.practiceNodes;
                    }
                    // 更新practiceStars数据
                    if (response.data.practiceStars) {
                        // 将practiceStars数据添加到每个练习节点中
                        practiceNodes.value = practiceNodes.value.map(node => ({
                            ...node,
                            stars: response.data.practiceStars[node.id] || 0
                        }));
                    }
                    // 如果提供了nodeId，更新selectedNodePracticeRelations
                    // if (nodeId && response.data.links && Array.isArray(response.data.links)) {
                    //     selectedNodePracticeRelations.value = response.data.links;
                    // }
                } else {
                    console.error('加载FuturePractice数据失败:', response.message);
                }
            } catch (error) {
                console.error('加载FuturePractice数据时发生错误:', error);
            }
        };

        // 自动计算并更新节点的stars值
        const autoCalculateAndUpdateStars = async (node) => {
            // 获取原节点的stars值 (a)
            const originalStars = nodeStars.value[node.id] || 0;

            // 计算练习节点的stars总和 (b)
            let practiceStarsSum = 0;
            if (practiceNodes.value && Array.isArray(practiceNodes.value)) {
                practiceStarsSum = practiceNodes.value.reduce((sum, practiceNode) => {
                    return sum + (practiceNode.stars || 0);
                }, 0);
            }

            // 计算学习节点的stars值总和 (c)
            let studyStarsSum = 0;
            if (studyNodes.value && Array.isArray(studyNodes.value)) {
                studyStarsSum = studyNodes.value.reduce((sum, studyNode) => {
                    const parsedDesc = parseNodeDesc(studyNode.desc);
                    return sum + (parsedDesc.stars || 0);
                }, 0);
            }

            // 计算新stars值 d = max(a, int((b+c)/2))
            const calculatedStars = Math.max(
                originalStars,
                Math.floor((practiceStarsSum + studyStarsSum) / 2)
            );

            // 如果计算结果大于当前stars值，则自动调用submitRating
            if (calculatedStars > originalStars) {
                // 设置评分相关参数
                selectedNodeForRating.value = node;

                // 确保selectedUser已设置，如果没有则尝试从AuthService获取当前用户
                if (!selectedUser.value) {
                    const currentUser = AuthService.getCurrentUser();
                    if (currentUser) {
                        // 根据从其他组件看到的用户对象结构，构建合适的用户对象
                        selectedUser.value = {
                            id: currentUser.userid || currentUser.id || 1,
                            username: currentUser.nickname || currentUser.username || '默认用户',
                            nickname: currentUser.nickname || currentUser.username || '默认用户',
                            userType: currentUser.userType || 1
                        };
                    } else {
                        // 如果无法获取当前用户，使用默认用户（ID为1）
                        selectedUser.value = {
                            id: 1,
                            username: '默认用户',
                            nickname: '默认用户',
                            userType: 1
                        };
                    }
                }

                // 设置新的stars值
                stars.value = calculatedStars;

                // 调用submitRating函数
                await submitRating();

                // // 重新加载当前节点相关的数据以确保界面更新
                // await loadFutureStudyData(node.id);
                // await loadFuturePracticeData(node.id);
            }
        };

        // 快捷键提示面板显示状态
        const showShortcutHelp = ref(false);

        // 进程监控相关数据
        const showProcessMonitor = ref(false);
        const runningProcesses = ref([]);
        const processStats = ref({
            totalNodes: 0,
            displayedNodes: 0,
            totalLinks: 0,
            renderedLinks: 0,
            memoryUsage: 0,
            renderTime: 0
        });

        // 进程监控悬浮窗拖动相关数据
        const processMonitorPosition = ref({ x: 20, y: 100 }); // 默认位置
        const isProcessMonitorDragging = ref(false);
        const processMonitorDragOffset = ref({ x: 0, y: 0 });
        const isProcessMonitorMinimized = ref(false); // 是否最小化

        // 测试节点相关数据
        const testNodeCount = ref(5); // 默认测试节点数量

        // 用于存储临时节点和链接的ID计数器
        let tempNodeIdCounter = 10000; // 使用较大ID避免与现有节点冲突
        let tempLinkIdCounter = 10000; // 使用较大ID避免与现有链接冲突

        // 开始拖动进程监控窗
        // eslint-disable-next-line no-unused-vars
        const startProcessMonitorDrag = (e) => {
            if (isProcessMonitorMinimized.value) return; // 最小化时不能拖动

            isProcessMonitorDragging.value = true;

            // 计算鼠标相对于元素的偏移
            const rect = e.target.getBoundingClientRect();
            if (e.type === 'mousedown') {
                processMonitorDragOffset.value = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };
            } else if (e.type === 'touchstart') {
                const touch = e.touches[0];
                processMonitorDragOffset.value = {
                    x: touch.clientX - rect.left,
                    y: touch.clientY - rect.top
                };
            }

            // 防止默认行为
            e.preventDefault();

            // 添加事件监听器
            document.addEventListener('mousemove', handleProcessMonitorDrag);
            document.addEventListener('mouseup', stopProcessMonitorDrag);
            document.addEventListener('touchmove', handleProcessMonitorDrag, { passive: false });
            document.addEventListener('touchend', stopProcessMonitorDrag);
        };

        // 开始拖动进程监控窗（旧函数，保持兼容性）
        const startDrag = (e) => {
            if (isProcessMonitorMinimized.value) return; // 最小化时不能拖动

            isProcessMonitorDragging.value = true;

            // 计算鼠标相对于元素的偏移
            const rect = e.target.getBoundingClientRect();
            if (e.type === 'mousedown') {
                processMonitorDragOffset.value = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };
            } else if (e.type === 'touchstart') {
                const touch = e.touches[0];
                processMonitorDragOffset.value = {
                    x: touch.clientX - rect.left,
                    y: touch.clientY - rect.top
                };
            }

            // 防止默认行为
            e.preventDefault();

            // 添加事件监听器
            document.addEventListener('mousemove', handleProcessMonitorDrag);
            document.addEventListener('mouseup', stopProcessMonitorDrag);
            document.addEventListener('touchmove', handleProcessMonitorDrag, { passive: false });
            document.addEventListener('touchend', stopProcessMonitorDrag);
        };

        // 拖动中
        const handleProcessMonitorDrag = (e) => {
            if (!isProcessMonitorDragging.value) return;

            let clientX, clientY;
            if (e.type === 'mousemove') {
                clientX = e.clientX;
                clientY = e.clientY;
            } else if (e.type === 'touchmove') {
                const touch = e.touches[0];
                clientX = touch.clientX;
                clientY = touch.clientY;
            }

            if (clientX !== undefined && clientY !== undefined) {
                processMonitorPosition.value = {
                    x: clientX - processMonitorDragOffset.value.x,
                    y: clientY - processMonitorDragOffset.value.y
                };

                // 限制在屏幕范围内
                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;
                const elementWidth = 300; // 悬浮窗宽度
                const elementHeight = 400; // 悬浮窗高度

                processMonitorPosition.value.x = Math.max(0, Math.min(processMonitorPosition.value.x, windowWidth - elementWidth));
                processMonitorPosition.value.y = Math.max(0, Math.min(processMonitorPosition.value.y, windowHeight - elementHeight));
            }
        };

        // 停止拖动
        const stopProcessMonitorDrag = () => {
            isProcessMonitorDragging.value = false;

            // 移除事件监听器
            document.removeEventListener('mousemove', handleProcessMonitorDrag);
            document.removeEventListener('mouseup', stopProcessMonitorDrag);
            document.removeEventListener('touchmove', handleProcessMonitorDrag);
            document.removeEventListener('touchend', stopProcessMonitorDrag);
        };

        // 最小化进程监控窗
        const minimizeProcessMonitor = () => {
            isProcessMonitorMinimized.value = !isProcessMonitorMinimized.value;
        };

        // 添加测试节点和链接
        const addTestNodes = () => {
            const count = parseInt(testNodeCount.value) || 5;
            if (count < 1 || count > 100) {
                alert('节点数量必须在1到100之间');
                return;
            }

            // 计算当前视口范围，以便将新节点添加到可见区域
            const { minX, maxX, minY, maxY } = calculateViewportRange();

            // 创建临时节点
            for (let i = 0; i < count; i++) {
                const nodeId = `temp_node_${tempNodeIdCounter++}`;
                // 在视口范围内随机生成位置
                const x = Math.random() * (maxX - minX - 100) + minX + 50; // 减去节点大小并加上边距
                const y = Math.random() * (maxY - minY - 100) + minY + 50;

                const newNode = {
                    id: nodeId,
                    name: `测试节点 ${nodeId.split('_')[2]}`,
                    desc: {
                        position: {
                            x: x,
                            y: y
                        },
                        size: 80,
                        shape: 'circle',
                        borderColor: '#007bff',
                        backgroundColor: '#e7f3ff',
                        style: {
                            borderStyle: 'solid',
                            borderWidth: 2,
                            opacity: 1
                        }
                    },
                    active: true,
                    temp: true // 标记为临时节点
                };

                // 将节点添加到临时存储中，不保存到数据库
                talentNodes.value.push(newNode);
            }

            // 随机创建一些连接
            const nodes = talentNodes.value.filter(node => node.temp);
            if (nodes.length > 1) {
                for (let i = 0; i < nodes.length - 1; i++) {
                    // 有一定概率创建连接
                    if (Math.random() > 0.3) { // 70%概率创建连接
                        const linkId = `temp_link_${tempLinkIdCounter++}`;
                        const newLink = {
                            id: linkId,
                            source: nodes[i].id,
                            target: nodes[i + 1].id,
                            fromId: nodes[i].id,
                            toId: nodes[i + 1].id,
                            value: 2,
                            style: {
                                stroke: '#000000',
                                lineStyle: 'solid',
                                lineType: Math.random() > 0.5 ? 'straight' : 'sCurve',
                                cornerRadius: 10
                            },
                            temp: true // 标记为临时链接
                        };

                        // 将链接添加到临时存储中，不保存到数据库
                        links.value.push(newLink);
                    }
                }
            }

            // 强制更新显示
            forceUpdateDisplayedNodes();

            // 添加测试节点后需要重建四叉树
            quadTreeNeedsRebuild = true;

            console.log(`添加了 ${count} 个测试节点和相关连接`);
        };

        // 切换快捷键提示面板显示状态
        const toggleShortcutHelp = () => {
            showShortcutHelp.value = !showShortcutHelp.value;
        };

        // 评分功能相关数据
        const showRatingModal = ref(false);
        const nodeSearchText = ref('');
        const userSearchText = ref('');
        const selectedNodeForRating = ref(null);
        const selectedUser = ref(null);
        const stars = ref(0);
        const remark = ref('');
        const result = ref(false);
        const allUsers = ref([]);
        // 新增：题目相关数据
        const selectedQuestion = ref(null);

        // 布置作业功能相关数据
        const showHomeworkModal = ref(false);
        const homeworkNodeSearchText = ref('');
        const homeworkUserSearchText = ref('');
        const selectedNodeForHomework = ref(null);
        const selectedUserForHomework = ref(null);
        const homeworkQuestions = ref([]);
        const homeworkTasks = ref([]); // 任务列表

        // 用户切换功能相关数据
        const showUserSwitcher = ref(false);
        const userSwitchSearchText = ref('');
        const selectedUserForView = ref(null);
        // allUsers 已经在评分功能相关数据中定义过了

        // 设置功能相关数据
        const showSettingsModal = ref(false);
        const displayMode = ref('active'); // 'all' 或 'active' - 默认设置为仅显示激活节点

        // 用户设置的默认样式（保存到localStorage）
        const savedDefaultNodeStyle = localStorage.getItem('defaultNodeStyle') ?
            JSON.parse(localStorage.getItem('defaultNodeStyle')) : {
                backgroundColor: '#ffffff',
                borderColor: '#000000',
                shape: 'circle',
                size: 80,
                borderStyle: 'solid',
                borderWidth: 2,
                opacity: 1
            };

        const savedDefaultLineStyle = localStorage.getItem('defaultLineStyle') ?
            JSON.parse(localStorage.getItem('defaultLineStyle')) : {
                stroke: '#000000',
                lineStyle: 'solid',
                lineType: 'straight',
                cornerRadius: 10
            };

        const defaultNodeStyle = reactive(savedDefaultNodeStyle);
        const defaultLineStyle = reactive(savedDefaultLineStyle);

        // 预设样式相关数据
        const presetName = ref('');
        const selectedPreset = ref('');

        // 节点和连接编辑时的预设样式选择
        const selectedNodePreset = ref('');
        const selectedLinkPreset = ref('');

        // 从localStorage加载预设样式
        const savedPresetStyles = localStorage.getItem('presetStyles') ?
            JSON.parse(localStorage.getItem('presetStyles')) : [];
        const presetStyles = ref(savedPresetStyles);

        // 搜索功能相关数据
        const searchQuery = ref('');
        const searchResults = ref([]);
        const showSearchResultsList = ref(false);
        // 新增：用于存储搜索匹配节点的响应式变量
        const searchMatchedNodes = ref(new Set());

        // 添加：用于跟踪临时激活模式的状态变量
        const isTemporarilyActivated = ref(false);

        // 更新进程监控数据
        const updateProcessMonitorData = () => {
            // 记录开始时间
            const startTime = performance.now();

            processStats.value = {
                totalNodes: talentNodes.value.length,
                displayedNodes: displayedNodes.value.length,
                totalLinks: links.value.length,
                renderedLinks: links.value.filter(link => shouldShowLink(link)).length,
                memoryUsage: Math.round((performance.memory ? performance.memory.usedJSHeapSize / 1048576 : 0) * 100) / 100, // MB
                renderTime: performanceMonitor.renderCount > 0 ? Math.round(performanceMonitor.lastRenderTime * 100) / 100 : 0
            };

            // 模拟一些运行中的进程
            runningProcesses.value = [
                { id: 1, name: '节点数据加载', status: 'running', progress: 100, lastUpdate: new Date().toLocaleTimeString() },
                { id: 2, name: '连接线渲染', status: 'running', progress: 100, lastUpdate: new Date().toLocaleTimeString() },
                { id: 3, name: '布局计算', status: 'idle', progress: 100, lastUpdate: new Date().toLocaleTimeString() },
                { id: 4, name: '用户交互处理', status: 'running', progress: 100, lastUpdate: new Date().toLocaleTimeString() }
            ];

            // 如果是管理员，添加更多进程
            if (user.value.userType === 1) {
                runningProcesses.value.push(
                    { id: 5, name: '数据同步', status: 'running', progress: 85, lastUpdate: new Date().toLocaleTimeString() },
                    { id: 6, name: '后台任务处理', status: 'pending', progress: 30, lastUpdate: new Date().toLocaleTimeString() }
                );
            }

            // 计算处理时间
            const endTime = performance.now();
            console.log(`Process monitor update time: ${endTime - startTime}ms`);
        };

        // 定时更新进程监控数据
        const processMonitorInterval = setInterval(() => {
            if (showProcessMonitor.value) {
                updateProcessMonitorData();
            }
        }, 5000); // 每5秒更新一次，减少性能开销

        // 组件卸载时清理定时器
        onUnmounted(() => {
            if (processMonitorInterval) {
                clearInterval(processMonitorInterval);
            }
            // 清理事件监听器
            document.removeEventListener('mousemove', handleProcessMonitorDrag);
            document.removeEventListener('mouseup', stopProcessMonitorDrag);
            document.removeEventListener('touchmove', handleProcessMonitorDrag);
            document.removeEventListener('touchend', stopProcessMonitorDrag);
        });
        // 添加：用于保存节点原始active状态的映射
        const originalNodeActiveStates = ref({});

        // 添加右键菜单相关的响应式数据
        const contextMenu = ref({
            visible: false,
            x: 0,
            y: 0
        });

        // 计算右键菜单的最佳位置，确保菜单在屏幕可视区域内
        const calculateContextMenuPosition = (clientX, clientY) => {
            // 预估菜单尺寸（根据CSS样式和菜单项数量估算）
            const menuWidth = 180; // 根据.context-menu的min-width: 150px以及可能的边距和图标估算
            const menuItemHeight = 32; // 每个菜单项大约的高度（padding: 8px 12px + icon + text）
            const menuPadding = 4; // 上下padding
            const menuItemCount = 12; // 菜单项的数量（根据HTML结构估算）
            const menuHeight = menuPadding * 2 + menuItemCount * menuItemHeight; // 总高度

            // 获取视口尺寸
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // 计算屏幕的中心线
            const verticalCenter = viewportHeight / 2;

            // 根据点击位置相对于屏幕中心的位置来确定菜单位置
            let finalX, finalY;

            // 水平位置：优先放在右侧，如果右侧超出则放在左侧
            if (clientX + menuWidth <= viewportWidth) {
                // 右侧有足够空间，放在右侧
                finalX = clientX;
            } else {
                // 右侧空间不足，放在左侧
                finalX = Math.max(0, clientX - menuWidth);
            }

            // 垂直位置：如果在屏幕上方，菜单放在下方；如果在屏幕下方，菜单放在上方
            if (clientY < verticalCenter) {
                // 点击位置在屏幕上方，优先放在下方
                const potentialY = clientY;
                if (potentialY + menuHeight <= viewportHeight) {
                    // 下方有足够空间，放在下方
                    finalY = potentialY;
                } else {
                    // 下方空间不足，调整到上方
                    finalY = Math.max(0, clientY - menuHeight);
                }
            } else {
                // 点击位置在屏幕下方，优先放在上方
                const potentialY = clientY - menuHeight;
                if (potentialY >= 0) {
                    // 上方有足够空间，放在上方
                    finalY = potentialY;
                } else {
                    // 上方空间不足，放在下方但确保不超出屏幕
                    finalY = Math.max(0, clientY);
                }
            }

            // 确保最终位置在视口范围内
            finalX = Math.max(0, Math.min(finalX, viewportWidth - menuWidth));
            finalY = Math.max(0, Math.min(finalY, viewportHeight - menuHeight));

            return { x: finalX, y: finalY };
        };

        // 添加子菜单显示状态
        const showGroupSubMenu = ref(false);
        const showTagSubMenu = ref(false); // 添加标签子菜单显示状态

        // 二级菜单的ref引用
        const groupSubmenuRef = ref(null);
        const tagSubmenuRef = ref(null);

        // 计算二级菜单位置的函数
        const calculateSubmenuPosition = (submenuElement, parentElement) => {
            if (!submenuElement || !parentElement) {
                return { left: '100%', top: '0px' }; // 默认位置
            }

            // 获取父菜单和子菜单的边界信息
            const parentRect = parentElement.getBoundingClientRect();
            const submenuRect = submenuElement.getBoundingClientRect();

            // 获取视口尺寸
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // 计算屏幕中心线
            const verticalCenter = viewportHeight / 2;
            const horizontalCenter = viewportWidth / 2;

            // 确定垂直方向：如果父菜单在屏幕上方，则子菜单向下显示；如果在屏幕下方，则子菜单向上显示
            let topPosition;
            if (parentRect.top < verticalCenter) {
                // 父菜单在屏幕上方，子菜单显示在下方
                topPosition = '0px';
                // 检查是否超出屏幕下边界
                if (parentRect.bottom + submenuRect.height > viewportHeight) {
                    // 如果超出下边界，则调整为向上显示
                    topPosition = `-${submenuRect.height - 5}px`;
                }
            } else {
                // 父菜单在屏幕下方，子菜单显示在上方
                topPosition = `-${submenuRect.height - 50}px`;
                // 检查是否超出屏幕上边界
                if (parentRect.top - submenuRect.height < 0) {
                    // 如果超出上边界，则调整为向下显示
                    topPosition = '0px';
                }
            }

            // 确定水平方向：如果父菜单在屏幕左侧，则子菜单向右显示；如果在屏幕右侧，则子菜单向左显示
            let leftPosition;
            if (parentRect.left < horizontalCenter) {
                // 父菜单在屏幕左侧，子菜单显示在右侧
                leftPosition = '100%';
                // 检查是否超出屏幕右边界
                if (parentRect.right + submenuRect.width > viewportWidth) {
                    // 如果超出右边界，则调整为向左显示
                    leftPosition = `-${submenuRect.width - 5}px`;
                }
            } else {
                // 父菜单在屏幕右侧，子菜单显示在左侧
                leftPosition = `-${submenuRect.width - 5}px`;
                // 检查是否超出屏幕左边界
                if (parentRect.left - submenuRect.width < 0) {
                    // 如果超出左边界，则调整为向右显示
                    leftPosition = '100%';
                }
            }

            return { left: leftPosition, top: topPosition };
        };

        // 显示带位置的群组子菜单
        const showGroupSubmenuWithPosition = (e) => {
            // 显示菜单以获取其尺寸
            showGroupSubMenu.value = true;

            // 使用nextTick确保DOM已更新
            nextTick(() => {
                // 获取触发二级菜单的父元素
                const parentElement = e.target.closest('.submenu-trigger');
                if (parentElement && groupSubmenuRef.value) {
                    // 计算并设置位置
                    const position = calculateSubmenuPosition(groupSubmenuRef.value, parentElement);

                    // 应用位置到子菜单
                    if (groupSubmenuRef.value) {
                        groupSubmenuRef.value.style.left = position.left;
                        groupSubmenuRef.value.style.top = position.top;
                    }

                    updateGroupMenu();
                }
            });
        };

        // 显示带位置的标签子菜单
        const showTagSubmenuWithPosition = (e) => {
            // 显示菜单以获取其尺寸
            showTagSubMenu.value = true;

            // 使用nextTick确保DOM已更新
            nextTick(() => {
                // 获取触发二级菜单的父元素
                const parentElement = e.target.closest('.submenu-trigger');
                if (parentElement && tagSubmenuRef.value) {
                    // 计算并设置位置
                    const position = calculateSubmenuPosition(tagSubmenuRef.value, parentElement);

                    // 应用位置到子菜单
                    if (tagSubmenuRef.value) {
                        tagSubmenuRef.value.style.left = position.left;
                        tagSubmenuRef.value.style.top = position.top;
                    }

                    updateTagMenu();
                }
            });
        };

        // 用于存储右键点击的节点
        const selectedNodeForContextMenu = ref(null);

        // 计算当前视口范围
        const calculateViewportRange = () => {
            // 获取当前视口的尺寸和位置
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // 考虑缩放因子的影响，计算实际可视范围
            const scaledViewportWidth = viewportWidth / scale.value;
            const scaledViewportHeight = viewportHeight / scale.value;

            // 计算1.5倍视口范围
            const extendedWidth = scaledViewportWidth * 1.5;
            const extendedHeight = scaledViewportHeight * 1.5;

            // 计算视口中心点
            const centerX = -currentPanOffset.value.x + scaledViewportWidth / 2;
            const centerY = -currentPanOffset.value.y + scaledViewportHeight / 2;

            // 计算扩展范围的边界
            const minX = centerX - extendedWidth / 2;
            const maxX = centerX + extendedWidth / 2;
            const minY = centerY - extendedHeight / 2;
            const maxY = centerY + extendedHeight / 2;

            return { minX, maxX, minY, maxY };
        };

        // 添加节点优先级分类函数
        const prioritizeNodes = (nodes) => {
            // 根据节点的重要性和连接关系进行优先级排序
            return nodes.sort((a, b) => {
                // 优先显示激活的节点
                if (a.desc.active && !b.desc.active) return -1;
                if (!a.desc.active && b.desc.active) return 1;

                // 优先显示有连接关系的节点
                const aLinks = links.value.filter(link => link.source === a.id || link.target === a.id).length;
                const bLinks = links.value.filter(link => link.source === b.id || link.target === b.id).length;

                return bLinks - aLinks;
            });
        };

        // 限制最大渲染节点数
        const MAX_RENDERED_NODES = 300;

        // 四叉树节点类
        class QuadTreeNode {
            constructor(x, y, width, height, capacity = 4) {
                this.x = x;
                this.y = y;
                this.width = width;
                this.height = height;
                this.capacity = capacity;
                this.nodes = [];
                this.divided = false;

                // 子象限
                this.northeast = null;
                this.northwest = null;
                this.southeast = null;
                this.southwest = null;
            }

            contains(point) {
                const left = this.x - this.width / 2;
                const right = this.x + this.width / 2;
                const top = this.y - this.height / 2;
                const bottom = this.y + this.height / 2;

                return (point.x >= left &&
                    point.x <= right &&
                    point.y >= top &&
                    point.y <= bottom);
            }

            intersects(range) {
                const left = this.x - this.width / 2;
                const right = this.x + this.width / 2;
                const top = this.y - this.height / 2;
                const bottom = this.y + this.height / 2;

                const rleft = range.x - range.width / 2;
                const rright = range.x + range.width / 2;
                const rtop = range.y - range.height / 2;
                const rbottom = range.y + range.height / 2;

                return !(rleft > right ||
                    rright < left ||
                    rtop > bottom ||
                    rbottom < top);
            }

            subdivide() {
                const halfWidth = this.width / 2;
                const halfHeight = this.height / 2;
                const x = this.x;
                const y = this.y;

                this.northeast = new QuadTreeNode(x + halfWidth / 2, y - halfHeight / 2, halfWidth, halfHeight, this.capacity);
                this.northwest = new QuadTreeNode(x - halfWidth / 2, y - halfHeight / 2, halfWidth, halfHeight, this.capacity);
                this.southeast = new QuadTreeNode(x + halfWidth / 2, y + halfHeight / 2, halfWidth, halfHeight, this.capacity);
                this.southwest = new QuadTreeNode(x - halfWidth / 2, y + halfHeight / 2, halfWidth, halfHeight, this.capacity);

                this.divided = true;
            }

            insert(node) {
                // 检查节点是否有有效的位置
                if (!node.desc?.position) {
                    console.warn('Node has no position, skipping insert:', node);
                    return false;
                }

                if (!this.contains({ x: node.desc.position.x, y: node.desc.position.y })) {
                    return false;
                }

                if (this.nodes.length < this.capacity && !this.divided) {
                    this.nodes.push(node);
                    return true;
                }

                if (!this.divided) {
                    this.subdivide();
                }

                if (this.northeast.insert(node) ||
                    this.northwest.insert(node) ||
                    this.southeast.insert(node) ||
                    this.southwest.insert(node)) {
                    return true;
                }

                return false;
            }

            query(range, found = []) {
                if (!this.intersects(range)) {
                    return found;
                }

                for (let node of this.nodes) {
                    if (range.contains && range.contains({ x: node.desc.position.x, y: node.desc.position.y })) {
                        found.push(node);
                    }
                }

                if (this.divided) {
                    this.northeast.query(range, found);
                    this.northwest.query(range, found);
                    this.southeast.query(range, found);
                    this.southwest.query(range, found);
                }

                return found;
            }
        }

        // 创建四叉树实例
        let quadTree = null;
        let quadTreeNeedsRebuild = true;

        // 构建四叉树
        const buildQuadTree = () => {
            // 计算边界框
            let minX = Infinity, maxX = -Infinity;
            let minY = Infinity, maxY = -Infinity;

            for (const node of talentNodes.value) {
                if (node.desc?.position) {
                    minX = Math.min(minX, node.desc.position.x);
                    maxX = Math.max(maxX, node.desc.position.x);
                    minY = Math.min(minY, node.desc.position.y);
                    maxY = Math.max(maxY, node.desc.position.y);
                }
            }

            if (minX === Infinity) return null; // 没有有效节点

            // 确保边界有效
            if (minX === maxX) maxX = minX + 100;
            if (minY === maxY) maxY = minY + 100;

            // 创建四叉树，稍微扩大边界以包含所有节点
            const width = maxX - minX + 400; // 增加边界以确保包含所有节点
            const height = maxY - minY + 400;
            const centerX = minX + width / 2;
            const centerY = minY + height / 2;

            quadTree = new QuadTreeNode(centerX, centerY, width, height, 8);

            // 插入所有节点
            let insertedNodes = 0;
            for (const node of talentNodes.value) {
                if (node.desc?.position) {
                    if (quadTree.insert(node)) {
                        insertedNodes++;
                    }
                }
            }

            console.log(`QuadTree built: ${insertedNodes} nodes inserted out of ${talentNodes.value.length} total nodes`);

            quadTreeNeedsRebuild = false;
        };

        // LOD（细节层次）相关
        // eslint-disable-next-line no-unused-vars
        const getLODDetailLevel = () => {
            // 根据缩放级别确定细节层次
            if (scale.value > 2) return 'high';
            if (scale.value > 1) return 'medium';
            return 'low';
        };

        // 渲染控制相关
        let lastPanTime = 0;
        let isPanningRecently = false;

        // 监听画布拖动状态
        watch(isPanning, (newVal) => {
            if (newVal) {
                isPanningRecently = true;
                lastPanTime = Date.now();
            }
        });

        // 定期检查是否刚结束拖动
        setInterval(() => {
            if (isPanningRecently && (Date.now() - lastPanTime) > 300) { // 300ms后认为停止拖动
                isPanningRecently = false;
            }
        }, 100);

        // 优化后的计算属性，添加缓存机制以提高性能
        const displayedNodes = computed(() => {

            // 如果需要重建四叉树，则重建
            if (quadTreeNeedsRebuild) {
                buildQuadTree();
            }

            // 计算当前视口范围
            const { minX, maxX, minY, maxY } = calculateViewportRange();

            // 创建查询范围
            const queryRange = {
                x: (minX + maxX) / 2,
                y: (minY + maxY) / 2,
                width: maxX - minX,
                height: maxY - minY,
                contains: function (point) {
                    return (point.x >= minX && point.x <= maxX &&
                        point.y >= minY && point.y <= maxY);
                }
            };

            let filteredNodes = [];

            // 使用四叉树进行空间查询
            if (quadTree) {
                try {
                    const quadTreeNodes = quadTree.query(queryRange);

                    // 根据显示模式过滤节点
                    if (displayMode.value === 'all') {
                        // 显示所有节点
                        filteredNodes = quadTreeNodes;
                    } else if (displayMode.value === 'active') {
                        // 仅显示激活的节点
                        filteredNodes = quadTreeNodes.filter(node => node.desc?.active);
                    } else {
                        // 默认显示所有节点
                        filteredNodes = quadTreeNodes;
                    }
                } catch (error) {
                    console.error('四叉树查询出错，使用回退方案:', error);
                    // 出错时使用回退方案
                    const totalNodes = talentNodes.value;
                    for (let i = 0; i < totalNodes.length; i++) {
                        const node = totalNodes[i];
                        if (!node.desc?.position) continue;

                        const nodeX = node.desc.position.x;
                        const nodeY = node.desc.position.y;
                        const nodeSize = node.desc.size || 80;

                        // 检查节点是否在扩展范围内 - 优化边界检查
                        const rightEdge = nodeX + nodeSize;
                        const bottomEdge = nodeY + nodeSize;

                        if (rightEdge >= minX &&
                            nodeX <= maxX &&
                            bottomEdge >= minY &&
                            nodeY <= maxY) {
                            filteredNodes.push(node);
                        }
                    }
                }
            } else {
                // 回退到线性搜索
                const totalNodes = talentNodes.value;

                if (displayMode.value === 'all') {
                    // 显示所有节点中在屏幕1.5倍范围内的节点
                    for (let i = 0; i < totalNodes.length; i++) {
                        const node = totalNodes[i];
                        if (!node.desc?.position) continue;

                        const nodeX = node.desc.position.x;
                        const nodeY = node.desc.position.y;
                        const nodeSize = node.desc.size || 80;

                        // 检查节点是否在扩展范围内 - 优化边界检查
                        const rightEdge = nodeX + nodeSize;
                        const bottomEdge = nodeY + nodeSize;

                        if (rightEdge >= minX &&
                            nodeX <= maxX &&
                            bottomEdge >= minY &&
                            nodeY <= maxY) {
                            filteredNodes.push(node);
                        }
                    }
                } else if (displayMode.value === 'active') {
                    // 仅显示激活的节点中在屏幕1.5倍范围内的节点
                    for (let i = 0; i < totalNodes.length; i++) {
                        const node = totalNodes[i];
                        if (!node.desc?.active || !node.desc?.position) continue;

                        const nodeX = node.desc.position.x;
                        const nodeY = node.desc.position.y;
                        const nodeSize = node.desc.size || 80;

                        // 检查节点是否在扩展范围内 - 优化边界检查
                        const rightEdge = nodeX + nodeSize;
                        const bottomEdge = nodeY + nodeSize;

                        if (rightEdge >= minX &&
                            nodeX <= maxX &&
                            bottomEdge >= minY &&
                            nodeY <= maxY) {
                            filteredNodes.push(node);
                        }
                    }
                } else {
                    // 默认显示所有节点中在屏幕1.5倍范围内的节点
                    for (let i = 0; i < totalNodes.length; i++) {
                        const node = totalNodes[i];
                        if (!node.desc?.position) continue;

                        const nodeX = node.desc.position.x;
                        const nodeY = node.desc.position.y;
                        const nodeSize = node.desc.size || 80;

                        // 检查节点是否在扩展范围内 - 优化边界检查
                        const rightEdge = nodeX + nodeSize;
                        const bottomEdge = nodeY + nodeSize;

                        if (rightEdge >= minX &&
                            nodeX <= maxX &&
                            bottomEdge >= minY &&
                            nodeY <= maxY) {
                            filteredNodes.push(node);
                        }
                    }
                }
            }

            // 如果节点数量超过限制，进行优先级排序并截取
            if (filteredNodes.length > MAX_RENDERED_NODES) {
                filteredNodes = prioritizeNodes([...filteredNodes]).slice(0, MAX_RENDERED_NODES);
            }

            // console.log(`Node filtering time: ${endTime - startTime}ms, Nodes: ${filteredNodes.length}`);

            return filteredNodes;
        });

        // 添加渲染性能优化
        // 优化连接线渲染
        const visibleLinks = computed(() => {

            // 只渲染与可见节点相关的连接线
            const visibleNodeIds = new Set(displayedNodes.value.map(node => node.id));

            let result = [];
            const totalLinks = links.value;

            // 使用循环而不是filter，提高性能
            for (let i = 0; i < totalLinks.length; i++) {
                const link = totalLinks[i];
                // 检查连接线两端节点是否在可见范围内 - 修改为只要有一个节点在视野范围内就显示连接线
                if ((visibleNodeIds.has(link.source) || visibleNodeIds.has(link.target)) && shouldShowLink(link)) {
                    result.push(link);
                }
            }

            // console.log(`Link filtering time: ${endTime - startTime}ms, Links: ${result.length}`);

            return result;
        });

        // 打开评分弹窗
        const openRatingModal = () => {
            showRatingModal.value = true;
            // 加载所有用户数据
            // loadAllUsers();
        };

        // 打开设置弹窗
        const openSettingsModal = () => {
            showSettingsModal.value = true;
        };

        // 关闭设置弹窗
        const closeSettingsModal = () => {
            showSettingsModal.value = false;
        };

        // 应用设置
        const applySettings = () => {
            // 保存到localStorage
            localStorage.setItem('defaultNodeStyle', JSON.stringify({ ...defaultNodeStyle }));
            localStorage.setItem('defaultLineStyle', JSON.stringify({ ...defaultLineStyle }));

            console.log('应用设置:', {
                displayMode: displayMode.value,
                defaultNodeStyle: { ...defaultNodeStyle },
                defaultLineStyle: { ...defaultLineStyle }
            });

            // 关闭设置弹窗
            closeSettingsModal();

            // 可以在这里添加提示信息
            alert('设置已应用');
        };

        // 保存预设样式
        const savePresetStyle = () => {
            if (!presetName.value.trim()) {
                alert('请输入预设样式名称');
                return;
            }

            // 创建预设样式对象
            const newPreset = {
                name: presetName.value.trim(),
                nodeStyle: { ...defaultNodeStyle },
                lineStyle: { ...defaultLineStyle }
            };

            // 添加到预设样式列表
            presetStyles.value.push(newPreset);

            // 保存到localStorage
            localStorage.setItem('presetStyles', JSON.stringify(presetStyles.value));

            // 清空输入框
            presetName.value = '';

            alert('预设样式已保存');
        };

        // 应用预设样式
        const applyPresetStyle = () => {
            if (selectedPreset.value === '') return;

            const preset = presetStyles.value[selectedPreset.value];
            if (preset) {
                // 应用节点样式
                Object.assign(defaultNodeStyle, preset.nodeStyle);
                // 应用线条样式
                Object.assign(defaultLineStyle, preset.lineStyle);

                alert('预设样式已应用');
            }
        };

        // 删除预设样式
        const deletePresetStyle = () => {
            if (selectedPreset.value === '') return;

            if (confirm('确定要删除这个预设样式吗？')) {
                presetStyles.value.splice(selectedPreset.value, 1);
                selectedPreset.value = '';

                // 保存到localStorage
                localStorage.setItem('presetStyles', JSON.stringify(presetStyles.value));

                alert('预设样式已删除');
            }
        };

        // 选择预设样式
        const selectPreset = (index) => {
            selectedPreset.value = index;
        };

        // 节点编辑时应用预设样式
        const applyNodePresetStyle = () => {
            if (selectedNodePreset.value === '') return;

            const preset = presetStyles.value[selectedNodePreset.value];
            if (preset && preset.nodeStyle) {
                // 应用节点样式到当前编辑的节点
                editingNode.shape = preset.nodeStyle.shape;
                editingNode.size = preset.nodeStyle.size;
                editingNode.borderColor = preset.nodeStyle.borderColor;
                editingNode.backgroundColor = preset.nodeStyle.backgroundColor;
                editingNode.style.borderStyle = preset.nodeStyle.borderStyle;
                editingNode.style.borderWidth = preset.nodeStyle.borderWidth;
                editingNode.style.opacity = preset.nodeStyle.opacity;

                alert('节点预设样式应用成功');
            }
        };

        // 连接编辑时应用预设样式
        const applyLinkPresetStyle = () => {
            if (selectedLinkPreset.value === '') return;

            const preset = presetStyles.value[selectedLinkPreset.value];
            if (preset && preset.lineStyle) {
                // 应用线条样式到当前编辑的连接
                editingLink.style.stroke = preset.lineStyle.stroke;
                editingLink.style.lineStyle = preset.lineStyle.lineStyle;
                editingLink.style.lineType = preset.lineStyle.lineType;
                editingLink.style.cornerRadius = preset.lineStyle.cornerRadius;

                alert('连接预设样式应用成功');
            }
        };

        // 关闭评分弹窗
        const closeRatingModal = () => {
            showRatingModal.value = false;
            // 重置数据
            nodeSearchText.value = '';
            userSearchText.value = '';
            selectedNodeForRating.value = null;
            selectedUser.value = null;
            selectedQuestion.value = null; // 重置题目选择
            stars.value = 0;
            remark.value = '';
            result.value = false;
        };

        // 打开布置作业弹窗
        const openHomeworkModal = () => {
            showHomeworkModal.value = true;
            // 加载所有用户数据
            // loadAllUsers();
        };

        // 关闭布置作业弹窗
        const closeHomeworkModal = () => {
            showHomeworkModal.value = false;
            // 重置数据
            homeworkNodeSearchText.value = '';
            homeworkUserSearchText.value = '';
            selectedNodeForHomework.value = null;
            selectedUserForHomework.value = null;
            homeworkQuestions.value = [];
            homeworkTasks.value = [];
        };



        // 跳转到作业查看页面
        const goToHomeworkView = () => {
            // 跳转到作业查看页面
            window.location.href = '/homework';
        };

        // 加载所有用户数据
        const loadAllUsers = async () => {
            try {
                // 调用后端API获取所有用户数据
                const response = await graphService.getAllUsers();
                console.log('获取所有用户数据:', response);
                if (response && Array.isArray(response)) {
                    allUsers.value = response.map(user => ({
                        id: user.id,
                        username: user.username || user.nickname || `用户${user.id}`,  // 使用username或nickname作为username显示
                        nickname: user.nickname || user.username || `用户${user.id}`
                    }));
                } else {
                    // 如果没有获取到数据，使用模拟数据
                    allUsers.value = [
                        { id: 1, username: 'user1', nickname: '用户1' },
                        { id: 2, username: 'user2', nickname: '用户2' },
                        { id: 3, username: 'user3', nickname: '用户3' }
                    ];
                }
            } catch (error) {
                console.error('加载用户数据失败:', error);
                // 出错时使用模拟数据
                allUsers.value = [
                    { id: 1, username: 'user1', nickname: '用户1' },
                    { id: 2, username: 'user2', nickname: '用户2' },
                    { id: 3, username: 'user3', nickname: '用户3' }
                ];
            }
        };
        // 过滤节点
        const filteredNodes = computed(() => {
            if (!nodeSearchText.value) {
                return talentNodes.value;
            }
            return talentNodes.value.filter(node =>
                node.name.toLowerCase().includes(nodeSearchText.value.toLowerCase())
            );
        });

        // 过滤用户
        const filteredUsers = computed(() => {
            if (!userSearchText.value) {
                return allUsers.value;
            }
            return allUsers.value.filter(user =>
                user.username.toLowerCase().includes(userSearchText.value.toLowerCase()) ||
                user.nickname.toLowerCase().includes(userSearchText.value.toLowerCase())
            );
        });

        // 过滤节点用于布置作业
        const filteredHomeworkNodes = computed(() => {
            if (!homeworkNodeSearchText.value) {
                return talentNodes.value;
            }
            return talentNodes.value.filter(node =>
                node.name.toLowerCase().includes(homeworkNodeSearchText.value.toLowerCase())
            );
        });

        // 过滤用户用于布置作业
        const filteredHomeworkUsers = computed(() => {
            if (!homeworkUserSearchText.value) {
                return allUsers.value;
            }
            return allUsers.value.filter(user =>
                user.username.toLowerCase().includes(homeworkUserSearchText.value.toLowerCase()) ||
                user.nickname.toLowerCase().includes(homeworkUserSearchText.value.toLowerCase())
            );
        });

        // 过滤用户用于用户切换
        const filteredUsersForSwitch = computed(() => {
            if (!userSwitchSearchText.value) {
                return allUsers.value;
            }
            return allUsers.value.filter(user =>
                user.username.toLowerCase().includes(userSwitchSearchText.value.toLowerCase()) ||
                user.nickname.toLowerCase().includes(userSwitchSearchText.value.toLowerCase())
            );
        });

        // 选择节点进行评分
        const selectNodeForRating = async (node) => {
            selectedNodeForRating.value = node;
            // 当选择节点时，加载该节点的练习题目
            await loadFuturePracticeData(node.id);
            // 为每个题目添加fromNodeId属性，以便在界面中识别
            practiceNodes.value = practiceNodes.value.map(question => ({
                ...question,
                fromNodeId: node.id
            }));
        };

        // 新增：选择题目
        const selectQuestion = (question) => {
            selectedQuestion.value = question;
        };

        // 选择用户
        const selectUser = (user) => {
            selectedUser.value = user;
        };

        // 选择节点进行布置作业
        const selectNodeForHomework = async (node) => {
            selectedNodeForHomework.value = node;
            // 当选择节点时，加载该节点的练习题目
            await loadFuturePracticeData(node.id);
            // 为每个题目添加fromNodeId属性，以便在界面中识别
            practiceNodes.value = practiceNodes.value.map(question => ({
                ...question,
                fromNodeId: node.id
            }));
            // 更新题目列表
            homeworkQuestions.value = [...practiceNodes.value];

            // 如果已经选择了用户，加载该用户在此节点上的布置作业数据
            if (selectedUserForHomework.value) {
                await loadHomeworkData(node.id, selectedUserForHomework.value.id);
            }
        };

        // 选择用户进行布置作业
        const selectUserForHomework = async (user) => {
            selectedUserForHomework.value = user;

            // 如果已经选择了节点，加载该用户在此节点上的布置作业数据
            if (selectedNodeForHomework.value) {
                await loadHomeworkData(selectedNodeForHomework.value.id, user.id);
            }
        };

        // 添加题目到任务列表
        const addQuestionToHomework = (question) => {
            // 检查题目是否已经在任务列表中
            const existingIndex = homeworkTasks.value.findIndex(task => task.id === question.id);
            if (existingIndex === -1) {
                // 如果不在任务列表中，则添加
                homeworkTasks.value.push(question);
            }
        };

        // 从任务列表中移除题目
        const removeQuestionFromHomework = (questionId) => {
            homeworkTasks.value = homeworkTasks.value.filter(task => task.id !== questionId);
        };

        // 设置星级
        const setStars = (starCount) => {
            stars.value = starCount;
        };


        // 获取节点的真实星级数据
        // const loadNodeStars = async () => {
        //     try {
        //         // 获取当前用户ID（这里假设从某个地方获取，实际项目中可能需要从store或props传入）
        //         // const userId = localStorage.getItem('userId') || '1'; // 临时使用默认值
        //         const userId = null;
        //         // 为每个节点获取星级数据
        //         for (const node of talentNodes.value) {
        //             try {
        //                 console.log("talentNodes node:", node);
        //                 const response = await graphService.getStatistics(node.id, userId);
        //                 if (response.status === 200 && response.data.length > 0) {
        //                     // 获取所有统计数据中的最大stars值
        //                     let maxStars = 0;
        //                     response.data.forEach(stat => {
        //                         if (stat.description) {
        //                             try {
        //                                 const descData = JSON.parse(stat.description);
        //                                 if (descData.stars && descData.stars > maxStars) {
        //                                     maxStars = descData.stars;
        //                                 }
        //                             } catch (e) {
        //                                 console.warn('解析desc数据失败:', e);
        //                             }
        //                         }
        //                     });
        //                     nodeStars.value[node.id] = maxStars;
        //                     // if (maxStars > 0) {
        //                     //     node.desc.active = true;
        //                     // }
        //                 } else {
        //                     // 如果没有数据，默认为0星
        //                     nodeStars.value[node.id] = 0;
        //                 }
        //             } catch (error) {
        //                 console.error(`获取节点${node.id}的星级数据失败:`, error);
        //                 nodeStars.value[node.id] = 0;
        //             }
        //         }
        //     } catch (error) {
        //         console.error('获取节点星级数据失败:', error);
        //         // 出错时使用临时数据
        //         generateTempNodeStars();
        //     }
        // };

        // 获取当前选中节点的学习节点
        // const getStudyNodesForSelectedNode = () => {
        //     if (!selectedNode.value) return [];

        //     // 根据UI需求，这里应该返回与当前节点相关的学习节点
        //     // 通过筛选studyNodes，找出与当前节点有关联的学习节点
        //     // 在实际应用中，我们需要根据selectedNodeStudyRelations来筛选
        //     return studyNodes.value.filter(studyNode =>
        //         selectedNodeStudyRelations.value.some(relation => relation.toNodeId === studyNode.id)
        //     );
        // };
        // 获取当前选中节点的练习节点
        // const getPracticeNodesForSelectedNode = () => {
        //     if (!selectedNode.value) return [];

        //     // 查找与当前选中节点相关的练习节点关系
        //     // 通过筛选practiceNodes，找出与当前节点有关联的练习节点
        //     return practiceNodes.value.filter(practiceNode =>
        //         selectedNodePracticeRelations.value.some(relation => relation.toNodeId === practiceNode.id)
        //     );
        // };

        // 解析节点描述中的stars和link属性
        const parseNodeDesc = (desc) => {
            if (!desc) return {};

            // 如果desc已经是对象，直接返回
            if (typeof desc === 'object' && desc !== null) {
                return desc;
            }

            // 如果desc是字符串，尝试解析为JSON
            if (typeof desc === 'string') {
                try {
                    return JSON.parse(desc);
                } catch (e) {
                    console.warn('Failed to parse node description:', e);
                    return {};
                }
            }

            return {};
        };

        // 验证星级输入
        const validateStars = (stars) => {
            let starsValue = parseInt(stars.value);
            if (isNaN(starsValue) || starsValue < 1) {
                stars.value = 1;
            } else if (starsValue > 5) {
                stars.value = 5;
            }
        };        // 打开链接
        const openLink = (url) => {
            if (url) {
                window.open(url, '_blank');
            }
        };

        // 添加学习节点关系
        const addStudyNodeRelation = async () => {
            if (!selectedNode.value || !newStudyNodeName.value) return;

            try {
                // 创建新的学习节点
                const nodeDesc = {
                    description: '',
                    icon: '',
                    position: { x: 100, y: 100 },
                    active: true,
                    shape: 'circle',
                    size: 60,
                    borderColor: '#4caf50',
                    backgroundColor: '#e8f5e9',
                    properties: {},
                    stars: parseInt(newStudyNodeStars.value) || 0,
                    link: newStudyNodeLink.value || ''
                };

                const nodeDescString = JSON.stringify(nodeDesc);

                // 添加新节点
                const nodeResponse = await graphService.addNode(
                    newStudyNodeName.value,
                    nodeDescString
                );

                if (nodeResponse.status === 200) {
                    const newNodeId = nodeResponse.data.id;

                    // 添加学习节点关系 (fromNode=当前节点, toNode=新节点, graph=futureStudy)
                    const linkResponse = await graphService.addGraph(
                        selectedNode.value.id,
                        newNodeId,
                        futureStudyGraphId.value
                    );

                    if (linkResponse.status === 200) {
                        // 重新加载学习节点数据
                        await loadFutureStudyData(selectedNode.value.id);
                        // 清空输入框
                        newStudyNodeName.value = '';
                        newStudyNodeStars.value = 0;
                        newStudyNodeLink.value = '';
                        alert('学习节点关系添加成功');
                    } else {
                        alert(`添加学习节点关系失败: ${linkResponse.message}`);
                    }
                } else {
                    alert(`添加学习节点失败: ${nodeResponse.message}`);
                }
            } catch (error) {
                console.error('添加学习节点关系时发生错误:', error);
                alert('添加学习节点关系失败，请查看控制台错误信息');
            }
        };
        // 添加练习节点关系
        const addPracticeNodeRelation = async () => {
            if (!selectedNode.value || !newPracticeNodeName.value) return;

            try {
                // 创建新的练习节点
                const nodeDesc = {
                    description: '',
                    icon: '',
                    position: { x: 100, y: 100 },
                    active: true,
                    shape: 'circle',
                    size: 60,
                    borderColor: '#2196f3',
                    backgroundColor: '#e3f2fd',
                    properties: {},
                    stars: parseInt(newPracticeNodeStars.value) || 0,
                    link: newPracticeNodeLink.value || ''
                };

                const nodeDescString = JSON.stringify(nodeDesc);

                // 添加新节点
                const nodeResponse = await graphService.addNode(
                    newPracticeNodeName.value,
                    nodeDescString
                );

                if (nodeResponse.status === 200) {
                    const newNodeId = nodeResponse.data.id;

                    // 添加练习节点关系 (fromNode=当前节点, toNode=新节点, graph=futurePractice)
                    const linkResponse = await graphService.addGraph(
                        selectedNode.value.id,
                        newNodeId,
                        futurePracticeGraphId.value
                    );

                    if (linkResponse.status === 200) {
                        // 重新加载练习节点数据
                        await loadFuturePracticeData(selectedNode.value.id);
                        // 清空输入框
                        newPracticeNodeName.value = '';
                        newPracticeNodeStars.value = 0;
                        newPracticeNodeLink.value = '';
                        alert('练习节点关系添加成功');
                    } else {
                        alert(`添加练习节点关系失败: ${linkResponse.message}`);
                    }
                } else {
                    alert(`添加练习节点失败: ${nodeResponse.message}`);
                }
            } catch (error) {
                console.error('添加练习节点关系时发生错误:', error);
                alert('添加练习节点关系失败，请查看控制台错误信息');
            }
        };
        // 删除学习节点关系
        const removeStudyNodeRelation = async (studyNodeId) => {
            if (!selectedNode.value) return;

            try {
                // 删除学习节点关系
                const response = await graphService.deleteGraph(
                    selectedNode.value.id,
                    studyNodeId,
                    futureStudyGraphId.value
                );

                if (response.status === 200) {
                    // 重新加载学习节点数据
                    // await loadFutureStudyData();
                    alert('学习节点关系删除成功');
                } else {
                    alert(`删除学习节点关系失败: ${response.message}`);
                }
            } catch (error) {
                console.error('删除学习节点关系时发生错误:', error);
                alert('删除学习节点关系失败，请查看控制台错误信息');
            }
        };
        // 删除练习节点关系
        const removePracticeNodeRelation = async (practiceNodeId) => {
            if (!selectedNode.value) return;

            try {
                // 删除练习节点关系
                const response = await graphService.deleteGraph(
                    selectedNode.value.id,
                    practiceNodeId,
                    futurePracticeGraphId.value
                );

                if (response.status === 200) {
                    // 重新加载练习节点数据
                    // await loadFuturePracticeData();
                    alert('练习节点关系删除成功');
                } else {
                    alert(`删除练习节点关系失败: ${response.message}`);
                }
            } catch (error) {
                console.error('删除练习节点关系时发生错误:', error);
                alert('删除练习节点关系失败，请查看控制台错误信息');
            }
        };
        // 提交评分
        const submitRating = async () => {
            // 检查是否选择了节点和用户
            if (!selectedNodeForRating.value || !selectedUser.value) {
                alert('请选择节点和用户');
                return;
            }

            try {
                // 构造desc数据
                const descData = {
                    stars: stars.value,
                    remark: remark.value,
                    result: result.value
                };
                const desc = JSON.stringify(descData);

                // 确定要评分的目标ID（节点或题目）
                let targetId = selectedNodeForRating.value.id;
                if (selectedQuestion.value) {
                    // 如果选择了题目，则对题目进行评分
                    targetId = selectedQuestion.value.id;
                }

                // 调用后端API保存评分数据
                const response = await graphService.addStatistics(
                    targetId,
                    selectedUser.value.id,
                    result.value,
                    desc
                );

                if (response.status === 200) {
                    alert('评分提交成功');
                    closeRatingModal();
                    // 不重新加载数据，只更新相关节点的星级显示
                    // 更新节点星级数据
                    if (selectedQuestion.value && selectedQuestion.value.id) {
                        // 如果是对题目评分，更新题目的星级
                        const practiceNode = practiceNodes.value.find(node => node && node.id === selectedQuestion.value.id);
                        if (practiceNode) {
                            practiceNode.stars = stars.value;
                        }
                    } else {
                        // 如果是对节点评分，更新节点的星级
                        if (selectedNodeForRating.value && selectedNodeForRating.value.id) {
                            nodeStars.value[selectedNodeForRating.value.id] = stars.value;
                        }
                        // 更新节点激活状态
                        updateNodeActiveStatus();
                    }
                } else {
                    alert(`评分提交失败: ${response.message}`);
                }
            } catch (error) {
                console.error('提交评分失败:', error);
                alert('评分提交失败');
            }
        };
        // 提交布置作业
        const submitHomework = async () => {
            // 检查是否选择了节点和用户





























            if (!selectedNodeForHomework.value || !selectedUserForHomework.value) {
                alert('请选择节点和用户');
                return;
            }

            // 检查任务列表是否为空
            if (homeworkTasks.value.length === 0) {
                alert('请至少选择一个题目添加到任务列表');
                return;
            }

            try {
                // 调用后端API保存布置作业数据
                const response = await graphService.addHomeworks(
                    selectedNodeForHomework.value.id,
                    selectedUserForHomework.value.id,
                    homeworkTasks.value
                );

                if (response.status === 200) {
                    alert('布置作业提交成功');
                    closeHomeworkModal();
                    // 不重新加载数据，保持当前页面状态
                } else {
                    alert(`布置作业提交失败: ${response.message}`);
                }
            } catch (error) {
                console.error('提交布置作业失败:', error);
                alert('布置作业提交失败');
            }
        };

        // 获取布置作业数据
        const loadHomeworkData = async (nodeId, userId) => {
            try {                // 调用后端API获取布置作业数据
                const response = await graphService.getHomeworks(nodeId, userId);

                if (response.status === 200) {
                    // 更新任务列表
                    homeworkTasks.value = response.data.map(task => ({
                        id: task.id,
                        name: task.name,
                        desc: task.desc
                    }));
                } else {
                    console.error('获取布置作业数据失败:', response.message);
                    homeworkTasks.value = [];
                }
            } catch (error) {
                console.error('获取布置作业数据失败:', error);
                homeworkTasks.value = [];
            }
        };

        // 加载指定用户的FutureEngine数据
        const loadFutureEngineDataForUser = async (userId, page = 1, pageSize = 100) => {
            // 设置最大重试次数
            const maxRetries = 5;

            // 用于跟踪总节点数，以便计算进度
            let totalLoadedNodes = 0;

            // 辅助函数：带重试的API调用
            const fetchWithRetry = async (currentPage, currentPageSize, retryCount = 0) => {
                try {
                    const response = await graphService.findFutureEngineDataForUser(currentPage, currentPageSize, userId);
                    return response;
                } catch (error) {
                    // 检查是否是超时错误
                    const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');

                    if (isTimeoutError && retryCount < maxRetries) {
                        console.warn(`获取第 ${currentPage} 页数据超时，正在进行第 ${retryCount + 1} 次重试...`);
                        // 等待一段时间后重试，使用指数退避策略
                        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
                        return fetchWithRetry(currentPage, currentPageSize, retryCount + 1);
                    } else if (retryCount >= maxRetries) {
                        console.error(`获取第 ${currentPage} 页数据失败，已达到最大重试次数 ${maxRetries}:`, error);
                        throw error; // 抛出错误以停止整个加载过程
                    } else {
                        console.error(`获取第 ${currentPage} 页数据发生错误:`, error);
                        throw error; // 非超时错误直接抛出
                    }
                }
            };

            try {
                // 显示加载动画
                loading.value = true;

                // 如果是第一页，清空现有数据
                if (page === 1) {
                    talentNodes.value = [];
                    links.value = [];
                    nodeStars.value = {};

                    // 重置进度
                    totalLoadedNodes = 0;
                }

                const response = await fetchWithRetry(page, pageSize);
                if (response.status === 200) {
                    // 更新talentNodes
                    if (response.data.talentNodes && Array.isArray(response.data.talentNodes)) {
                        // 转换数据格式以适配现有结构
                        const convertedNodes = response.data.talentNodes.map(node => {
                            // 尝试解析节点描述
                            let parsedDesc = {};
                            console.log('Processing node:', node.id, 'desc type:', typeof node.desc, 'desc value:', node.desc);
                            if (typeof node.desc === 'string') {
                                try {
                                    parsedDesc = JSON.parse(node.desc);
                                    console.log('Parsed desc for node', node.id, ':', parsedDesc);
                                } catch (e) {
                                    console.warn('Failed to parse node description for node:', node.id, e);
                                    parsedDesc = {};
                                }
                            } else if (typeof node.desc === 'object' && node.desc !== null) {
                                parsedDesc = node.desc;
                                console.log('Direct object desc for node', node.id, ':', parsedDesc);
                            }

                            // 提取或设置默认值
                            let position = parsedDesc.position || { x: Math.random() * 500, y: Math.random() * 500 };
                            // 确保坐标为整数
                            position = {
                                x: Math.round(position.x),
                                y: Math.round(position.y)
                            };
                            const shape = parsedDesc.shape || 'circle';
                            const size = parsedDesc.size || 80;
                            const borderColor = parsedDesc.borderColor || '#000000';
                            const backgroundColor = parsedDesc.backgroundColor || '#ffffff';
                            const icon = parsedDesc.icon || '';
                            const description = parsedDesc.description || '';
                            const properties = parsedDesc.properties || {};
                            const active = Object.prototype.hasOwnProperty.call(parsedDesc, 'active') ? parsedDesc.active : false;
                            // 保存原始位置信息
                            originalNodePositions.value[node.id] = { ...position };

                            return {
                                id: node.id,
                                name: node.name,
                                desc: {
                                    ...parsedDesc,
                                    position,
                                    shape,
                                    size,
                                    borderColor,
                                    backgroundColor,
                                    icon,
                                    description,
                                    properties,
                                    active
                                }
                            };
                        });

                        // 追加新节点到现有节点列表
                        talentNodes.value = [...talentNodes.value, ...convertedNodes];

                        // 更新已加载节点数
                        totalLoadedNodes += convertedNodes.length;

                        // 更新进度条
                        // 根据当前已加载节点数和当前页数来估算进度
                        // 使用一个简单的进度计算方式：假设总页数为当前页数的2倍（如果当前是第1页）或当前页数（如果当前是后续页）
                        const estimatedTotalPages = Math.max(page * 2, 10); // 估算总页数
                        const estimatedTotalNodes = estimatedTotalPages * pageSize; // 估算总节点数

                        // 计算进度，但限制在80%以内，直到确认完成
                        const estimatedProgress = Math.min(80, Math.round((totalLoadedNodes / estimatedTotalNodes) * 100));
                        loadingProgress.value = estimatedProgress;
                        loadingStatus.value = `加载中... (${totalLoadedNodes}个节点)`;
                    }
                    // 更新links
                    if (response.data.links && Array.isArray(response.data.links)) {
                        // 转换链接数据格式以适配现有结构
                        const convertedLinks = response.data.links.map(link => {
                            // 确保链接有默认的样式
                            const style = link.style || {
                                stroke: "#000000",
                                lineStyle: "solid",
                                lineType: "straight"
                            };

                            // 确保每个链接都有独立的offset对象，避免引用共享
                            const linkOffset = link.offset ? { ...link.offset } : { x: 0, y: 0 };

                            return {
                                source: link.source,
                                target: link.target,
                                graphId: link.graphId || "futureEngine",
                                value: link.value || 1,
                                style: style,
                                offset: linkOffset
                            };
                        });

                        // 追加新链接到现有链接列表
                        links.value = [...links.value, ...convertedLinks];

                        // 强制触发Vue的响应式更新
                        nextTick(() => {
                            // 通过创建新的链接数组来触发重新渲染
                            const newLinks = links.value.map(link => ({
                                ...link,
                                // 确保offset对象也是新的
                                offset: link.offset ? { ...link.offset } : { x: 0, y: 0 }
                            }));
                            // 再次使用splice方法更新链接数组，确保Vue能正确检测到变化
                            links.value.splice(0, links.value.length, ...newLinks);
                        });

                        // 保存原始连接线offset信息
                        links.value.forEach(link => {
                            originalLinkOffsets.value[`${link.source}-${link.target}`] = {
                                ...(link.offset || { x: 0, y: 0 })
                            };
                        });
                    }
                    //更新nodestars
                    // 后端现在返回的是字典而不是列表
                    if (response.data.nodeStars && typeof response.data.nodeStars === 'object' && !Array.isArray(response.data.nodeStars)) {
                        // 合并星级数据
                        nodeStars.value = { ...nodeStars.value, ...response.data.nodeStars };
                    }

                    // 检查是否有更多数据需要加载
                    if (response.data.pagination && response.data.pagination.has_more) {
                        // 递归加载下一页数据
                        console.log('Loading more data...');
                        await loadFutureEngineDataForUser(userId, page + 1, pageSize);
                    } else {
                        // 所有数据加载完成，更新active状态
                        updateNodeActiveStatus();

                        // 在active状态更新完成后，将第一个active节点居中显示
                        nextTick(() => {
                            // 查找第一个active节点
                            const firstActiveNode = talentNodes.value.find(node => node.desc?.active);
                            if (firstActiveNode) {
                                // 延迟一小段时间确保DOM完全渲染后再执行居中操作
                                setTimeout(() => {
                                    centerNodeOnScreen(firstActiveNode);
                                }, 100);
                            }
                        });

                        // 数据更新后，需要重建四叉树
                        quadTreeNeedsRebuild = true;
                    }
                } else {
                    console.error('加载FutureEngine数据失败:', response.message);
                    // 更新进度状态为失败
                    loadingStatus.value = '加载失败';
                }
            } catch (error) {
                // 检查是否是超时错误
                const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.includes('timeout');

                // 更新进度状态为失败
                loadingStatus.value = '加载失败';

                if (isTimeoutError) {
                    console.error('加载FutureEngine数据超时，已达到最大重试次数:', error);
                    // 即使超时，也要显示部分已加载的数据
                    if (page > 1) {
                        // 如果不是第一页超时，至少显示已加载的数据
                        updateNodeActiveStatus();
                        nextTick(() => {
                            const firstActiveNode = talentNodes.value.find(node => node.desc?.active);
                            if (firstActiveNode) {
                                setTimeout(() => {
                                    centerNodeOnScreen(firstActiveNode);
                                }, 100);
                            }
                        });
                        quadTreeNeedsRebuild = true;
                    }
                } else {
                    console.error('加载FutureEngine数据时发生错误:', error);
                }
            } finally {
                // 隐藏加载动画
                // 只有在第一页调用（即最外层调用）完成后才隐藏加载动画
                if (page === 1) {  // 只有在第一次调用（第一页）完成后才隐藏加载动画
                    // 确保加载动画至少显示一段时间，让用户能看到
                    setTimeout(() => {
                        loading.value = false;
                        // 最后重置进度条状态
                        setTimeout(() => {
                            loadingProgress.value = 0;
                            loadingStatus.value = '';
                        }, 1000);
                    }, 500); // 最少显示500毫秒
                }
            }
        };

        // 选择用户进行查看
        const selectUserForView = async (user) => {
            selectedUserForView.value = user;
            showUserSwitcher.value = false;

            // 加载所选用户的FutureEngine数据
            await loadFutureEngineDataForUser(user.id);
        };

        // 切换用户选择器显示状态
        const toggleUserSwitcher = () => {
            showUserSwitcher.value = !showUserSwitcher.value;
            if (showUserSwitcher.value) {
                // 如果用户列表为空，重新加载
                if (allUsers.value.length === 0) {
                    loadAllUsers();
                }
            }
        };
        // 在组件挂载时加载FutureEngine数据
        onMounted(async () => {
            // 添加键盘事件监听器
            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('keyup', handleKeyUp);
            // 加载用户信息
            await loadUserInfo();
            // 加载应用信息
            await loadAppInfo();
            // 确保currentPanOffset有初始值
            if (!currentPanOffset.value) {
                currentPanOffset.value = { x: 0, y: 0 };
            }

            // 加载FutureEngine数据（第一页，每页100条）
            await loadFutureEngineData(1, 100);

            // 生成临时的节点星级数据
            // generateTempNodeStars();


            //获取FutureEngine数据
            futureEngineGraphId.value = await getFutureEngineGraphId();

            // 获取futureStudy和futurePractice的graphId
            futureStudyGraphId.value = await getFutureStudyGraphId();
            futurePracticeGraphId.value = await getFuturePracticeGraphId();
            console.log('futureStudyGraphId:', futureStudyGraphId.value);
            console.log('futurePracticeGraphId:', futurePracticeGraphId.value);
            // 加载学习节点和练习节点数据
            // await loadFutureStudyData();
            // await loadFuturePracticeData();

            // 为userType为1的用户加载所有用户数据
            if (user.value.userType === 1) {
                await loadAllUsers();
            }

            // 根据用户类型自动设置预览模式状态
            // 当用户类型不等于1时，自动进入预览模式
            if (user.value.userType !== 1) {
                isInPreviewMode.value = true;

                // 保存当前节点位置，以便后续可以恢复
                talentNodes.value.forEach(node => {
                    previewModeNodePositions.value[node.id] = { ...node.desc.position };
                });

                // 重置节点布局以适应预览模式
                resetNodes();
            }

            // 根据用户类型自动设置预览模式后，都需要重建四叉树
            // 无论是普通用户自动进入预览模式，还是管理员用户保持非预览模式
            quadTreeNeedsRebuild = true;

            // 在数据加载完成后，将第一个active节点居中显示
            nextTick(() => {
                // 查找第一个active节点
                const firstActiveNode = talentNodes.value.find(node => node.desc?.active);
                if (firstActiveNode) {
                    // 延迟一小段时间确保DOM完全渲染后再执行居中操作
                    setTimeout(() => {
                        centerNodeOnScreen(firstActiveNode);
                    }, 100);
                }
            });

            // 强制重建四叉树以确保初始状态正确
            // quadTreeNeedsRebuild = true; // 已在上面处理，避免重复
            // 初始化默认预设样式
            // 检查是否需要添加新的预设样式
            const newPresets = [
                {
                    name: '紫色梦幻风',
                    nodeStyle: {
                        backgroundColor: '#f3e5f5',
                        borderColor: '#9c27b0',
                        shape: 'circle',
                        size: 85,
                        borderStyle: 'solid',
                        borderWidth: 3,
                        opacity: 0.9
                    },
                    lineStyle: {
                        stroke: '#9c27b0',
                        lineStyle: 'solid',
                        lineType: 'sCurve',
                        cornerRadius: 20
                    }
                },
                {
                    name: '橙色活力风',
                    nodeStyle: {
                        backgroundColor: '#fff3e0',
                        borderColor: '#ff9800',
                        shape: 'rounded-rectangle',
                        size: 80,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        opacity: 1
                    },
                    lineStyle: {
                        stroke: '#ff9800',
                        lineStyle: 'solid',
                        lineType: 'sCurve',
                        cornerRadius: 15
                    }
                },
                {
                    name: '红色热情风',
                    nodeStyle: {
                        backgroundColor: '#ffebee',
                        borderColor: '#f44336',
                        shape: 'circle',
                        size: 90,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        opacity: 0.85
                    },
                    lineStyle: {
                        stroke: '#f44336',
                        lineStyle: 'dashed',
                        lineType: 'sCurve',
                        cornerRadius: 25
                    }
                },
                {
                    name: '青色冷静风',
                    nodeStyle: {
                        backgroundColor: '#e0f7fa',
                        borderColor: '#00bcd4',
                        shape: 'rounded-rectangle',
                        size: 75,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        opacity: 1
                    },
                    lineStyle: {
                        stroke: '#00bcd4',
                        lineStyle: 'solid',
                        lineType: 'straight',
                        cornerRadius: 10
                    }
                },
                {
                    name: '粉色甜美风',
                    nodeStyle: {
                        backgroundColor: '#fce4ec',
                        borderColor: '#e91e63',
                        shape: 'circle',
                        size: 80,
                        borderStyle: 'dotted',
                        borderWidth: 2,
                        opacity: 0.9
                    },
                    lineStyle: {
                        stroke: '#e91e63',
                        lineStyle: 'dotted',
                        lineType: 'sCurve',
                        cornerRadius: 12
                    }
                },
                {
                    name: '深蓝专业风',
                    nodeStyle: {
                        backgroundColor: '#e8eaf6',
                        borderColor: '#3f51b5',
                        shape: 'rectangle',
                        size: 85,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        opacity: 1
                    },
                    lineStyle: {
                        stroke: '#3f51b5',
                        lineStyle: 'solid',
                        lineType: 'sCurve',
                        cornerRadius: 18
                    }
                },
                {
                    name: '金色奢华风',
                    nodeStyle: {
                        backgroundColor: '#fff8e1',
                        borderColor: '#ffc107',
                        shape: 'circle',
                        size: 95,
                        borderStyle: 'solid',
                        borderWidth: 3,
                        opacity: 0.95
                    },
                    lineStyle: {
                        stroke: '#ffc107',
                        lineStyle: 'solid',
                        lineType: 'sCurve',
                        cornerRadius: 30
                    }
                },
                {
                    name: '灰色简约风',
                    nodeStyle: {
                        backgroundColor: '#fafafa',
                        borderColor: '#9e9e9e',
                        shape: 'rounded-rectangle',
                        size: 80,
                        borderStyle: 'solid',
                        borderWidth: 1,
                        opacity: 1
                    },
                    lineStyle: {
                        stroke: '#9e9e9e',
                        lineStyle: 'solid',
                        lineType: 'straight',
                        cornerRadius: 5
                    }
                },
                {
                    name: '彩虹渐变风',
                    nodeStyle: {
                        backgroundColor: '#ffffff',
                        borderColor: '#ff5722',
                        shape: 'circle',
                        size: 85,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        opacity: 1
                    },
                    lineStyle: {
                        stroke: '#4caf50',
                        lineStyle: 'solid',
                        lineType: 'sCurve',
                        cornerRadius: 20
                    }
                },
                {
                    name: '夜空神秘风',
                    nodeStyle: {
                        backgroundColor: '#1a1a2e',
                        borderColor: '#16213e',
                        shape: 'circle',
                        size: 80,
                        borderStyle: 'solid',
                        borderWidth: 2,
                        opacity: 1
                    },
                    lineStyle: {
                        stroke: '#0f3460',
                        lineStyle: 'solid',
                        lineType: 'sCurve',
                        cornerRadius: 15
                    }
                }
            ];

            // 检查是否已经存在这些预设样式，如果没有则添加
            const existingPresetNames = presetStyles.value.map(preset => preset.name);
            const presetsToAdd = newPresets.filter(preset => !existingPresetNames.includes(preset.name));

            // 始终检查并添加缺失的预设样式，即使用户已经有部分预设样式
            if (presetsToAdd.length > 0) {
                // 添加新的预设样式
                presetStyles.value = [...presetStyles.value, ...presetsToAdd];
                // 保存到localStorage
                localStorage.setItem('presetStyles', JSON.stringify(presetStyles.value));
            }

            // 如果用户没有任何预设样式，则添加所有默认预设样式
            if (presetStyles.value.length === 0) {
                const defaultPresets = [
                    {
                        name: '经典样式',
                        nodeStyle: {
                            backgroundColor: '#ffffff',
                            borderColor: '#000000',
                            shape: 'circle',
                            size: 80,
                            borderStyle: 'solid',
                            borderWidth: 2,
                            opacity: 1
                        },
                        lineStyle: {
                            stroke: '#000000',
                            lineStyle: 'solid',
                            lineType: 'straight',
                            cornerRadius: 10
                        }
                    },
                    {
                        name: '蓝色科技风',
                        nodeStyle: {
                            backgroundColor: '#e3f2fd',
                            borderColor: '#2196f3',
                            shape: 'circle',
                            size: 80,
                            borderStyle: 'solid',
                            borderWidth: 2,
                            opacity: 1
                        },
                        lineStyle: {
                            stroke: '#2196f3',
                            lineStyle: 'solid',
                            lineType: 'sCurve',
                            cornerRadius: 15
                        }
                    },
                    {
                        name: '绿色清新风',
                        nodeStyle: {
                            backgroundColor: '#e8f5e9',
                            borderColor: '#4caf50',
                            shape: 'rounded-rectangle',
                            size: 80,
                            borderStyle: 'solid',
                            borderWidth: 2,
                            opacity: 1
                        },
                        lineStyle: {
                            stroke: '#4caf50',
                            lineStyle: 'dashed',
                            lineType: 'sCurve',
                            cornerRadius: 10
                        }
                    },
                    ...newPresets
                ];

                presetStyles.value = defaultPresets;
                // 保存到localStorage
                localStorage.setItem('presetStyles', JSON.stringify(presetStyles.value));
            }

            // 加载群组和标签数据（仅对userType为1的用户）
            if (user.value.userType === 1) {
                await loadGroupsAndTags();
            }
        });        // 键盘事件处理函数
        const handleKeyDown = (event) => {
            if (event.key === 'Control' || event.key === 'Ctrl') {
                isCtrlPressed.value = true;
            } else if (event.key === 'Shift') {
                isShiftPressed.value = true;
            } else if (event.key === 'Alt') {
                isAltPressed.value = true;
            }

            // Ctrl+P 快速移动选中节点（仅在编辑模式下可用）
            if (event.ctrlKey && event.key.toLowerCase() === 'p' && isEditMode.value) {
                moveSelectedNodesByCanvasOffset();
                event.preventDefault();
            }

            // user.type==1 时的快捷键
            if (user.value.userType === 1) {
                // Ctrl+E 进入或退出编辑模式
                if (event.ctrlKey && event.key.toLowerCase() === 'e') {
                    toggleEditMode();
                    event.preventDefault();
                }

                // Ctrl+S 保存当前布局（仅在编辑模式下）
                if (event.ctrlKey && event.key.toLowerCase() === 's' && isEditMode.value) {
                    saveCurrentLayout();
                    event.preventDefault();
                }

                // Ctrl+W 自动网格对齐
                if (event.ctrlKey && event.key.toLowerCase() === 'w') {
                    autoLayout();
                    event.preventDefault();
                }

                // Ctrl+L 进入添加链接状态
                if (event.ctrlKey && event.key.toLowerCase() === 'l' && isEditMode.value) {
                    if (isAddingLink.value) {
                        cancelAddLink();
                    } else {
                        startAddLink();
                    }
                    event.preventDefault();
                }

                // Ctrl+R 重新加载数据
                if (event.ctrlKey && event.key.toLowerCase() === 'r') {
                    reloadData();
                    event.preventDefault();
                }

                // Ctrl+Shift+S 进入或退出设置界面
                if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 's') {
                    showSettingsModal.value = !showSettingsModal.value;
                    event.preventDefault();
                }
            }

            // 方向键控制画布拖动
            if (!event.ctrlKey && !event.altKey && !event.shiftKey) {
                switch (event.key) {
                    case 'ArrowUp':
                        currentPanOffset.value.y += 50;
                        panOffset.value.y = currentPanOffset.value.y;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                    case 'ArrowDown':
                        currentPanOffset.value.y -= 50;
                        panOffset.value.y = currentPanOffset.value.y;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                    case 'ArrowLeft':
                        currentPanOffset.value.x += 50;
                        panOffset.value.x = currentPanOffset.value.x;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                    case 'ArrowRight':
                        currentPanOffset.value.x -= 50;
                        panOffset.value.x = currentPanOffset.value.x;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                }
            }

            // Ctrl+方向键大幅拖动画布
            if (event.ctrlKey && !event.altKey && !event.shiftKey) {
                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;

                switch (event.key) {
                    case 'ArrowUp':
                        currentPanOffset.value.y += windowHeight;
                        panOffset.value.y = currentPanOffset.value.y;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                    case 'ArrowDown':
                        currentPanOffset.value.y -= windowHeight;
                        panOffset.value.y = currentPanOffset.value.y;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                    case 'ArrowLeft':
                        currentPanOffset.value.x += windowWidth;
                        panOffset.value.x = currentPanOffset.value.x;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                    case 'ArrowRight':
                        currentPanOffset.value.x -= windowWidth;
                        panOffset.value.x = currentPanOffset.value.x;
                        debouncedForceUpdateDisplayedNodes();
                        event.preventDefault();
                        break;
                }
            }

            // Alt+ +/-/= 控制缩放
            if (event.altKey && !event.ctrlKey && !event.shiftKey) {
                switch (event.key) {
                    case '+':
                    case '=': // 处理不同的键盘布局
                        zoomIn();
                        event.preventDefault();
                        break;
                    case '-':
                        zoomOut();
                        event.preventDefault();
                        break;
                }
            }

            // Alt+0 或 Alt+= 重置缩放
            if (event.altKey && !event.ctrlKey && !event.shiftKey) {
                if (event.key === '0' || (event.key === '=' && event.shiftKey)) {
                    resetZoom();
                    event.preventDefault();
                }
            }

            // Ctrl+O 临时激活所有节点
            if (event.ctrlKey && event.key.toLowerCase() === 'o') {
                toggleTemporaryActivation();
                event.preventDefault();
            }

            // ESC键取消添加链接模式
            if (event.key === 'Escape' && isAddingLink.value) {
                cancelAddLink();
                event.preventDefault();
            }

            // 处理数字键，用于特殊高亮关系线
            if (/^[1-9]$/.test(event.key) && highlightedNodes.value.size > 0) {
                // 使用高亮节点中的第一个节点
                const firstHighlightedNodeId = highlightedNodes.value.values().next().value;
                const firstHighlightedNode = talentNodes.value.find(node => node.id === firstHighlightedNodeId);
                if (firstHighlightedNode) {
                    // 只有在有高亮节点时才处理数字键
                    handleNumberKey(parseInt(event.key));
                    event.preventDefault();
                }
            }

            // 处理+/-键，用于切换特殊高亮的关系线
            if ((event.key === '+' || event.key === '=') && highlightedNodes.value.size > 0) {
                handlePlusMinusKey(1); // +键向后切换
                event.preventDefault();
            }
            if (event.key === '-' && highlightedNodes.value.size > 0) {
                handlePlusMinusKey(-1); // -键向前切换
                event.preventDefault();
            }

            // 处理Tab键，跳转到特殊高亮关系线的另一个节点
            if (event.key === 'Tab' && highlightedNodes.value.size > 0) {
                handleTabKey();
                event.preventDefault();
            }
        };

        // 切换临时激活模式
        const toggleTemporaryActivation = () => {
            // 如果当前处于临时激活模式，则恢复原始状态
            if (isTemporarilyActivated.value) {
                // 恢复每个节点的原始active状态
                talentNodes.value.forEach(node => {
                    if (originalNodeActiveStates.value[node.id] !== undefined) {
                        node.desc.active = originalNodeActiveStates.value[node.id];
                    }
                });
                isTemporarilyActivated.value = false;
            } else {
                // 保存当前所有节点的active状态
                talentNodes.value.forEach(node => {
                    originalNodeActiveStates.value[node.id] = node.desc.active;
                    // 临时激活所有节点
                    node.desc.active = true;
                });
                isTemporarilyActivated.value = true;
            }
        };

        // 检查节点是否应该显示为激活状态（考虑临时激活模式）
        const isNodeActuallyActive = (node) => {
            // 如果处于临时激活模式，所有节点都应该是激活的
            if (isTemporarilyActivated.value) {
                return true;
            }
            // 否则使用节点本身的active状态
            return node.desc.active;
        };

        // 处理数字键，用于特殊高亮关系线
        const handleNumberKey = (number) => {
            // 获取与当前高亮节点相关的所有链接
            const relatedLinkIds = Array.from(relatedLinks.value);

            // 如果没有相关链接或数字超出范围，则取消特殊高亮
            if (relatedLinkIds.length === 0 || number > relatedLinkIds.length) {
                // 取消特殊高亮
                specialHighlightedLink.value = null;
                specialHighlightedLinkId.value = null;
                return;
            }

            // 如果之前有特殊高亮的链接，先取消它
            if (specialHighlightedLinkId.value !== null) {
                // 不需要特殊处理，因为CSS会自动更新样式
            }

            // 设置第number-1个链接为特殊高亮（数组索引从0开始）
            const targetLinkId = relatedLinkIds[number - 1];
            specialHighlightedLinkId.value = targetLinkId;

            // 找到对应的链接对象
            const linkParts = targetLinkId.split('-');
            if (linkParts.length === 2) {
                const source = parseInt(linkParts[0]);
                const target = parseInt(linkParts[1]);

                // 在links数组中查找对应的链接，需要考虑source和target的顺序
                const link = links.value.find(l =>
                    (l.source === source && l.target === target) ||
                    (l.source === target && l.target === source)
                );

                if (link) {
                    specialHighlightedLink.value = link;
                } else {
                    // 如果没找到，可能是因为relatedLinks存储的格式和links数组中的格式不一致
                    // 尝试在relatedLinkIds中直接使用targetLinkId查找
                    specialHighlightedLink.value = null; // 如果没找到链接对象，就设为null
                }
            }
        };

        // 处理+/-键，用于切换特殊高亮的关系线
        const handlePlusMinusKey = (direction) => { // direction: 1 for '+', -1 for '-'
            // 获取与当前高亮节点相关的所有链接
            const relatedLinkIds = Array.from(relatedLinks.value);

            // 如果没有相关链接，则不执行任何操作
            if (relatedLinkIds.length === 0) {
                return;
            }

            // 获取当前特殊高亮的链接索引
            let currentIndex = -1;
            if (specialHighlightedLinkId.value) {
                currentIndex = relatedLinkIds.indexOf(specialHighlightedLinkId.value);
            }

            // 计算新的索引
            let newIndex = currentIndex + direction;

            // 如果新索引超出范围
            if (newIndex < 0) {
                // 如果向前超出范围（小于0），则取消特殊高亮
                specialHighlightedLink.value = null;
                specialHighlightedLinkId.value = null;
                return;
            } else if (newIndex >= relatedLinkIds.length) {
                // 如果向后超出范围（>= 数组长度）
                if (direction === 1) {
                    // 如果是按+键且超出范围，则自动选择第1条线（索引为0）
                    newIndex = 0;
                } else {
                    // 如果是按-键且超出范围，则取消特殊高亮
                    specialHighlightedLink.value = null;
                    specialHighlightedLinkId.value = null;
                    return;
                }
            }

            // 设置新的特殊高亮链接
            const targetLinkId = relatedLinkIds[newIndex];
            specialHighlightedLinkId.value = targetLinkId;

            // 找到对应的链接对象
            const linkParts = targetLinkId.split('-');
            if (linkParts.length === 2) {
                const source = parseInt(linkParts[0]);
                const target = parseInt(linkParts[1]);

                // 在links数组中查找对应的链接，需要考虑source和target的顺序
                const link = links.value.find(l =>
                    (l.source === source && l.target === target) ||
                    (l.source === target && l.target === source)
                );

                if (link) {
                    specialHighlightedLink.value = link;
                } else {
                    specialHighlightedLink.value = null; // 如果没找到链接对象，就设为null
                }
            }
        };

        // 添加节点右键点击事件处理函数
        const handleNodeRightClick = (event, node) => {
            // 检查用户类型，只有userType为1的用户才能使用右键菜单功能
            if (user.value.userType !== 1) {
                console.log('普通用户无法使用右键菜单功能');
                return;
            }

            event.preventDefault(); // 阻止默认右键菜单
            event.stopPropagation();

            // 存储右键点击的节点
            selectedNodeForContextMenu.value = node;
            console.log('右键点击的节点:', selectedNodeForContextMenu.value);

            // 使用智能定位函数计算右键菜单的最佳位置
            const { x, y } = calculateContextMenuPosition(event.clientX, event.clientY);

            // 设置右键菜单位置
            contextMenu.value.x = x;
            contextMenu.value.y = y;
            contextMenu.value.visible = true;

            // 添加全局点击事件监听器，用于隐藏右键菜单
            const hideContextMenu = () => {
                contextMenu.value.visible = false;
                document.removeEventListener('click', hideContextMenu);
            };
            document.addEventListener('click', hideContextMenu);
        };

        // 判断节点是否在指定group中
        const isNodeInGroup = (node, groupId) => {
            if (!node || !node.groups || !Array.isArray(node.groups)) return false;
            return node.groups.includes(groupId);
        };

        // 判断节点是否具有指定标签
        const isNodeInTag = (tagId) => {
            if (!selectedNodeForContextMenu.value || !selectedNodeForContextMenu.value.category) return false;

            // 检查节点的category属性是否包含该标签
            // category可能是数组或单个值
            if (Array.isArray(selectedNodeForContextMenu.value.category)) {
                return selectedNodeForContextMenu.value.category.includes(tagId);
            } else {
                return selectedNodeForContextMenu.value.category === tagId;
            }
        };

        // 切换节点group
        const toggleNodeGroup = async (groupId) => {
            // 检查用户类型，只有userType为1的用户才能使用群组功能
            if (user.value.userType !== 1) {
                console.log('普通用户无法使用群组功能');
                return;
            }

            if (!selectedNodeForContextMenu.value) return;

            // 确保节点有groups属性
            if (!selectedNodeForContextMenu.value.groups) {
                selectedNodeForContextMenu.value.groups = [];
            }

            // 创建节点groups的副本
            const currentGroups = [...selectedNodeForContextMenu.value.groups];
            const groupIndex = currentGroups.indexOf(groupId);

            if (groupIndex > -1) {
                // 如果节点已在该group中，则移除
                currentGroups.splice(groupIndex, 1);
            } else {
                // 如果节点不在该group中，则添加
                currentGroups.push(groupId);
            }

            // 更新节点的groups
            selectedNodeForContextMenu.value.groups = currentGroups;

            // 隐藏菜单
            contextMenu.value.visible = false;

            // 更新节点的groups属性
            selectedNodeForContextMenu.value.groups = currentGroups;

            // 这里可以添加API调用以保存群组信息到后端
            try {
                // 保存节点群组信息到后端
                const nodeDesc = { ...selectedNodeForContextMenu.value.desc };
                nodeDesc.groups = currentGroups;
                const nodeDescString = JSON.stringify(nodeDesc);

                const response = await graphService.modifyNode(
                    selectedNodeForContextMenu.value.id,
                    selectedNodeForContextMenu.value.name,
                    nodeDescString
                );

                if (response && response.status === 200) {
                    console.log('群组信息更新成功');
                } else {
                    console.error('群组信息更新失败:', response?.message || '未知错误');
                }
            } catch (error) {
                console.error('更新群组信息时发生错误:', error);
            }
        };

        // 处理标签选择
        const handleTagSelection = async (tag) => {
            // 检查用户类型，只有userType为1的用户才能使用标签功能
            if (user.value.userType !== 1) {
                console.log('普通用户无法使用标签功能');
                return;
            }

            if (selectedNodeForContextMenu.value && tag) {
                console.log('选中的标签:', tag.name || tag.label);
                // 调用addTagToNode方法为节点添加标签
                try {
                    const response = await graphService.addTagToNode(selectedNodeForContextMenu.value.id, tag.name || tag.label);
                    if (response && response.status === 200) {
                        console.log('标签添加成功');
                        // 更新节点的category属性以反映新添加的标签
                        if (!selectedNodeForContextMenu.value.category) {
                            selectedNodeForContextMenu.value.category = [];
                        }
                        // 如果category还不是数组，将其转换为数组
                        if (!Array.isArray(selectedNodeForContextMenu.value.category)) {
                            selectedNodeForContextMenu.value.category = [selectedNodeForContextMenu.value.category];
                        }
                        // 将标签ID添加到节点的category中（如果尚未存在）
                        if (!selectedNodeForContextMenu.value.category.includes(tag.id)) {
                            selectedNodeForContextMenu.value.category.push(tag.id);
                        }

                        // 更新节点信息到后端
                        const nodeDesc = { ...selectedNodeForContextMenu.value.desc };
                        nodeDesc.category = selectedNodeForContextMenu.value.category;
                        const nodeDescString = JSON.stringify(nodeDesc);

                        const updateResponse = await graphService.modifyNode(
                            selectedNodeForContextMenu.value.id,
                            selectedNodeForContextMenu.value.name,
                            nodeDescString
                        );

                        if (updateResponse && updateResponse.status === 200) {
                            console.log('节点标签信息更新成功');
                        } else {
                            console.error('节点标签信息更新失败:', updateResponse?.message || '未知错误');
                        }
                    } else {
                        console.error('添加标签失败:', response?.message || '未知错误');
                    }
                } catch (error) {
                    console.error('添加标签时发生错误:', error);
                }
                contextMenu.value.visible = false;
            }
        };

        // 更新群组菜单状态
        const updateGroupMenu = () => {
            // 触发响应式更新
            if (selectedNodeForContextMenu.value) {
                // 强制更新组件
                nextTick(() => {
                    // 重新计算群组状态
                })
            }
        };

        // 更新标签菜单状态
        const updateTagMenu = () => {
            // 触发响应式更新
            if (selectedNodeForContextMenu.value) {
                // 强制更新组件
                nextTick(() => {
                    // 重新计算标签状态
                })
            }
        };

        // 计算多边形包围区域的函数
        const getSelectionPolygonPoints = () => {
            if (highlightedNodes.value.size <= 1) {
                return '';
            }

            // 获取所有选中节点的坐标
            const selectedNodes = talentNodes.value.filter(node =>
                highlightedNodes.value.has(node.id) && node.desc?.position
            );

            if (selectedNodes.length < 2) {
                return '';
            }

            // 计算每个节点的中心坐标（考虑缩放和偏移）
            const points = selectedNodes.map(node => {
                const size = node.desc.size || 80;
                const x = node.desc.position.x + size / 2 + currentPanOffset.value.x;
                const y = node.desc.position.y + size / 2 + currentPanOffset.value.y;
                return { x: x * scale.value, y: y * scale.value };
            });

            // 计算凸包（Graham扫描算法的简化版本）
            if (points.length < 2) {
                return '';
            }

            let convexHull;
            if (points.length === 2) {
                // 如果只有两个点，创建一个包围这两个点的矩形
                const p1 = points[0];
                const p2 = points[1];
                const minX = Math.min(p1.x, p2.x);
                const maxX = Math.max(p1.x, p2.x);
                const minY = Math.min(p1.y, p2.y);
                const maxY = Math.max(p1.y, p2.y);

                // 添加一些边距
                const margin = 50;
                convexHull = [
                    { x: minX - margin, y: minY - margin },
                    { x: maxX + margin, y: minY - margin },
                    { x: maxX + margin, y: maxY + margin },
                    { x: minX - margin, y: maxY + margin }
                ];
            } else {
                // 对于3个或更多点，计算凸包
                convexHull = calculateConvexHull(points);
            }

            // 将凸包点转换为字符串格式
            return convexHull.map(point => `${point.x},${point.y}`).join(' ');
        };

        // Graham扫描算法计算凸包
        const calculateConvexHull = (points) => {
            if (points.length < 3) return points;

            // 找到最下面的点（y坐标最小，如果y相同则x最小）
            let start = points[0];
            for (let i = 1; i < points.length; i++) {
                if (points[i].y < start.y || (points[i].y === start.y && points[i].x < start.x)) {
                    start = points[i];
                }
            }

            // 按照相对于起始点的角度排序
            const sortedPoints = [...points].sort((a, b) => {
                if (a === start) return -1;
                if (b === start) return 1;

                const angleA = Math.atan2(a.y - start.y, a.x - start.x);
                const angleB = Math.atan2(b.y - start.y, b.x - start.x);

                if (angleA === angleB) {
                    // 如果角度相同，按距离排序
                    const distA = Math.sqrt(Math.pow(a.x - start.x, 2) + Math.pow(a.y - start.y, 2));
                    const distB = Math.sqrt(Math.pow(b.x - start.x, 2) + Math.pow(b.y - start.y, 2));
                    return distA - distB;
                }

                return angleA - angleB;
            });

            // 使用Graham扫描算法
            const hull = [];
            for (const point of sortedPoints) {
                while (hull.length > 1 &&
                    crossProduct(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
                    hull.pop();
                }
                hull.push(point);
            }

            return hull;
        };

        // 计算向量叉积，用于判断转向
        const crossProduct = (o, a, b) => {
            return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
        };

        // 扩大多边形函数（用于描边效果）
        const getExpandedPolygonPoints = () => {
            if (highlightedNodes.value.size <= 1) {
                return '';
            }

            // 获取所有选中节点的坐标
            const selectedNodes = talentNodes.value.filter(node =>
                highlightedNodes.value.has(node.id) && node.desc?.position
            );

            if (selectedNodes.length < 2) {
                return '';
            }

            // 计算每个节点的中心坐标（考虑缩放和偏移）
            const points = selectedNodes.map(node => {
                const size = node.desc.size || 80;
                const x = node.desc.position.x + size / 2 + currentPanOffset.value.x;
                const y = node.desc.position.y + size / 2 + currentPanOffset.value.y;
                return { x: x * scale.value, y: y * scale.value };
            });

            // 计算凸包（Graham扫描算法的简化版本）
            if (points.length < 2) {
                return '';
            }

            let convexHull;
            if (points.length === 2) {
                // 如果只有两个点，创建一个包围这两个点的矩形
                const p1 = points[0];
                const p2 = points[1];
                const minX = Math.min(p1.x, p2.x);
                const maxX = Math.max(p1.x, p2.x);
                const minY = Math.min(p1.y, p2.y);
                const maxY = Math.max(p1.y, p2.y);

                // 添加一些边距
                const margin = 50;
                convexHull = [
                    { x: minX - margin, y: minY - margin },
                    { x: maxX + margin, y: minY - margin },
                    { x: maxX + margin, y: maxY + margin },
                    { x: minX - margin, y: maxY + margin }
                ];
            } else {
                // 对于3个或更多点，计算凸包
                convexHull = calculateConvexHull(points);
            }

            // 扩大多边形，使用设置的扩展像素
            const expandedPolygon = expandPolygon(convexHull, selectionPolygonExpandOffset.value);

            // 将扩展后的凸包点转换为字符串格式
            return expandedPolygon.map(point => `${point.x},${point.y}`).join(' ');
        };

        // 生成带圆角的选择多边形点
        const getRoundedSelectionPolygonPoints = () => {
            if (highlightedNodes.value.size <= 1) {
                return '';
            }

            // 获取所有选中节点的坐标
            const selectedNodes = talentNodes.value.filter(node =>
                highlightedNodes.value.has(node.id) && node.desc?.position
            );

            if (selectedNodes.length < 2) {
                return '';
            }

            // 计算每个节点的中心坐标（考虑缩放和偏移）
            const points = selectedNodes.map(node => {
                const size = node.desc.size || 80;
                const x = node.desc.position.x + size / 2 + currentPanOffset.value.x;
                const y = node.desc.position.y + size / 2 + currentPanOffset.value.y;
                return { x: x * scale.value, y: y * scale.value };
            });

            // 计算凸包（Graham扫描算法的简化版本）
            if (points.length < 2) {
                return '';
            }

            let convexHull;
            if (points.length === 2) {
                // 如果只有两个点，创建一个包围这两个点的矩形
                const p1 = points[0];
                const p2 = points[1];
                const minX = Math.min(p1.x, p2.x);
                const maxX = Math.max(p1.x, p2.x);
                const minY = Math.min(p1.y, p2.y);
                const maxY = Math.max(p1.y, p2.y);

                // 添加一些边距
                const margin = 50;
                convexHull = [
                    { x: minX - margin, y: minY - margin },
                    { x: maxX + margin, y: minY - margin },
                    { x: maxX + margin, y: maxY + margin },
                    { x: minX - margin, y: maxY + margin }
                ];
            } else {
                // 对于3个或更多点，计算凸包
                convexHull = calculateConvexHull(points);
            }

            // 扩大多边形并添加圆角
            const expandedPolygon = expandPolygon(convexHull, selectionPolygonExpandOffset.value, selectionPolygonCornerRadius.value);

            // 将扩展后的凸包点转换为字符串格式
            return expandedPolygon.map(point => `${point.x},${point.y}`).join(' ');
        };

        // 通用多边形扩展函数，支持自定义扩展距离和圆角半径
        const expandPolygon = (polygon, offset, cornerRadius) => {
            if (polygon.length === 0) return [];

            if (polygon.length === 1) {
                // 单个点的情况，创建一个围绕该点的矩形
                const p = polygon[0];
                return [
                    { x: p.x - offset, y: p.y - offset },
                    { x: p.x + offset, y: p.y - offset },
                    { x: p.x + offset, y: p.y + offset },
                    { x: p.x - offset, y: p.y + offset }
                ];
            }

            if (polygon.length === 2) {
                // 两个点的情况，创建一个围绕线段的矩形
                const p1 = polygon[0];
                const p2 = polygon[1];

                // 计算线段的垂直向量
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const length = Math.sqrt(dx * dx + dy * dy);

                if (length === 0) {
                    // 如果两点重合，返回围绕该点的矩形
                    return [
                        { x: p1.x - offset, y: p1.y - offset },
                        { x: p1.x + offset, y: p1.y - offset },
                        { x: p1.x + offset, y: p1.y + offset },
                        { x: p1.x - offset, y: p1.y + offset }
                    ];
                }

                // 单位向量
                const ux = dx / length;
                const uy = dy / length;

                // 垂直向量（逆时针旋转90度）
                const vx = -uy;
                const vy = ux;

                // 计算四个角点
                return [
                    { x: p1.x - vx * offset - ux * offset, y: p1.y - vy * offset - uy * offset },
                    { x: p1.x + vx * offset - ux * offset, y: p1.y + vy * offset - uy * offset },
                    { x: p2.x + vx * offset + ux * offset, y: p2.y + vy * offset + uy * offset },
                    { x: p2.x - vx * offset + ux * offset, y: p2.y - vy * offset + uy * offset }
                ];
            }

            // 3个或更多点的情况，使用边偏移算法
            // 对于凸多边形，向外扩展可以通过将每条边向外平移实现
            const n = polygon.length;
            if (n < 3) return polygon;

            // 计算每条边的法向量（向外）
            const edges = [];
            for (let i = 0; i < n; i++) {
                const p1 = polygon[i];
                const p2 = polygon[(i + 1) % n];

                // 计算边向量
                const edgeX = p2.x - p1.x;
                const edgeY = p2.y - p1.y;

                // 计算法向量（向外）
                const length = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
                if (length > 0) {
                    // 单位向量
                    const nx = edgeY / length;  // 逆时针旋转90度得到法向量
                    const ny = -edgeX / length;

                    // 将边向外平移offset距离
                    edges.push({
                        start: { x: p1.x + nx * offset, y: p1.y + ny * offset },
                        end: { x: p2.x + nx * offset, y: p2.y + ny * offset },
                        normal: { x: nx, y: ny }
                    });
                } else {
                    // 如果边长度为0，使用原始点
                    edges.push({
                        start: p1,
                        end: p2,
                        normal: { x: 0, y: 0 }
                    });
                }
            }

            // 计算扩展后多边形的顶点（相邻边的交点）
            const expandedPoints = [];
            for (let i = 0; i < n; i++) {
                const currEdge = edges[i];
                const nextEdge = edges[(i + 1) % n];

                // 计算两条线的交点
                const x1 = currEdge.start.x;
                const y1 = currEdge.start.y;
                const x2 = currEdge.end.x;
                const y2 = currEdge.end.y;

                const x3 = nextEdge.start.x;
                const y3 = nextEdge.start.y;
                const x4 = nextEdge.end.x;
                const y4 = nextEdge.end.y;

                // 计算线段交点
                const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
                if (Math.abs(denom) > 1e-10) {  // 避免除零
                    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
                    const ix = x1 + t * (x2 - x1);
                    const iy = y1 + t * (y2 - y1);
                    expandedPoints.push({ x: ix, y: iy });
                } else {
                    // 如果线段平行，使用当前边的终点
                    expandedPoints.push({ x: currEdge.end.x, y: currEdge.end.y });
                }
            }

            // 添加圆角效果
            return createRoundedCorners(expandedPoints, cornerRadius); // 使用传入的圆角半径
        };

        // 将透明度转换为十六进制
        const opacityToHex = (opacity) => {
            // 确保透明度在0-1范围内
            const normalizedOpacity = Math.max(0, Math.min(1, opacity));
            // 转换为0-255范围的整数
            const alpha = Math.round(normalizedOpacity * 255);
            // 转换为两位十六进制数
            const hex = alpha.toString(16).padStart(2, '0').toUpperCase();
            return hex;
        };

        // 创建带圆角的多边形
        const createRoundedCorners = (points, cornerRadius) => {
            if (points.length < 3) return points;

            const roundedPoints = [];
            const n = points.length;

            for (let i = 0; i < n; i++) {
                const prevPoint = points[(i - 1 + n) % n];
                const currPoint = points[i];
                const nextPoint = points[(i + 1) % n];

                // 计算两条边的向量
                const v1x = currPoint.x - prevPoint.x;
                const v1y = currPoint.y - prevPoint.y;
                const v2x = nextPoint.x - currPoint.x;
                const v2y = nextPoint.y - currPoint.y;

                // 归一化向量
                const len1 = Math.sqrt(v1x * v1x + v1y * v1y);
                const len2 = Math.sqrt(v2x * v2x + v2y * v2y);

                if (len1 > 0 && len2 > 0) {
                    const u1x = v1x / len1;
                    const u1y = v1y / len1;
                    const u2x = v2x / len2;
                    const u2y = v2y / len2;

                    // 计算内角：使用第一条边的反向向量和第二条边的向量
                    const dotProduct = (-u1x) * u2x + (-u1y) * u2y; // 反向向量与第二条边的点积
                    const angle = Math.acos(Math.max(-1, Math.min(1, dotProduct))); // 内角

                    // 计算角平分线向量（从顶点出发，指向多边形内部）
                    const bisectorX = -u1x + u2x; // 第一条边的反向向量 + 第二条边的向量
                    const bisectorY = -u1y + u2y;
                    const bisectorLen = Math.sqrt(bisectorX * bisectorX + bisectorY * bisectorY);

                    if (bisectorLen > 0) {
                        const normBisectorX = bisectorX / bisectorLen;
                        const normBisectorY = bisectorY / bisectorLen;

                        // 计算从顶点到圆角起点的距离
                        const distance = cornerRadius / Math.tan(angle / 2);

                        // 计算圆角的起点和终点
                        const cornerStartX = currPoint.x - u1x * distance;
                        const cornerStartY = currPoint.y - u1y * distance;
                        const cornerEndX = currPoint.x + u2x * distance;
                        const cornerEndY = currPoint.y + u2y * distance;

                        // 计算圆弧的圆心
                        // 圆心在角平分线上，距离顶点的距离为 distance / Math.sin(angle/2)
                        const centerDistance = cornerRadius / Math.sin(angle / 2);
                        const centerX = currPoint.x + normBisectorX * centerDistance;
                        const centerY = currPoint.y + normBisectorY * centerDistance;

                        // 计算起始角和结束角
                        const startAngle = Math.atan2(cornerStartY - centerY, cornerStartX - centerX);
                        const endAngle = Math.atan2(cornerEndY - centerY, cornerEndX - centerX);

                        // 确保角度差在正确范围内
                        let angleDiff = endAngle - startAngle;
                        if (angleDiff < 0) angleDiff += 2 * Math.PI;

                        // 根据圆角半径和角度差确定插入点的数量
                        const numSegments = Math.max(3, Math.floor(angleDiff * 3)); // 确保有足够的点

                        // 生成圆弧上的点
                        for (let j = 0; j <= numSegments; j++) {
                            const t = j / numSegments;
                            const currentAngle = startAngle + t * angleDiff;
                            const px = centerX + cornerRadius * Math.cos(currentAngle);
                            const py = centerY + cornerRadius * Math.sin(currentAngle);
                            roundedPoints.push({ x: px, y: py });
                        }
                    } else {
                        // 如果角平分线长度为0，直接添加原点
                        roundedPoints.push(currPoint);
                    }
                } else {
                    // 如果任意向量长度为0，直接添加原点
                    roundedPoints.push(currPoint);
                }
            }

            return roundedPoints;
        };

        // 处理Tab键，跳转到特殊高亮关系线的另一个节点
        const handleTabKey = () => {
            // 如果没有特殊高亮的链接，则不执行任何操作
            if (!specialHighlightedLink.value) {
                return;
            }

            // 获取当前高亮的节点
            const currentHighlightedNodeId = highlightedNodes.value.values().next().value;
            if (!currentHighlightedNodeId) {
                return;
            }

            // 确定目标节点（当前节点的另一个节点）
            let targetNodeId;
            if (specialHighlightedLink.value.source === currentHighlightedNodeId) {
                targetNodeId = specialHighlightedLink.value.target;
            } else if (specialHighlightedLink.value.target === currentHighlightedNodeId) {
                targetNodeId = specialHighlightedLink.value.source;
            } else {
                // 如果特殊高亮的链接不与当前节点相关，不执行跳转
                return;
            }

            // 查找目标节点对象
            const targetNode = talentNodes.value.find(node => node.id === targetNodeId);
            if (!targetNode) {
                return;
            }

            // 清除当前高亮状态
            highlightedNodes.value.clear();
            selectedNode.value = null; // 清除选中节点
            // 同时清空相关的节点和连线高亮状态
            relatedNodes.value.clear();
            relatedLinks.value.clear();
            // 同时清除特殊高亮状态
            specialHighlightedLink.value = null;
            specialHighlightedLinkId.value = null;

            // 设置新节点为高亮状态
            highlightedNodes.value.add(targetNode.id);

            // 查找与新节点相关的节点和连线
            findRelatedNodesAndLinks(targetNode.id);

            // 定位到目标节点
            centerNodeOnScreen(targetNode);
        };

        const handleKeyUp = (event) => {
            if (event.key === 'Control' || event.key === 'Ctrl') {
                isCtrlPressed.value = false;
            } else if (event.key === 'Shift') {
                isShiftPressed.value = false;
            } else if (event.key === 'Alt') {
                isAltPressed.value = false;
            }
        };


        // 在组件卸载时移除事件监听器
        onUnmounted(() => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);

            // 移除原来的事件监听器
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            // 取消可能存在的动画帧
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            // 清除可能存在的控制点双击定时器
            if (window.controlPointDoubleClickTimer) {
                clearTimeout(window.controlPointDoubleClickTimer);
                window.controlPointDoubleClickTimer = null;
            }
            // 清除可能存在的节点双击定时器
            if (window.nodeDoubleClickTimer) {
                clearTimeout(window.nodeDoubleClickTimer);
                window.nodeDoubleClickTimer = null;
            }
            // 清除触摸双击定时器
            lastTouchTime.value = 0;
            lastTouchTarget.value = null;
            lastNodeTouchTime.value = 0;
            lastNodeTouchTarget.value = null;
            lastLinkTouchTime.value = 0;
            lastLinkTouchTarget.value = null;
            // 恢复文本选择
            document.body.style.userSelect = '';

            // 清除框选状态
            isSelecting.value = false;
            // 清除画布拖拽状态
            isDraggingCanvas.value = false;
            hadSelectedNodes.value = false;
        });

        // 模糊搜索节点
        const performSearch = () => {
            if (!searchQuery.value.trim()) {
                searchResults.value = [];
                // 清空搜索匹配节点集合
                searchMatchedNodes.value.clear();
                return;
            }

            const query = searchQuery.value.toLowerCase();
            const matchedNodes = talentNodes.value.filter(node =>
                node.name.toLowerCase().includes(query)
            );

            // 更新搜索结果
            searchResults.value = matchedNodes;

            // 更新搜索匹配节点集合
            searchMatchedNodes.value = new Set(matchedNodes.map(node => node.id));
        };

        // 显示搜索结果
        const showSearchResults = () => {
            showSearchResultsList.value = true;
        };

        // 隐藏搜索结果
        const hideSearchResults = () => {
            // 添加一个小延迟，确保点击事件能正常触发
            setTimeout(() => {
                showSearchResultsList.value = false;
            }, 200);
        };

        // 选择搜索结果
        const selectSearchResult = (node) => {
            // 隐藏搜索结果列表
            showSearchResultsList.value = false;

            // 清空搜索框
            searchQuery.value = '';

            // 清空搜索匹配节点集合
            searchMatchedNodes.value.clear();

            // 设置节点为高亮状态（清空之前的选中并选中当前节点）
            highlightedNodes.value.clear();
            highlightedNodes.value.add(node.id);

            // 查找与当前节点相关的节点和连线
            findRelatedNodesAndLinks(node.id);

            // 定位到选中的节点
            centerNodeOnScreen(node);
        };

        // 居中显示节点
        const centerNodeOnScreen = (node) => {
            if (!talentTree.value || !node.desc?.position) return;

            // 获取节点位置
            const nodeX = node.desc.position.x;
            const nodeY = node.desc.position.y;

            // 获取浏览器窗口的可视区域尺寸
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            // 节点尺寸
            const nodeSize = node.desc.size || 80;

            // 计算目标偏移量，使节点居中于浏览器窗口可视区域
            // 考虑节点的实际渲染位置（包含当前的平移偏移）
            const targetOffsetX = windowWidth / 2 - (nodeX + currentPanOffset.value.x) - nodeSize / 2;
            const targetOffsetY = windowHeight / 2 - (nodeY + currentPanOffset.value.y) - nodeSize * 3 / 2;

            // 更新画布偏移量
            currentPanOffset.value = {
                x: currentPanOffset.value.x + targetOffsetX,
                y: currentPanOffset.value.y + targetOffsetY
            };

            // 更新panOffset以保持状态一致
            panOffset.value = { ...currentPanOffset.value };
        };

        // 快速移动选中节点到屏幕中心
        const moveSelectedNodesByCanvasOffset = () => {
            // 检查是否有选中的节点
            if (highlightedNodes.value.size === 0) {
                return;
            }

            // 获取当前画布偏移量
            const offsetX = currentPanOffset.value.x;
            const offsetY = currentPanOffset.value.y;

            // 如果偏移量为0，则不需要移动
            if (offsetX === 0 && offsetY === 0) {
                return;
            }

            // 计算屏幕中心位置
            const screenWidth = window.innerWidth;
            const screenHeight = window.innerHeight;
            const centerX = screenWidth / 2;
            const centerY = screenHeight / 2;

            // 计算目标位置相对于当前视口的坐标
            const targetX = centerX - currentPanOffset.value.x;
            const targetY = centerY - currentPanOffset.value.y;

            // 计算选中节点的中心点
            let totalX = 0;
            let totalY = 0;
            let nodeCount = 0;

            // 获取所有选中的节点
            const selectedNodes = talentNodes.value.filter(node => highlightedNodes.value.has(node.id));

            selectedNodes.forEach(node => {
                if (node.desc?.position) {
                    totalX += node.desc.position.x + (node.desc.size || 80) / 2;
                    totalY += node.desc.position.y + (node.desc.size || 80) / 2;
                    nodeCount++;
                }
            });

            if (nodeCount === 0) return;

            const avgX = totalX / nodeCount;
            const avgY = totalY / nodeCount;

            // 计算需要移动的偏移量
            const moveOffsetX = targetX - avgX;
            const moveOffsetY = targetY - avgY;

            // 移动每个选中的节点
            selectedNodes.forEach(node => {
                if (node.desc?.position) {
                    // 更新节点位置
                    node.desc.position.x += Math.round(moveOffsetX);
                    node.desc.position.y += Math.round(moveOffsetY);

                    // 更新节点在界面上的位置
                    const nodeElement = document.querySelector(`[data-node-id="${node.id}"]`);
                    if (nodeElement) {
                        nodeElement.style.left = `${node.desc.position.x + currentPanOffset.value.x}px`;
                        nodeElement.style.top = `${node.desc.position.y + currentPanOffset.value.y}px`;
                    }
                }
            });

            // 强制触发响应式更新，确保Vue检测到节点位置变化
            talentNodes.value = [...talentNodes.value];

            // 不重置画布偏移量，保持画布在当前位置

            // 调整画布大小
            adjustCanvasSize();

            // 强制更新路径
            forceUpdatePaths();

            // 强制更新显示的节点，确保移动后的节点正确显示
            forceUpdateDisplayedNodes();
        };

        // 缩放功能
        const zoomIn = () => {
            if (scale.value < maxScale.value) {
                scale.value = Math.min(scale.value * 1.2, maxScale.value);
                // 立即更新显示的节点，因为缩放改变了可视区域
                forceUpdateDisplayedNodes();
            }
        };

        const zoomOut = () => {
            if (scale.value > minScale.value) {
                scale.value = Math.max(scale.value / 1.2, minScale.value);
                // 立即更新显示的节点，因为缩放改变了可视区域
                forceUpdateDisplayedNodes();
            }
        };

        const resetZoom = () => {
            scale.value = 1;
            // 立即更新显示的节点
            forceUpdateDisplayedNodes();
        };

        // 立即更新显示的节点
        const forceUpdateDisplayedNodes = () => {
            // 启动性能监控
            performanceMonitor.startRender();

            // 触发Vue的响应式更新
            nextTick(() => {
                // 重新计算显示的节点
                // 通过触发currentPanOffset的更新来刷新计算属性
                currentPanOffset.value = { ...currentPanOffset.value };

                // 结束性能监控
                performanceMonitor.endRender();
            });
        };

        // 处理鼠标滚轮缩放
        const handleWheel = (event) => {
            event.preventDefault();
            if (event.ctrlKey || event.metaKey) {
                // Ctrl键或Cmd键按下时进行缩放
                if (event.deltaY < 0) {
                    zoomIn();
                } else {
                    zoomOut();
                }
            }
        };

        // 获取连接模态框标题
        const getLinkModalTitle = () => {
            if (sourceNodes?.value?.length > 0 && targetNode?.value) {
                return `编辑连接 (${sourceNodes.value.length} 个源节点 -> 1 个目标节点)`;
            } else if (editingLink.source && editingLink.target) {
                return '编辑连接';
            } else {
                return '添加连接';
            }
        };
        return {
            user, // 添加用户信息
            apps, // 添加应用信息
            talentNodes,
            nodeStars,
            links,
            isEditMode,
            isAddingLink,
            isDragging,
            isDraggingControlPoint,
            isPanning,
            isDraggingCanvas,
            hadSelectedNodes,
            draggedNode,
            draggedNodeElement,
            draggedControlPoint,
            sourceNodes,
            targetNode,
            selectedNode,
            selectedLink,
            showEditModal,
            showLinkModal,
            editingNode,
            editingLink,
            propertiesJson,
            talentTree,
            canvasSize,
            currentPanOffset,
            isSelecting,
            selectionStart,
            selectionEnd,
            getNodePosition,
            getLineStyle,
            getLineStyleText,
            getLineTypeText,
            getNodeLinks,
            getLinkedNodeName,
            isAlreadyLinked,
            isLinkActive,
            shouldShowLink,
            toggleEditMode,
            startAddLink,
            cancelAddLink,
            handleNodeClick,
            handleDragStart,
            handleDrag,
            handleDragEnd,
            handleControlPointMouseDown,
            handleStraightControlPointMouseDown,
            handleControlPointDrag,
            handleCanvasMouseDown,
            handleCanvasMouseMove,
            handleCanvasMouseUp,
            handleCanvasDoubleClick,
            handleCanvasTouchEnd,
            selectNodesInSelection,
            adjustCanvasSize,
            autoLayout,
            resetNodes,
            togglePreviewMode,
            isInPreviewMode,
            gridSpacing,
            previewModeNodePositions,
            addNode,
            editNode,
            editLink,
            saveNode,
            saveLink,
            deleteNode,
            deleteLink,
            selectNode,
            selectLink,
            closeDetailModal,
            closeEditModal,
            closeLinkModal,
            closeLinkDetailModal,
            updateNodeActivation,
            saveCurrentLayout,
            reloadData,
            getLinkPath,
            getControlPoints,
            linksLayer,
            loadFutureEngineData,
            getFutureEngineGraphId,
            getFutureStudyGraphId,
            getFuturePracticeGraphId,
            loadFutureStudyData,
            loadFuturePracticeData,
            generateTempNodeStars,
            // 学习节点和练习节点相关数据
            studyNodes,
            practiceNodes,
            // selectedNodeStudyRelations,
            // selectedNodePracticeRelations,
            newStudyNodeName,
            newPracticeNodeName,
            newStudyNodeStars,
            newStudyNodeLink,
            newPracticeNodeStars,
            newPracticeNodeLink,
            // 学习节点和练习节点相关方法
            // getStudyNodesForSelectedNode,
            // getPracticeNodesForSelectedNode,
            parseNodeDesc,
            validateStars,
            openLink,
            addStudyNodeRelation,
            addPracticeNodeRelation,
            removeStudyNodeRelation,
            removePracticeNodeRelation,
            // 评分功能相关            showRatingModal,            nodeSearchText,
            showRatingModal,
            nodeSearchText,
            userSearchText,
            selectedNodeForRating,
            selectedUser,
            stars,
            remark,
            result,
            filteredNodes,
            filteredUsers,
            openRatingModal,
            closeRatingModal,
            selectNodeForRating,
            selectQuestion,
            selectUser,
            setStars,
            submitRating,
            selectedQuestion,
            // 布置作业功能相关
            showHomeworkModal,
            homeworkNodeSearchText,
            homeworkUserSearchText,
            selectedNodeForHomework,
            selectedUserForHomework,
            homeworkQuestions,
            homeworkTasks,
            openHomeworkModal,
            closeHomeworkModal,
            selectNodeForHomework,
            selectUserForHomework,
            addQuestionToHomework,
            removeQuestionFromHomework,
            submitHomework,
            filteredHomeworkNodes,
            filteredHomeworkUsers,
            goToHomeworkView,
            // 设置功能相关
            showSettingsModal,
            displayMode,
            defaultNodeStyle,
            defaultLineStyle,
            presetName,
            selectedPreset,
            presetStyles,
            selectedNodePreset,
            selectedLinkPreset,
            openSettingsModal,
            closeSettingsModal,
            applySettings,
            savePresetStyle,
            applyPresetStyle,
            deletePresetStyle,
            selectPreset,
            applyNodePresetStyle,
            applyLinkPresetStyle,
            displayedNodes, // 添加这个计算属性
            visibleLinks, // 添加优化后的连接线计算属性
            // 搜索功能相关
            searchQuery,
            searchResults,
            showSearchResultsList,
            performSearch,
            showSearchResults,
            hideSearchResults,
            selectSearchResult,
            // 搜索高亮节点功能
            searchMatchedNodes,
            // 高亮节点功能
            highlightedNodes,
            // 相关节点和连线功能
            relatedNodes,
            relatedLinks,
            // 特殊高亮功能
            specialHighlightedLink,
            specialHighlightedLinkId,
            isCtrlPressed,
            isShiftPressed,
            isAltPressed,
            // 快捷键提示面板
            showShortcutHelp,
            toggleShortcutHelp,
            // 快速移动节点功能
            moveSelectedNodesByCanvasOffset,
            debugNodeSelection, // 调试函数
            // 缩放相关
            scale,
            minScale,
            maxScale,
            zoomIn,
            zoomOut,
            resetZoom,
            handleWheel,
            getLinkModalTitle,
            forceUpdateDisplayedNodes,
            // 临时激活相关
            isTemporarilyActivated,
            originalNodeActiveStates,
            toggleTemporaryActivation,
            isNodeActuallyActive,
            // 进程监控相关
            showProcessMonitor,
            runningProcesses,
            processStats,
            updateProcessMonitorData,
            // 进程监控悬浮窗相关
            processMonitorPosition,
            startDrag,
            minimizeProcessMonitor,
            isProcessMonitorMinimized,
            isProcessMonitorDragging,
            processMonitorDragOffset,
            // 测试节点相关
            testNodeCount,
            addTestNodes,
            // LOD细节层次函数
            getLODDetailLevel,
            // 右键菜单相关
            contextMenu,
            showGroupSubMenu,
            showTagSubMenu,
            selectedNodeForContextMenu,
            handleNodeRightClick,
            isNodeInGroup,
            isNodeInTag,
            toggleNodeGroup,
            handleTagSelection,
            updateGroupMenu,
            updateTagMenu,
            showGroupSubmenuWithPosition,
            showTagSubmenuWithPosition,
            groupSubmenuRef,
            tagSubmenuRef,
            groups,
            graphTypes,
            loadGroupsAndTags,
            // 多边形选择相关
            getSelectionPolygonPoints,
            getExpandedPolygonPoints,
            getRoundedSelectionPolygonPoints,
            // 多边形选择设置
            selectionPolygonExpandOffset,
            selectionPolygonCornerRadius,
            selectionPolygonFillColor,
            selectionPolygonFillOpacity,
            selectionPolygonStrokeColor,
            selectionPolygonStrokeOpacity,
            selectionPolygonStrokeDasharray,
            selectionPolygonStrokeWidth,
            // 透明度转换函数
            opacityToHex,
            // 用户切换功能相关
            allUsers,
            selectedUserForView,
            showUserSwitcher,
            userSwitchSearchText,
            filteredUsersForSwitch,
            selectUserForView,
            toggleUserSwitcher,
            loadFutureEngineDataForUser,
            // 其他功能相关
            loading,
            handleCanvasClick,
            // 批量节点功能
            batchNodes,
            recordNode,
            removeBatchNode,
        };
    }
};


</script>

<style scoped>
/* 加载动画样式 */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 99999 !important;
    pointer-events: none;
    /* 确保不影响用户交互 */
}

.loading-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-overlay p {
    font-size: 16px;
    color: #333;
    margin: 0;
}

.talent-system {
    padding: 20px;
    font-family: Arial, sans-serif;
}

/* 节点统计信息 */
.node-stats {
    position: absolute;
    top: 20px;
    right: 100px;
    z-index: 100;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    font-weight: bold;
    font-size: 14px;
}

/* 进程监控按钮 */
.process-monitor-button {
    position: absolute;
    top: 350px;
    right: 30px;
    z-index: 100;
    width: 40px;
    height: 40px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.process-monitor-button:hover {
    background-color: #f0f0f0;
    transform: scale(1.1);
}

/* 进程监控悬浮窗 */
.process-monitor-float {
    position: fixed;
    top: 100px;
    left: 20px;
    width: 400px;
    /* 增加宽度 */
    background-color: rgba(255, 255, 255, 0.92);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    min-height: 200px;
    max-height: 70vh;
    /* 增加最大高度 */
}

.process-monitor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    cursor: move;
    user-select: none;
    border-radius: 8px 8px 0 0;
}

.process-monitor-title {
    font-weight: bold;
    color: #495057;
    font-size: 14px;
}

.process-monitor-controls {
    display: flex;
    gap: 8px;
}

.minimize-btn,
.close-process-monitor {
    background: none;
    border: 1px solid #ced4da;
    border-radius: 4px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    color: #495057;
    padding: 0;
    transition: all 0.2s;
}

.minimize-btn:hover,
.close-process-monitor:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
}

.process-monitor-body {
    padding: 15px;
    max-height: 600px;
    /* 增加最大高度 */
    overflow-y: auto;
}

/* 保持原有的进程监控样式 */
.process-stats {
    margin-bottom: 20px;
}

.process-stats h4 {
    margin-bottom: 10px;
    color: #555;
    font-size: 14px;
}

.stats-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    padding: 6px 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #eee;
    font-size: 13px;
}

.stat-label {
    font-weight: bold;
    color: #555;
}

.stat-value {
    color: #007bff;
    font-weight: bold;
}

.running-processes h4 {
    margin-top: 20px;
    margin-bottom: 10px;
    color: #555;
    font-size: 14px;
}

.process-list {
    max-height: 300px;
    /* 增加进程列表最大高度 */
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
    background-color: #fdfdfd;
}

.process-item {
    padding: 10px;
    margin-bottom: 8px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    font-size: 12px;
}

.process-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}

.process-name {
    font-weight: bold;
    color: #333;
    font-size: 13px;
}

.process-status {
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 0.7em;
    font-weight: bold;
}

.status-running {
    background-color: #d4edda;
    color: #155724;
}

.status-pending {
    background-color: #fff3cd;
    color: #856404;
}

.status-idle {
    background-color: #d1ecf1;
    color: #0c5460;
}

.process-progress {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
}

.progress-bar {
    flex: 1;
    height: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
    margin-right: 10px;
}

.progress-fill {
    height: 100%;
    background-color: #007bff;
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 0.8em;
    color: #6c757d;
    min-width: 30px;
    text-align: right;
}

.process-info {
    font-size: 11px;
    color: #6c757d;
}

/* 测试控件样式 */
.test-controls {
    margin: 15px 0;
    padding: 10px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background-color: #f8f9fa;
}

.test-control-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.test-control-group label {
    font-size: 12px;
    font-weight: bold;
    color: #495057;
}

.test-control-group input {
    padding: 6px 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 13px;
    width: 100%;
}

.test-btn {
    background-color: #28a745;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: bold;
    transition: background-color 0.2s;
}

.test-btn:hover {
    background-color: #218838;
}

.process-stats {
    margin-bottom: 20px;
}

.process-stats h4 {
    margin-bottom: 10px;
    color: #555;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    padding: 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #eee;
}

.stat-label {
    font-weight: bold;
    color: #555;
}

.stat-value {
    color: #007bff;
    font-weight: bold;
}

.running-processes h4 {
    margin-top: 20px;
    margin-bottom: 10px;
    color: #555;
}

.process-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
}

.process-item {
    padding: 12px;
    margin-bottom: 10px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
}

.process-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.process-name {
    font-weight: bold;
    color: #333;
}

.process-status {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8em;
    font-weight: bold;
}

.status-running {
    background-color: #d4edda;
    color: #155724;
}

.status-pending {
    background-color: #fff3cd;
    color: #856404;
}

.status-idle {
    background-color: #d1ecf1;
    color: #0c5460;
}

.process-progress {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.progress-bar {
    flex: 1;
    height: 10px;
    background-color: #e9ecef;
    border-radius: 5px;
    overflow: hidden;
    margin-right: 10px;
}

.progress-fill {
    height: 100%;
    background-color: #007bff;
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 0.8em;
    color: #6c757d;
    min-width: 40px;
    text-align: right;
}

.process-info {
    font-size: 0.85em;
    color: #6c757d;
}

.close-process-monitor {
    display: block;
    margin: 20px auto 0;
    padding: 10px 20px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.close-process-monitor:hover {
    background-color: #545b62;
}

/* 快捷键提示按钮 */
.shortcut-help-button {
    position: absolute;
    top: 300px;
    right: 30px;
    z-index: 100;
    width: 40px;
    height: 40px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.shortcut-help-button:hover {
    background-color: #f0f0f0;
    transform: scale(1.1);
}

/* 标签管理面板 */
.tag-management-panel {
    position: absolute;
    top: 360px;
    /* 位于快捷键按钮下方 */
    right: 30px;
    z-index: 100;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 10px;
    max-width: 200px;
    max-height: 300px;
    overflow-y: auto;
}

.tag-item {
    padding: 5px;
    margin: 3px 0;
    background-color: #e9ecef;
    border-radius: 4px;
    font-size: 12px;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s;
}

.tag-item:hover {
    background-color: #dee2e6;
}

/* 快捷键提示面板 */
.shortcut-help-panel {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.shortcut-help-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}

.shortcut-help-content h3 {
    margin-top: 0;
    text-align: center;
}

.shortcut-category {
    margin-bottom: 20px;
}

.shortcut-category h4 {
    margin-bottom: 10px;
    color: #333;
}

.shortcut-category ul {
    list-style-type: none;
    padding: 0;
}

.shortcut-category li {
    margin-bottom: 8px;
    padding: 5px;
    background-color: #f8f9fa;
    border-radius: 4px;
}

.close-help-button {
    display: block;
    margin: 20px auto 0;
    padding: 10px 20px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.close-help-button:hover {
    background-color: #0056b3;
}

/* kbd 样式 */
kbd {
    background-color: #f1f1f1;
    border: 1px solid #aaa;
    border-radius: 4px;
    padding: 2px 6px;
    font-family: monospace;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

/* 缩放控制按钮 */
.zoom-controls {
    position: absolute;
    top: 70px;
    right: 20px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.zoom-button {
    width: 40px;
    height: 40px;
    border: 1px solid #ddd;
    background-color: white;
    border-radius: 50%;
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.zoom-button:hover {
    background-color: #f0f0f0;
    transform: scale(1.1);
}

.zoom-button:active {
    transform: scale(0.95);
}

/* 缩放容器 */
.zoom-container {
    /* 使用与父容器相同的尺寸确保SVG连接线能正确显示 */
    width: 100%;
    height: 100%;
    transform-origin: 0 0;
    /* 添加position:relative确保子元素正确定位 */
    position: relative;
}

.talent-container {
    position: relative;
    min-height: 600px;
    height: calc(100vh - 100px);
    /* 根据视窗高度调整容器高度 */
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f9f9f9;
    overflow: hidden;
}

.toolbar {
    padding: 10px;
    background-color: #f0f0f0;
    border-bottom: 1px solid #ddd;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.toolbar button {
    padding: 8px 16px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.toolbar button:hover {
    background-color: #0056b3;
}

.search-container {
    position: relative;
    flex-grow: 1;
    max-width: 300px;
}

.search-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 100;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.search-result-item {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
}

.search-result-item:hover {
    background-color: #f5f5f5;
}

.search-result-item:last-child {
    border-bottom: none;
}

.talent-tree {
    position: relative;
    width: 100%;
    height: 100%;
    /* 使用父容器的全部高度 */
    overflow: hidden;
    cursor: grab;
    background-image:
        radial-gradient(circle, #ddd 1px, transparent 1px),
        radial-gradient(circle, #ddd 1px, transparent 1px);
    background-size: 20px 20px;
    background-position: 0 0, 10px 10px;
}

.links-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
    transform: translateZ(0);
    /* 启用硬件加速 */
}

.talent-node {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 2px;
    text-align: center;
    z-index: 2;
    border: 2px solid;
    transform: translateZ(0);
    /* 启用硬件加速 */
    user-select: none;
    /* 防止文字被选中 */
    -webkit-user-select: none;
    /* Safari兼容 */
    -moz-user-select: none;
    /* Firefox兼容 */
    -ms-user-select: none;
    /* IE/Edge兼容 */
    overflow: hidden;
    /* 防止内容溢出 */
    /* 移除所有过渡动画以避免拖拽延迟 */
    transition: none;
}

.talent-node.circle {
    border-radius: 50%;
}

.talent-node.rectangle {
    border-radius: 0;
}

.talent-node.rounded-rectangle {
    border-radius: 10px;
}

.talent-node.triangle {
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    border-radius: 0;
}

.talent-node.active {
    /* background-color: #e3f2fd; */
    /* border: 2px solid #2196f3; */
}

.talent-node.inactive {
    opacity: 0.7;
    filter: grayscale(100%);
    border-color: #cccccc !important;
    background-color: #f0f0f0 !important;
}

.talent-node.inactive .node-name {
    color: #999999;
}

.talent-node.inactive .node-icon {
    filter: grayscale(100%);
    opacity: 0.7;
}

/* 相关节点高亮样式 */
.related-node {
    box-shadow: 0 0 0 3px #ff9800, 0 0 15px rgba(255, 152, 0, 0.8);
    transform: scale(1.05);
    z-index: 5;
    transition: none;
}

/* 相关连线高亮样式 */
.related-link {
    stroke: #ff9800 !important;
    stroke-width: 3 !important;
    filter: drop-shadow(0 0 5px rgba(255, 152, 0, 0.8));
}

/* 特殊高亮连线样式 */
.special-highlighted-link {
    stroke: #e91e63 !important;
    /* 深粉色 */
    stroke-width: 4 !important;
    filter: drop-shadow(0 0 8px rgba(233, 30, 99, 0.9));
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }

    100% {
        opacity: 1;
    }
}

.talent-node.selected {
    transform: scale(1.1);
    box-shadow: 0 0 0 3px #ff9800;
}

.talent-node.highlighted {
    box-shadow: 0 0 0 4px #ffeb3b, 0 0 20px rgba(255, 235, 59, 0.8);
    transform: scale(1.05);
    z-index: 5;
    /* 移除过渡动画以避免延迟 */
    transition: none;
}

.talent-node.search-highlighted {
    box-shadow: 0 0 0 3px #4caf50, 0 0 15px rgba(76, 175, 80, 0.6);
    transform: scale(1.03);
    z-index: 4;
    /* 移除过渡动画以避免延迟 */
    transition: none;
}

.talent-node.source-node {
    box-shadow: 0 0 0 3px #4caf50;
}

.talent-node.target-node {
    box-shadow: 0 0 0 3px #2196f3;
}

.talent-node.draggable {
    cursor: move;
}

.talent-node.dragging {
    opacity: 0.75;
    z-index: 10;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    transition: none;
}

.node-icon {
    margin-bottom: 5px;
    border-radius: 50%;
    object-fit: contain;
    display: block;
    margin-left: auto;
    margin-right: auto;
    flex-shrink: 0;
    /* 防止图标被压缩 */
    z-index: 3;
    /* 确保图标层级高于边框 */
    position: relative;
    /* 确保定位上下文 */
    width: 100%;
    height: auto;
}

.node-name {
    font-weight: bold;
    overflow: hidden;
    text-overflow: clip;
    white-space: normal;
    width: 100%;
    flex-shrink: 1;
    /* 允许文字被压缩以适应空间 */
    flex-grow: 0;
    /* 防止文字占用过多空间 */
    flex-basis: auto;
    /* 自动计算基础大小 */
    min-height: 1.2em;
    /* 确保最小高度 */
    word-break: break-word;
    /* 长单词换行 */
    line-height: 1.2;
    /* 行高 */
}

.node-detail-modal,
.node-edit-modal,
.link-edit-modal,
.link-detail-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}

.form-group {
    margin-bottom: 15px;
}

.star-rating {
    display: flex;
    justify-content: center;
    margin-top: 5px;
}

.star {
    fill: #ccc;
    margin: 0 1px;
}

.star.filled {
    fill: #ffd700;
}

.star-rating-external {
    pointer-events: none;
    /* 防止星星阻挡节点交互 */
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.color-picker-container {
    display: flex;
    align-items: center;
    gap: 10px;
}

.color-input {
    width: 50px;
    height: 40px;
    padding: 2px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.color-preview {
    width: 40px;
    height: 40px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
}

.color-value {
    font-family: monospace;
    font-size: 14px;
    color: #666;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
}

.form-actions button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.form-actions button[type="submit"],
.form-actions button:first-child {
    background-color: #007bff;
    color: white;
}

.form-actions button[type="submit"]:hover,
.form-actions button:first-child:hover {
    background-color: #0056b3;
}

.form-actions button[type="button"],
.form-actions button:last-child {
    background-color: #6c757d;
    color: white;
}

.form-actions button[type="button"]:hover,
.form-actions button:last-child:hover {
    background-color: #545b62;
}

.detail-content img {
    max-width: 100%;
    height: auto;
}

.node-properties ul {
    list-style-type: none;
    padding: 0;
}

.node-properties li {
    margin-bottom: 5px;
}

.link-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 10px;
}

.link-item span {
    flex: 1;
    margin: 0 5px;
}

.link-item .required {
    color: red;
    font-weight: bold;
}

.link-item .optional {
    color: blue;
    font-weight: bold;
}

.delete-link-btn,
.delete-btn {
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 4px;
    cursor: pointer;
}

.delete-link-btn:hover,
.delete-btn:hover {
    background-color: #c82333;
}

.edit-actions {
    margin: 20px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.close-btn {
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    float: right;
}

.close-btn:hover {
    background-color: #545b62;
}

.control-point {
    cursor: move;
    pointer-events: all;
}

.control-point:hover {
    fill: #ff5722;
    stroke: #fff;
    stroke-width: 2;
}

.control-point.dragging {
    fill: #ff5722;
    stroke: #fff;
    stroke-width: 3;
    r: 8;
}

/* 评分弹窗样式 */
.rating-modal,
.settings-modal,
.homework-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.rating-content,
.settings-content,
.homework-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    max-width: 90%;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}

.rating-layout,
.settings-layout,
.homework-layout {
    display: flex;
    flex-direction: row;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.nodes-section,
.questions-section,
.users-section,
.properties-section,
.setting-section {
    flex: 1;
    min-width: 200px;
    margin-bottom: 15px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.nodes-section,
.questions-section,
.users-section {
    max-width: 25%;
}

.properties-section,
.setting-section {
    max-width: 25%;
}


.questions-section h3 {
    margin-top: 0;
    margin-bottom: 10px;
}

.questions-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
}

.question-item {
    padding: 8px;
    margin-bottom: 5px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;
}

.question-item:hover {
    background-color: #f0f0f0;
}

.question-item.selected {
    background-color: #28a745;
    color: white;
}

.question-item.faded {
    opacity: 0.5;
}

.nodes-section h3,
.users-section h3,
.properties-section h3 {
    margin-top: 0;
    margin-bottom: 10px;
}

.search-input {
    width: 100%;
    padding: 8px;
    margin-bottom: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.nodes-list,
.users-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
}

.node-item,
.user-item {
    padding: 8px;
    margin-bottom: 5px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;
}

.node-item:hover,
.user-item:hover {
    background-color: #f0f0f0;
}

.node-item.selected,
.user-item.selected {
    background-color: #007bff;
    color: white;
}

.property-item {
    margin-bottom: 15px;
}

.property-item label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
}

.property-item textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
    resize: vertical;
}

.tasks-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
}

.task-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    margin-bottom: 5px;
    background-color: #f8f9fa;
    border-radius: 4px;
}

.remove-task-btn {
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
}

.remove-task-btn:hover {
    background-color: #c82333;
}

.empty-tasks {
    color: #6c757d;
    font-style: italic;
    text-align: center;
    padding: 20px;
}

.star-rating {
    display: flex;
    gap: 5px;
}

.star {
    font-size: 24px;
    color: #ccc;
    cursor: pointer;
}

.star.filled {
    color: #ffd700;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}

.submit-btn,
.cancel-btn {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.submit-btn {
    background-color: #007bff;
    color: white;
}

.cancel-btn {
    background-color: #6c757d;
    color: white;
}

.submit-btn:hover {
    background-color: #0056b3;
}

.cancel-btn:hover {
    background-color: #545b62;
}

/* 设置弹窗特有样式 */
.settings-layout .form-group {
    margin-bottom: 15px;
}

.settings-layout .form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
}

.settings-layout .form-group input,
.settings-layout .form-group select {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.settings-layout .form-group input[type="range"] {
    width: calc(100% - 60px);
    vertical-align: middle;
}

.settings-layout .form-group span {
    display: inline-block;
    width: 50px;
    text-align: right;
    margin-left: 10px;
    vertical-align: middle;
}

.settings-layout h3 {
    margin-top: 0;
    margin-bottom: 15px;
    padding-bottom: 5px;
    border-bottom: 1px solid #eee;
}

/* 框选样式 */
.selection-box {
    position: absolute;
    border: 2px dashed #007bff;
    background-color: rgba(0, 123, 255, 0.2);
    pointer-events: none;
    z-index: 1000;
}

/* 反选框样式 */
.deselection-box {
    position: absolute;
    border: 2px dashed #ff0000;
    background-color: rgba(255, 0, 0, 0.2);
    pointer-events: none;
    z-index: 1000;
}

/* 多选节点包围多边形 */
.selection-polygon-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
}

.selection-polygon {
    fill: rgba(0, 123, 255, 0.2);
    /* 半透明蓝色填充 */
    stroke: rgba(0, 123, 255, 0.8);
    /* 稍微不透明的蓝色边框 */
    stroke-width: 2px;
    pointer-events: none;
    /* 确保不影响节点交互 */
}

/* 预设样式管理 */
.preset-name-input {
    margin-right: 10px;
    padding: 5px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.save-preset-btn,
.delete-preset-btn {
    padding: 5px 10px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-left: 10px;
}

.save-preset-btn {
    background-color: #28a745;
    color: white;
}

.delete-preset-btn {
    background-color: #dc3545;
    color: white;
}

.save-preset-btn:hover {
    background-color: #218838;
}

.delete-preset-btn:hover {
    background-color: #c82333;
}

.preset-list {
    margin-top: 15px;
}

.preset-list h4 {
    margin-top: 0;
    margin-bottom: 10px;
}

.no-presets {
    color: #6c757d;
    font-style: italic;
    text-align: center;
    padding: 10px;
}

.preset-items {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.preset-item {
    display: flex;
    align-items: center;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
}

.preset-item:hover {
    background-color: #f8f9fa;
}

.preset-item.selected {
    background-color: #007bff;
    color: white;
}

.preset-name {
    margin-right: 10px;
    flex: 1;
}

.preset-preview {
    display: flex;
    align-items: center;
}

.node-preview {
    width: 20px;
    height: 20px;
    margin-right: 5px;
}

.line-preview {
    width: 30px;
    height: 4px;
}

/* 预设样式选择 */
.preset-selection-group {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
}

.preset-selection-group label {
    font-weight: bold;
    min-width: 80px;
}

.preset-selection-group select {
    flex: 1;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.apply-preset-btn {
    padding: 8px 12px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.apply-preset-btn:hover:not(:disabled) {
    background-color: #0056b3;
}

.apply-preset-btn:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

/* LOD（细节层次）优化 */
.talent-node.low-lod {
    /* 低细节级别：简化渲染 */
    background: #e0e0e0;
    border: 2px solid #999;
}

.talent-node.low-lod .node-icon {
    display: none;
}

.talent-node.low-lod .node-name {
    font-size: 10px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.talent-node.medium-lod {
    /* 中等细节级别：平衡渲染 */
}

.talent-node.medium-lod .node-icon {
    width: 20px !important;
    height: 20px !important;
}

.talent-node.medium-lod .node-name {
    font-size: 12px !important;
}

/* 拖动期间的性能优化 */
.talent-tree.is-panning .talent-node {
    /* 拖动时降低透明度以提高性能 */
    transition: none;
}

/* 连接线LOD优化 */
.links-layer.low-lod path {
    stroke-width: 1;
}

.links-layer.medium-lod path {
    stroke-width: 1.5;
}

/* 右键菜单样式 */
.context-menu {
    position: fixed;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 10000;
    min-width: 150px;
    padding: 4px 0;
}

.context-menu-item {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #333;
    position: relative;
}

.context-menu-item:hover {
    background-color: #f5f5f5;
}

.menu-icon {
    margin-right: 8px;
    font-size: 16px;
}

.context-submenu {
    position: absolute;
    left: 100%;
    top: 0;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    min-width: 120px;
    padding: 4px 0;
    z-index: 10001;
}

.submenu-trigger {
    position: relative;
}

.submenu-trigger:hover .context-submenu {
    display: block;
}

.group-in::before {
    content: '●';
    color: #007bff;
    margin-right: 5px;
}

.group-out::before {
    content: '○';
    color: #ccc;
    margin-right: 5px;
}

/* 标签管理面板 */
.tag-management-panel {
    position: absolute;
    top: 360px;
    /* 位于快捷键按钮下方 */
    right: 30px;
    z-index: 100;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 10px;
    max-width: 200px;
    max-height: 300px;
    overflow-y: auto;
}

.tag-item {
    padding: 5px;
    margin: 3px 0;
    background-color: #f0f0f0;
    border-radius: 4px;
    text-align: center;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    color: #333;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tag-item:hover {
    background-color: #e0e0e0;
}

.no-tags-message {
    color: #999;
    font-style: italic;
    text-align: center;
    padding: 10px;
    font-size: 12px;
}

/* 多节点连接选择样式 */
.node-list {
    max-height: 100px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px;
    background-color: #f9f9f9;
}

.selected-node {
    padding: 4px 8px;
    margin: 2px 0;
    background-color: #e3f2fd;
    border-radius: 4px;
    border-left: 3px solid #2196f3;
}

/* 调试信息样式 */
.debug-info {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.debug-info ul {
    margin: 5px 0;
    padding-left: 20px;
}
</style>